<template>
    <div class="msku-manage-page">
        <!-- 顶部操作栏 -->
        <div class="operation-bar">
            <div class="search-form">
                <span class="form-label">店铺</span>
                <el-select v-model="searchForm.shop" placeholder="选择店铺" style="width:180px" filterable
                    @change="onShopChange" size="small">
                    <el-option v-for="item in filters" :key="item.value" :label="item.text" :value="item.value" />
                </el-select>
                <span class="form-label">本地父体</span>
                <el-select v-model="searchForm.parentName" placeholder="选择父体" style="width:260px" filterable clearable
                    size="small">
                    <el-option v-for="p in parentOptions" :key="p.value" :label="p.text" :value="p.value" />
                </el-select>
                <el-button type="primary" size="small" @click="handleSearch" :loading="isSending">查询</el-button>
                <el-button size="small" @click="resetSearch">重置</el-button>
                <el-button size="small" type="warning" plain @click="openParentConfig">产品父体配置</el-button>
                <!-- 首期上线保留：查看 MSKU 轮询、FNSKU/ASIN 与配对同步状态。 -->
                <el-button size="small" type="warning" plain @click="openAutoWorkbench('msku')">Auto工作台</el-button>
                <!-- 当前阶段开放：品名/父体配置模板下载、批量导入、颜色翻译维护。 -->
                <el-button size="small" type="success" plain @click="openBatchDialog">批量导入配置</el-button>
                <el-button size="small" type="success" plain @click="openFillTplDialog('pm')"
                    :disabled="!tableSelection.length">生成品名模板{{ tableSelection.length ? '(' + tableSelection.length +
                        ')' : '' }}</el-button>
                <el-button size="small" type="success" plain @click="openFillTplDialog('parent')">父体模板</el-button>
                <el-button size="small" type="success" plain @click="openMskuBatch">批量更新在售MSKU</el-button>
                <el-button size="small" type="info" plain @click="openColorConfig">颜色配置</el-button>
                <!-- 首期上线保留：批量生成 MSKU，并写入 Auto 监控队列。 -->
                <el-button size="small" type="warning" plain @click="openGenMulti">批量生成MSKU{{ tableSelection.length ?
                    '(' + tableSelection.length + ')' : '' }}</el-button>
                <el-button size="small" type="success" plain @click="openBatchAsin">批量新建ASIN{{ tableSelection.length ?
                    '(' + tableSelection.length + ')' : '' }}</el-button>
            </div>
        </div>

        <!-- 数据表格区域 -->
        <div class="table-area">
            <div class="table-header-bar">
                <span class="table-title">品名列表</span>
                <el-input v-model="pmFilter" placeholder="筛选本地品名" size="small" style="width:200px" clearable
                    prefix-icon="el-icon-search" />
            </div>
            <el-table ref="mainTable" :data="filteredTableData" style="width:100%;margin-top:20px" border stripe
                :row-class-name="tableRowClass" :row-key="tableRowKey" v-loading="isSending"
                @selection-change="onTableSelectionChange">
                <el-table-column type="selection" width="45" :reserve-selection="true" />
                <el-table-column prop="本地父体" label="本地父体" min-width="140" sortable />
                <el-table-column prop="本地品名" label="本地品名" min-width="140" />
                <el-table-column prop="店铺" label="店铺" width="120" />
                <el-table-column prop="msku" label="当前售卖MSKU" min-width="220">
                    <template slot-scope="scope">
                        <div v-if="scope.row._mskuEditing" style="display:flex;align-items:center;">
                            <el-input v-model="scope.row.msku_edit" size="mini" style="flex:1;min-width:120px;"
                                placeholder="填写要绑定的MSKU" @keyup.enter.native="saveMskuEdit(scope.row)" />
                            <el-button type="primary" size="mini" icon="el-icon-check" style="margin-left:4px;"
                                :loading="scope.row._mskuSaving" @click="saveMskuEdit(scope.row)" />
                            <el-button size="mini" icon="el-icon-close" style="margin-left:2px;"
                                @click="cancelMskuEdit(scope.row)" />
                        </div>
                        <div v-else class="msku-cell" title="点击编辑MSKU" @click="startMskuEdit(scope.row)">
                            <span v-if="!scope.row._hasRel" style="color:#C0C4CC;">/</span>
                            <span v-else-if="!scope.row.msku" style="color:#E6A23C;">未绑定</span>
                            <span v-else>{{ scope.row.msku }}</span>
                            <i class="el-icon-edit-outline msku-edit-icon"></i>
                        </div>
                    </template>
                </el-table-column>
                <el-table-column prop="状态" label="状态" width="130" align="center">
                    <template slot-scope="scope">
                        <el-tag :type="statusType(scope.row.状态)" size="small" effect="plain">
                            {{ scope.row['状态'] || '待操作' }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="更多操作" width="110" align="center" fixed="right">
                    <template #default="{ row }">
                        <el-dropdown trigger="click" @command="cmd => onRowCommand(cmd, row)">
                            <el-button size="mini" type="primary" plain>
                                编辑<i class="el-icon-arrow-down el-icon--right"></i>
                            </el-button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item command="config">产品操作配置</el-dropdown-item>
                                <el-dropdown-item command="create_pair">创建产品并配对</el-dropdown-item>
                                <el-dropdown-item command="move_in">移入</el-dropdown-item>
                                <el-dropdown-item command="move_out">移出</el-dropdown-item>
                                <el-dropdown-item command="reset" divided>重置</el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- Auto 采购汇总确认：存在正数采购数量时，先汇总确认再提交混合批次 -->
        <el-dialog title="采购汇总确认" :visible.sync="batchAsinDraftVisible" width="820px"
            custom-class="au-dialog" :close-on-click-modal="false" append-to-body>
            <el-alert title="以下 MSKU 将参与采购；采购数量为 0 的 MSKU 只创建 ASIN，不参与采购。确认后进入统一刊登与采购监控。"
                type="warning" :closable="false" show-icon style="margin-bottom:12px;" />
            <div class="msku-hint" style="white-space:pre-wrap;margin-bottom:12px;">{{ batchAsinDraftConfigSaveText }}</div>
            <el-table :data="batchAsinDraftRows" size="small" max-height="380" border stripe>
                <el-table-column type="index" label="#" width="55" align="center" />
                <el-table-column prop="pm" label="本地品名" min-width="150" show-overflow-tooltip />
                <el-table-column prop="shop" label="店铺" width="110" />
                <el-table-column prop="msku" label="生成的 MSKU" min-width="220" show-overflow-tooltip />
                <el-table-column prop="quantity" label="采购数量" width="100" align="center" />
                <el-table-column prop="warehouse" label="采购仓库（锁定）" min-width="150" />
            </el-table>
            <div class="msku-hint" style="margin-top:8px;">
                合计：{{ batchAsinDraftRows.length }} 个 MSKU，
                {{ batchAsinDraftTotalQuantity }} 件；仓库由店铺站点锁定，不可修改。
            </div>
            <span slot="footer">
                <el-button @click="batchAsinDraftVisible = false">取消</el-button>
                <el-button type="primary" :loading="batchAsinDraftConfirming"
                    @click="confirmBatchAsinProcurementDraft">确认采购并提交</el-button>
            </span>
        </el-dialog>

        <!-- 移入弹窗 -->
        <el-dialog title="移入操作" :visible.sync="moveInVisible" width="580px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body>
            <div v-if="moveInStep === 'form'">
                <el-form label-width="140px" size="small">
                    <el-form-item label="源 ASIN" required>
                        <el-select v-model="moveInSelectedAsin" filterable clearable
                            :loading="moveInCandidateLoading"
                            placeholder="从当前店铺 Listing 选择源 ASIN" style="width:100%"
                            @change="onMoveInAsinChange">
                            <el-option v-for="c in moveInCandidates" :key="c.asin + '|' + c.source_msku"
                                :label="formatAsinCandidateLabel(c)" :value="c.asin + '|' + c.source_msku">
                                <span>{{ c.asin }}</span>
                                <span style="float:right;color:#909399;font-size:12px;">
                                    {{ c.source_msku }}
                                </span>
                            </el-option>
                        </el-select>
                        <div class="msku-hint">
                            来源：本地父体/品名 → 关联SKU → 当前店铺 Listing，仅当前站点匹配记录，不跨站点兜底
                        </div>
                        <el-button type="text" size="mini" :loading="moveInCandidateLoading"
                            @click="loadMoveInCandidates">刷新候选</el-button>
                    </el-form-item>
                    <el-form-item label="源MSKU">
                        <el-input v-model="moveInRow.msku" readonly placeholder="选择源 ASIN 后自动带出" style="width:100%" />
                        <div class="msku-hint">取该 MSKU 的 ASIN 与属性，创建新跟卖产品并移入目标父体</div>
                    </el-form-item>
                    <el-form-item label="新MSKU" required>
                        <el-input v-model="moveInRow.new_msku" placeholder="按品名配置自动生成(-C)，可修改" style="width:100%" />
                        <div class="msku-hint">按父体「MSKU 生成模式」+ 品名配置自动生成（后缀 -C），创建成功后录入为当前售卖MSKU</div>
                    </el-form-item>
                    <el-form-item label="Parent SKU" required>
                        <el-input v-model="moveInRow.parent_sku" placeholder="要移入的父体 MSKU" style="width:100%" />
                        <div class="msku-hint">选择要把新 MSKU 移入的父体 MSKU</div>
                    </el-form-item>
                    <el-form-item label="店铺" required>
                        <el-select v-model="moveInRow['店铺']" filterable placeholder="选择店铺" style="width:100%"
                            @change="loadMoveInCandidates">
                            <el-option v-for="item in filters" :key="item.value" :label="item.text"
                                :value="item.value" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="产品类型" required>
                        <el-input v-model="moveInRow.product_type" readonly placeholder="取自品名/父体配置" />
                        <div class="msku-hint">产品类型来自配置，变体主题由父体配置统一维护</div>
                    </el-form-item>
                    <el-form-item label="变体主题" required>
                        <el-input v-model="moveInRow.variation_theme" readonly
                            placeholder="请先在父体配置中选择变体主题" />
                        <div class="msku-hint">
                            变体主题只读继承父体配置；缺失的变体值（颜色、尺寸等）在下方补充
                        </div>
                    </el-form-item>
                </el-form>
            </div>
            <div v-if="moveInStep === 'preview'">
                <el-alert :title="moveInPreviewData.message"
                    :type="moveInPreviewData.status === 'ready' ? 'success' : 'warning'" :closable="false" show-icon
                    style="margin-bottom:12px;" />
                <el-descriptions v-if="moveInPreviewData.cloned_count" :column="2" size="small" border
                    style="margin-bottom:12px;">
                    <el-descriptions-item label="Product Type">{{ moveInPreviewData.product_type
                    }}</el-descriptions-item>
                    <el-descriptions-item label="源ASIN">{{ moveInPreviewData.source_asin || '-'
                    }}</el-descriptions-item>
                    <el-descriptions-item label="新MSKU">{{ moveInRow.new_msku }}</el-descriptions-item>
                    <el-descriptions-item label="已克隆字段">{{ moveInPreviewData.cloned_count }} 个</el-descriptions-item>
                </el-descriptions>
                <div v-if="moveInPreviewData.missing_fields && moveInPreviewData.missing_fields.length">
                    <p style="color:#E6A23C;font-weight:bold;margin-bottom:8px;">
                        <i class="el-icon-warning"></i> 以下变体字段缺失，请补充：
                    </p>
                    <el-form label-width="120px" size="small">
                        <el-form-item v-for="mf in moveInPreviewData.missing_fields" :key="mf.field_name"
                            :label="mf.title_zh + ' (' + mf.field_name + ')'">
                            <el-select v-if="mf.enum && mf.enum.length" v-model="moveInExtraValues[mf.field_name]"
                                filterable allow-create default-first-option clearable style="width:100%"
                                :placeholder="mf.description || '请输入值'">
                                <el-option v-for="opt in mf.enum" :key="opt" :label="opt" :value="opt" />
                            </el-select>
                            <el-input v-else v-model="moveInExtraValues[mf.field_name]"
                                :placeholder="mf.description || '请输入值'" />
                        </el-form-item>
                    </el-form>
                </div>
                <div v-if="moveInPreviewData.configured_variation_fields && moveInPreviewData.configured_variation_fields.length"
                    style="margin-top:8px;">
                    <p style="color:#409EFF;"><i class="el-icon-info"></i>
                        品名已配置变体值（可直接复用，如需修改请到品名配置）：{{
                            moveInPreviewData.configured_variation_fields.map(
                                f => f + '=' + ((moveInPreviewData.configured_variation_values || {})[f] || '')
                            ).join('、') }}</p>
                </div>
                <div v-if="moveInPreviewData.existing_variation_fields && moveInPreviewData.existing_variation_fields.length"
                    style="margin-top:8px;">
                    <p style="color:#67C23A;"><i class="el-icon-check"></i>
                        源产品已有：{{ moveInPreviewData.existing_variation_fields.join('、') }}</p>
                </div>
            </div>
            <div v-if="moveInStep === 'executing'">
                <el-result icon="success" title="移入提交成功" sub-title="后台自动处理中，可关闭弹窗或查看任务面板">
                    <template slot="extra">
                        <el-descriptions :column="1" size="small" border>
                            <el-descriptions-item label="Task ID">{{ moveInResult.task_id }}</el-descriptions-item>
                            <el-descriptions-item label="Record ID">{{ moveInResult.record_unique_id
                            }}</el-descriptions-item>
                        </el-descriptions>
                        <el-button size="small" type="info" @click="moveInVisible = false; openTaskPanel()"
                            style="margin-top:10px;">打开任务面板查看</el-button>
                    </template>
                </el-result>
            </div>
            <span slot="footer">
                <el-button @click="moveInVisible = false; moveInStep = 'form'">关闭</el-button>
                <el-button v-if="moveInStep === 'form'" type="info" icon="el-icon-search" @click="moveInPreview"
                    :loading="moveInLoading"
                    :disabled="!moveInRow.msku || !moveInRow.new_msku || !moveInRow.parent_sku || !moveInRow.product_type || !moveInRow.variation_theme">预览检查</el-button>
                <el-button v-if="moveInStep === 'preview'" @click="backToMoveInForm">上一步</el-button>
                <el-button v-if="moveInStep === 'preview'" type="primary" icon="el-icon-upload2" @click="moveInExecute"
                    :loading="moveInLoading">提交移入</el-button>
            </span>
        </el-dialog>

        <!-- 移出弹窗 -->
        <el-dialog title="移出操作" :visible.sync="moveOutVisible" width="480px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body>
            <div v-if="moveOutStep === 'confirm'">
                <el-form label-width="120px" size="small">
                    <el-form-item label="MSKU" required>
                        <el-input v-model="moveOutRow.msku" placeholder="要移出的 MSKU" style="width:100%"
                            @change="onMoveOutMskuChange" />
                        <div class="msku-hint">自动带出当前售卖MSKU；未绑定时需手动输入</div>
                    </el-form-item>
                    <el-form-item label="店铺" required>
                        <el-select v-model="moveOutRow['店铺']" filterable placeholder="选择店铺" style="width:100%"
                            @change="onMoveOutMskuChange">
                            <el-option v-for="item in filters" :key="item.value" :label="item.text"
                                :value="item.value" />
                        </el-select>
                    </el-form-item>
                </el-form>
                <el-alert v-if="!moveOutSnapshotReady"
                    title="标题不再手工填写：将从在线商品读取原标题并替换末尾15字符（YYMMDD+9位随机字母），请先预览"
                    type="info" :closable="false" show-icon style="margin-bottom:12px;" />
                <template v-else>
                    <el-alert :title="'本次后缀：' + (moveOutPreviewInfo.suffix || '') + '（同次操作所有 MSKU 共用同一后缀）'"
                        type="success" :closable="false" show-icon style="margin-bottom:12px;" />
                    <el-descriptions :column="1" size="small" border>
                        <el-descriptions-item label="本地品名">{{ moveOutRow.本地品名 }}</el-descriptions-item>
                        <el-descriptions-item label="店铺">{{ moveOutRow.店铺 }}</el-descriptions-item>
                        <el-descriptions-item label="ASIN">{{ moveOutPreviewInfo.asin || '-' }}</el-descriptions-item>
                        <el-descriptions-item label="主标题(改前)">{{ moveOutPreviewInfo.old_item_name || '（空）' }}</el-descriptions-item>
                        <el-descriptions-item label="主标题(改后)">{{ moveOutPreviewInfo.new_item_name || '（空）' }}</el-descriptions-item>
                        <el-descriptions-item label="副标题(改前)">{{ moveOutPreviewInfo.old_title_differentiation || '（空）' }}</el-descriptions-item>
                        <el-descriptions-item label="副标题(改后)">{{ moveOutPreviewInfo.new_title_differentiation || '（空，不追加颜色）' }}</el-descriptions-item>
                    </el-descriptions>
                    <div class="msku-hint" style="margin:10px 0 4px;">
                        受影响 MSKU（同店铺同 ASIN，共 {{ (moveOutPreviewInfo.affected_mskus || []).length }} 个）
                    </div>
                    <div class="move-out-mskus">
                        <el-tag v-for="m in moveOutPreviewInfo.affected_mskus" :key="m" size="small"
                            :type="m === moveOutRow.msku ? 'primary' : ''"
                            style="margin:0 6px 6px 0;">{{ m }}</el-tag>
                    </div>
                    <p v-if="moveOutPreviewInfo.has_parent_relation" style="color:#E6A23C;margin-top:10px;">
                        <i class="el-icon-warning"></i> 该产品存在父子关系，移出后将修改 parent_sku 为自身，使其独立。
                    </p>
                    <p v-else style="color:#909399;margin-top:10px;">该产品已是独立产品。</p>
                </template>
            </div>
            <div v-if="moveOutStep === 'executing'">
                <el-result icon="success" title="移出提交成功" sub-title="后台处理中">
                    <template slot="extra">
                        <el-tag size="small">Task ID: {{ moveOutResult.task_id }}</el-tag>
                    </template>
                </el-result>
            </div>
            <span slot="footer">
                <el-button @click="moveOutVisible = false; moveOutStep = 'confirm'">关闭</el-button>
                <el-button v-if="moveOutStep === 'confirm'" @click="moveOutPreview"
                    :loading="moveOutPreviewLoading">预览移出</el-button>
                <el-button v-if="moveOutStep === 'confirm'" type="primary" :disabled="!moveOutSnapshotReady"
                    @click="moveOutExecute" :loading="moveOutLoading">确认移出</el-button>
            </span>
        </el-dialog>

        <!-- 父体配置弹窗 -->
        <parent-config-dialog :visible.sync="dialogParentConfigVisible" :parent="searchForm.parentName"
            :parent-list="allParentList" @saved="refreshTableData" />
        <!-- 批量生成缺配置时使用的 MSKU 专用快捷弹窗，不打开完整父体/品名配置。 -->
        <msku-rule-dialog :visible.sync="dialogMskuRuleVisible" :parent="searchForm.parentName"
            :parent-list="allParentList" @saved="onMskuParentRuleSaved" />
        <msku-rule-dialog :visible.sync="dialogMskuPmRuleVisible" :parent="mskuPmRuleMeta.parent"
            :pm="mskuPmRuleMeta.pm" :shop="mskuPmRuleMeta.shop" :parent-list="allParentList"
            @saved="onMskuPmRuleSaved" />

        <!-- 品名配置弹窗 -->
        <pm-config-dialog :visible.sync="dialogPmConfigVisible" :parent="pmConfigMeta['本地父体']"
            :pm="pmConfigMeta['本地品名']" :shop="pmConfigMeta['店铺']" :msku="pmConfigMeta.msku" :show-warehouse="false"
            @saved="handlePmConfigSaved" />
        <!-- 批量新建ASIN：行内父体配置（产品类型/公用字段可在父体配置） -->
        <parent-config-dialog :visible.sync="batchAsinParentConfigVisible" :parent="batchAsinParentConfigMeta.parent"
            :parent-list="allParentList" @saved="onBatchAsinParentConfigSaved" />
        <!-- 批量新建ASIN：缺 MSKU 生成配置时使用 MSKU 专用快捷弹窗，不打开完整品名配置 -->
        <msku-rule-dialog :visible.sync="batchAsinMskuRuleVisible" :parent="batchAsinMskuRuleMeta.parent"
            :pm="batchAsinMskuRuleMeta.pm" :shop="batchAsinMskuRuleMeta.shop" :parent-list="allParentList"
            @saved="onBatchAsinMskuRuleSaved" />

        <!-- Auto 统一工作台：MSKU记录 / 任务面板 / 操作日志分页切换 -->
        <auto-workbench-dialog :visible.sync="dialogAutoWorkbench" :active-tab="autoWorkbenchTab"
            :parent="searchForm.parentName" :shop="searchForm.shop" :msku="autoWorkbenchMsku"
            :shop-options="filters"
            @tasks-loaded="onWorkbenchTasksLoaded" />

        <!-- 颜色配置弹窗 -->
        <color-translation :visible.sync="colorConfigVisible" />

        <!-- 批量导入配置弹窗 -->
        <el-dialog title="批量导入配置" :visible.sync="batchDialogVisible" width="920px"
            custom-class="au-dialog main-feature-dialog" :close-on-click-modal="false" append-to-body
            @closed="resetBatchDialog">
            <template v-if="batchStep === 'form'">
                <!-- 板块一：模板下载 -->
                <div class="batch-section">
                    <div class="batch-section-title"><i class="el-icon-download"></i> 模板下载</div>
                    <el-form label-width="120px" size="small">
                        <el-form-item label="模板类型">
                            <el-radio-group v-model="batchKind" size="small" @change="batchProductTypeChange">
                                <el-radio-button label="pm">品名配置模板</el-radio-button>
                                <el-radio-button label="parent">父体配置模板</el-radio-button>
                            </el-radio-group>
                            <div class="msku-hint" v-if="batchKind === 'parent'">
                                父体模板字段按最新模板表中 config_level=parent 的标记生成；品名模板含品名配置标量 + 品名级创建字段，父体共用字段仍由父体模板维护
                            </div>
                        </el-form-item>
                        <el-form-item label="产品类型">
                            <el-select v-model="batchProductType" filterable placeholder="选择产品类型" style="width:320px"
                                @change="batchProductTypeChange">
                                <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label"
                                    :value="pt.value" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="下载空模板">
                            <el-button type="primary" size="small" icon="el-icon-download" :disabled="!batchProductType"
                                :loading="batchDownloading" @click="downloadBatchTemplate">
                                {{ batchProductType || '请先选择产品类型' }}.xlsx
                            </el-button>
                            <div class="msku-hint" v-if="batchKind === 'pm'">模板第 1 行是
                                「基础信息 / MSKU生成 / 创建产品」分区标题，第 2 行才是字段表头（前 3 列为
                                基础信息）；含生成模式 / MSKU模板 / 系列 / 款式 / MSKU分隔符 / 供应商 / 国家 /
                                Parent SKU / 创建主副标题等品名配置标量及品名级创建字段。只填写需要修改的配置项，
                                单元格空白会保留原配置。产品类型以本弹窗选择为准；移入/移出字段无需填写；导入仅更新本地品名配置，不影响线上商品。
                            </div>
                            <div class="msku-hint" v-else>模板含「本地父体」及父体级公用字段（品牌/描述/五点等），品名自动继承；只填写需要修改的配置项。导入仅更新父体配置。
                            </div>
                        </el-form-item>
                    </el-form>
                </div>
                <!-- 板块二：导入配置 -->
                <div class="batch-section">
                    <div class="batch-section-title"><i class="el-icon-upload2"></i> 导入配置</div>
                    <el-form label-width="120px" size="small">
                        <el-form-item label="上传文件">
                            <el-upload :show-file-list="false" accept=".xlsx" :http-request="batchUploadRequest"
                                :disabled="!batchProductType">
                                <el-button size="small" type="success" plain :disabled="!batchProductType">上传并预览
                                </el-button>
                            </el-upload>
                            <div class="msku-hint">上传后先预览解析结果，确认无误再执行导入；模板类型与产品类型需与文件一致。</div>
                        </el-form-item>
                    </el-form>
                </div>
            </template>
            <template v-else-if="batchStep === 'preview'">
                <el-alert v-if="batchErrors.length" :title="'校验提示 ' + batchErrors.length + ' 条'" type="warning"
                    :closable="false" show-icon style="margin-bottom:12px;" />
                <el-alert v-else-if="!batchRows.length" title="未解析到有效数据行" type="info" :closable="false" show-icon
                    style="margin-bottom:12px;" />
                <el-table v-if="batchRows.length" :data="batchRows" size="small" max-height="420" border stripe>
                    <el-table-column prop="本地父体" label="本地父体" min-width="130" />
                    <el-table-column v-if="batchKind !== 'parent'" prop="本地品名" label="本地品名" min-width="130" />
                    <el-table-column v-if="batchKind !== 'parent'" prop="店铺" label="店铺" width="120" />
                    <el-table-column label="更新字段数" width="90" align="center">
                        <template slot-scope="scope">{{ Object.keys(scope.row.values || {}).length }}</template>
                    </el-table-column>
                    <el-table-column label="字段值预览" min-width="300">
                        <template slot-scope="scope">
                            <span style="font-size:12px;color:#909399;">{{ formatBatchValues(scope.row.values) }}</span>
                        </template>
                    </el-table-column>
                </el-table>
                <div v-if="batchErrors.length" style="margin-top:10px;max-height:150px;overflow-y:auto;">
                    <p v-for="(e, i) in batchErrors" :key="i" style="font-size:12px;color:#E6A23C;margin:2px 0;">{{ e }}
                    </p>
                </div>
            </template>
            <template v-else-if="batchStep === 'result'">
                <el-alert v-if="batchSummary" :title="'执行完成：成功 ' + batchSummary.ok + ' / 失败 ' + batchSummary.fail"
                    :type="batchSummary.fail ? 'warning' : 'success'" :closable="false" show-icon
                    style="margin-bottom:12px;" />
                <el-table :data="batchResults" size="small" max-height="420" border stripe>
                    <el-table-column prop="index" label="#" width="50" align="center" />
                    <el-table-column prop="本地父体" label="本地父体" min-width="130" />
                    <el-table-column prop="本地品名" label="本地品名" min-width="130" />
                    <el-table-column prop="店铺" label="店铺" width="110" />
                    <el-table-column label="结果" width="80" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.success ? 'success' : 'danger'" size="mini">
                                {{ scope.row.success ? '成功' : '失败' }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="error" label="失败原因" min-width="220" show-overflow-tooltip />
                </el-table>
            </template>
            <span slot="footer">
                <template v-if="batchStep === 'preview'">
                    <el-button @click="batchStep = 'form'">返回</el-button>
                    <el-button type="primary" :disabled="!batchRows.length" :loading="batchExecuting"
                        @click="executeBatch">确认执行（{{ batchRows.length }} 行）</el-button>
                </template>
                <template v-else>
                    <el-button @click="batchDialogVisible = false">关闭</el-button>
                </template>
            </span>
        </el-dialog>

        <!-- 生成品名待填模板弹窗：应用外层品名列表勾选的数据 -->
        <el-dialog title="生成品名待填模板" :visible.sync="fillTplVisible" width="560px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body>
            <el-alert type="info" :closable="false" show-icon style="margin-bottom:12px;"
                :title="'将按品名列表勾选的 ' + tableSelection.length + ' 个品名（涉及 ' + fillTplParentCount + ' 个父体）生成待填模板'">
            </el-alert>
            <el-form label-width="120px" size="small">
                <el-form-item label="产品类型">
                    <el-select v-model="fillTplProductType" filterable placeholder="选择产品类型" style="width:320px">
                        <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label" :value="pt.value" />
                    </el-select>
                </el-form-item>
            </el-form>
            <div class="msku-hint">模板第 1 行是「基础信息 / MSKU生成 / 创建产品」分区，第 2 行字段表头，数据从第 3 行开始。只预填该品名自身配置过的值，继承父体的值留空，上传后继续继承、不会被固化成品名覆盖；空白项上传后保留原配置。生成后可在「批量导入配置」中上传导入。</div>
            <span slot="footer">
                <el-button @click="fillTplVisible = false">关闭</el-button>
                <el-button type="primary" icon="el-icon-document-add" :loading="fillDownloading"
                    @click="downloadFilledTemplate">生成待填模板</el-button>
            </span>
        </el-dialog>

        <!-- 父体待填模板弹窗：加载全部父体供多选 -->
        <el-dialog title="父体待填模板" :visible.sync="fillParentTplVisible" width="560px"
            custom-class="au-dialog main-feature-dialog" :close-on-click-modal="false" append-to-body
            @closed="resetFillParentDialog">
            <el-alert type="info" :closable="false" show-icon style="margin-bottom:12px;"
                :title="'勾选需要生成待填模板的父体，已选 ' + fillParents.length + ' 个'">
            </el-alert>
            <div class="fill-parent-table">
                <el-input v-model="fillParentFilter" size="small" clearable prefix-icon="el-icon-search"
                    placeholder="搜索父体" style="width:240px;margin-bottom:8px;" />
                <el-table ref="fillParentTable" :data="filteredFillParents" size="small" height="280" border stripe
                    @selection-change="onFillParentSelectionChange">
                    <el-table-column type="selection" width="50" />
                    <el-table-column prop="text" label="父体" min-width="260" />
                </el-table>
            </div>
            <el-form label-width="120px" size="small" style="margin-top:12px;">
                <el-form-item label="产品类型">
                    <el-select v-model="fillTplProductType" filterable placeholder="选择产品类型" style="width:320px">
                        <el-option v-for="pt in productTypes" :key="pt.value" :label="pt.label" :value="pt.value" />
                    </el-select>
                </el-form-item>
            </el-form>
            <div class="msku-hint">父体模板标识列与当前配置值已预填；直接修改需要变更的单元格，空白项上传后保留原配置。生成后可在「批量导入配置」中上传导入。</div>
            <span slot="footer">
                <el-button @click="fillParentTplVisible = false">关闭</el-button>
                <el-button type="primary" icon="el-icon-document-add" :loading="fillDownloading"
                    @click="downloadFilledTemplate('parent')">生成父体待填模板</el-button>
            </span>
        </el-dialog>

        <!-- 批量更新MSKU弹窗（模板上传，仅覆盖 msku，保留其它字段） -->
        <el-dialog title="批量更新MSKU" :visible.sync="mskuBatchVisible" width="860px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body @closed="resetMskuBatch">
            <template v-if="mskuBatchStep === 'form'">
                <el-alert type="info" :closable="false" show-icon
                    title="模板只需改 msku 列：先在品名列表勾选要更新的品名，下载模板时会带出「本地父体 / 本地品名 / 店铺 / 当前MSKU」；改完上传预览，确认后更新。批量更新只覆盖当前售卖MSKU，不影响 parent_sku、变体主题、产品类型等字段；品名尚未关联时会自动创建关联。" />
                <div class="msku-batch-bar">
                    <el-button type="primary" icon="el-icon-download" :loading="mskuBatchDownloading"
                        @click="downloadMskuTemplate">下载模板（带出已勾选品名）</el-button>
                    <el-upload :show-file-list="false" accept=".xlsx" :http-request="mskuUploadRequest">
                        <el-button size="small" type="success" plain icon="el-icon-upload2"
                            :loading="mskuBatchUploading">上传并预览</el-button>
                    </el-upload>
                    <span class="msku-hint">已勾选 {{ tableSelection.length }} 个品名</span>
                </div>
            </template>
            <template v-else-if="mskuBatchStep === 'preview'">
                <el-alert v-if="mskuBatchErrors.length" :title="'校验提示 ' + mskuBatchErrors.length + ' 条'" type="warning"
                    :closable="false" show-icon style="margin-bottom:12px;" />
                <el-alert v-else :title="'解析到 ' + mskuBatchRows.length + ' 行有效数据，确认后执行'" type="success"
                    :closable="false" show-icon style="margin-bottom:12px;" />
                <el-table v-if="mskuBatchRows.length" :data="mskuBatchRows" size="small" max-height="420" border stripe>
                    <el-table-column prop="index" label="#" width="60" align="center" />
                    <el-table-column prop="本地父体" label="本地父体" min-width="150" />
                    <el-table-column prop="本地品名" label="本地品名" min-width="150" />
                    <el-table-column prop="店铺" label="店铺" width="130" />
                    <el-table-column prop="msku" label="msku" min-width="180" />
                    <el-table-column label="校验" width="160" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.error ? 'danger' : 'success'" size="mini">
                                {{ scope.row.error ? '异常' : 'OK' }}</el-tag>
                        </template>
                    </el-table-column>
                </el-table>
                <div v-if="mskuBatchErrors.length" style="margin-top:10px;max-height:150px;overflow-y:auto;">
                    <p v-for="(e, i) in mskuBatchErrors" :key="i" style="font-size:12px;color:#E6A23C;margin:2px 0;">{{
                        e }}</p>
                </div>
            </template>
            <template v-else-if="mskuBatchStep === 'result'">
                <el-alert v-if="mskuBatchSummary"
                    :title="'执行完成：成功 ' + mskuBatchSummary.ok + ' / 失败 ' + mskuBatchSummary.fail"
                    :type="mskuBatchSummary.fail ? 'warning' : 'success'" :closable="false" show-icon
                    style="margin-bottom:12px;" />
                <el-table :data="mskuBatchResults" size="small" max-height="420" border stripe>
                    <el-table-column prop="index" label="#" width="60" align="center" />
                    <el-table-column prop="本地父体" label="本地父体" min-width="150" />
                    <el-table-column prop="本地品名" label="本地品名" min-width="150" />
                    <el-table-column prop="店铺" label="店铺" width="130" />
                    <el-table-column prop="msku" label="msku" min-width="180" />
                    <el-table-column label="结果" width="90" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.success ? 'success' : 'danger'" size="mini">
                                {{ scope.row.success ? '成功' : '失败' }}</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="错误" min-width="160">
                        <template slot-scope="scope">
                            <span style="font-size:12px;color:#E6A23C;">{{ scope.row.error }}</span>
                        </template>
                    </el-table-column>
                </el-table>
            </template>
            <span slot="footer">
                <el-button @click="mskuBatchVisible = false">关闭</el-button>
                <el-button v-if="mskuBatchStep === 'preview'" type="primary" :loading="mskuBatchExecuting"
                    @click="executeMskuBatch">确认更新</el-button>
            </span>
        </el-dialog>

        <!-- 创建并配对弹窗（选 SKU，走后端复合方法，无采购线） -->
        <el-dialog title="创建并配对" :visible.sync="createPairVisible" width="520px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body>
            <div class="config-meta">
                <span class="meta-item"><span class="meta-label">本地父体</span>{{ createPairRow['本地父体'] }}</span>
                <span class="meta-item"><span class="meta-label">本地品名</span>{{ createPairRow['本地品名'] }}</span>
                <span class="meta-item"><span class="meta-label">店铺</span>{{ createPairRow['店铺'] }}</span>
            </div>
            <el-form label-width="110px" size="small">
                <el-form-item label="关联SKU" required>
                    <el-select v-model="createPairSku" filterable placeholder="选择要关联的 SKU" style="width:100%">
                        <el-option v-for="s in createPairSkus" :key="s" :label="s" :value="s" />
                    </el-select>
                    <div class="msku-hint">取自该品名关联的 SKU（amazon_form.male_sku）；将按品名配置生成新 MSKU 并创建后自动配对</div>
                </el-form-item>
                <el-form-item label="MSKU后缀">
                    <el-tag type="info">固定 -T（发货）</el-tag>
                    <div class="msku-hint">“创建产品并配对”只创建发货产品；跟卖请使用单独的“创建跟卖”操作。</div>
                </el-form-item>
            </el-form>
            <el-alert v-if="createPairError" :title="createPairError" type="error" :closable="false" show-icon
                style="margin-bottom:10px;" />
            <span slot="footer">
                <el-button @click="createPairVisible = false">取消</el-button>
                <el-button type="primary" :disabled="!createPairSku" :loading="createPairLoading"
                    @click="submitCreatePair">提交创建并配对</el-button>
            </span>
        </el-dialog>

        <!-- MSKU 域专用导入：只处理生成所需字段，不写产品创建/移入移出字段。 -->
        <el-dialog title="上传 MSKU 生成模板" :visible.sync="mskuColorImportVisible" width="980px" custom-class="au-dialog"
            :close-on-click-modal="false" append-to-body @closed="resetMskuColorImport">
            <el-alert type="info" :closable="false" show-icon style="margin-bottom:12px;"
                title="下载模板只包含表头，不预填任何数据。填写本地父体、SKU、系列、款式、国家代码、MSKU颜色、MSKU模板（可为空）、后缀（可为空）、生成数量；供应商将按 SKU 自动获取，模板不包含供应商和店铺，店铺使用当前弹窗上方选择。空后缀继承当前默认值，非空后缀需为1-6位字母或数字。确认后只处理 MSKU 生成字段，不会修改产品类型、创建字段、移入或移出配置。" />
            <el-form :inline="true" size="small" style="margin-bottom:6px;">
                <el-form-item label="店铺" required>
                    <el-select v-model="mskuColorImportShop" filterable clearable placeholder="选择店铺"
                        style="width:180px">
                        <el-option v-for="item in filters" :key="'msku-import-shop-' + item.value" :label="item.text"
                            :value="item.value" />
                    </el-select>
                </el-form-item>
                <el-form-item label="本地父体" required>
                    <el-select v-model="mskuColorImportParent" filterable clearable placeholder="选择父体"
                        style="width:260px">
                        <el-option v-for="p in parentOptions" :key="'msku-import-parent-' + p.value" :label="p.text"
                            :value="p.value" />
                    </el-select>
                </el-form-item>
                <el-form-item label="后缀">
                    <el-select v-model="mskuColorImportSuffix" style="width:130px"
                        @change="onMskuColorImportSuffixChange">
                        <el-option label="T（创建发货产品）" value="T" />
                        <el-option label="C（创建跟卖）" value="C" />
                    </el-select>
                </el-form-item>
                <el-form-item label="创建后配对">
                    <el-switch v-model="mskuColorImportShouldPair" active-text="是" inactive-text="否" />
                </el-form-item>
                <el-form-item>
                    <el-button type="text" :loading="mskuColorImportDownloading" @click="downloadMskuColorTemplate">
                        下载模板
                    </el-button>
                </el-form-item>
            </el-form>
            <el-upload action="#" :auto-upload="false" :limit="1" accept=".xlsx" :file-list="mskuColorImportFiles"
                :on-change="selectMskuColorFile" :on-remove="removeMskuColorFile">
                <el-button size="small" type="primary">选择 Excel 文件</el-button>
                <div slot="tip" class="el-upload__tip">表头：本地父体、SKU、系列、款式、国家代码、MSKU颜色、MSKU模板(可空)、后缀、生成数量；供应商按 SKU 自动获取，模板不含供应商和店铺。</div>
            </el-upload>
            <div v-if="mskuColorImportErrors.length" class="msku-import-errors">
                <el-alert type="error" :closable="false" show-icon style="margin-top:10px;">
                    <template slot="title">
                        共 {{ mskuColorImportErrors.length }} 行校验失败；修正 Excel 后重新上传。
                    </template>
                </el-alert>
                <div v-for="error in mskuColorImportErrors" :key="error" class="msku-import-error-line">{{ error }}
                </div>
            </div>
            <el-table v-if="mskuColorImportRows.length" :data="mskuColorImportRows" size="small" border stripe
                max-height="360" style="margin-top:12px;">
                <el-table-column prop="row_no" label="行" width="58" align="center" />
                <el-table-column prop="sku" label="SKU" min-width="150" />
                <el-table-column prop="suffix" label="后缀" width="76" align="center" />
                <el-table-column prop="pm" label="本地品名" min-width="200" show-overflow-tooltip />
                <el-table-column label="供应商" width="130">
                    <template slot-scope="scope">
                        <el-input v-model="scope.row.supplier" size="mini" :disabled="!scope.row.valid"
                            placeholder="配置/手填" />
                    </template>
                </el-table-column>
                <el-table-column label="MSKU颜色" width="165">
                    <template slot-scope="scope">
                        <el-input v-model="scope.row.msku_color" size="mini" :disabled="!scope.row.valid" />
                    </template>
                </el-table-column>
                <el-table-column prop="mode" label="模式" width="80" align="center">
                    <template slot-scope="scope">{{ scope.row.mode === 'attribute' ? '属性' : '模板' }}</template>
                </el-table-column>
                <el-table-column prop="base_preview" label="基础名预览" min-width="190" show-overflow-tooltip />
                <el-table-column label="数量" width="100" align="center">
                    <template slot-scope="scope">
                        <el-input-number v-model="scope.row.num" :min="1" :max="200" size="mini" :controls="false" />
                    </template>
                </el-table-column>
                <el-table-column label="结果" width="90" align="center">
                    <template slot-scope="scope">
                        <el-tag size="mini" :type="scope.row.valid ? 'success' : 'danger'">
                            {{ scope.row.valid ? '可生成' : '失败' }}
                        </el-tag>
                    </template>
                </el-table-column>
            </el-table>
            <span slot="footer">
                <el-button @click="mskuColorImportVisible = false">取消</el-button>
                <el-button type="primary" :loading="mskuColorImportPreviewing"
                    :disabled="!mskuColorImportFile || !mskuColorImportParent || !mskuColorImportShop"
                    @click="previewMskuColorImport">解析并预览</el-button>
                <el-button type="success" :loading="mskuColorImportSaving"
                    :disabled="!mskuColorImportRows.length || mskuColorImportErrors.length > 0"
                    @click="commitMskuColorImport">保存颜色并生成 MSKU</el-button>
            </span>
        </el-dialog>

        <!-- 按 SKU 识别弹窗：用于已有 MSKU 颜色配置的快速生成。 -->
        <el-dialog title="按 SKU 识别生成MSKU" :visible.sync="genMultiSkuImportVisible" width="620px"
            custom-class="au-dialog" :close-on-click-modal="false" append-to-body @closed="resetGenMultiSkuImport"
            :show-close="!genMultiResolveLoading" :close-on-press-escape="!genMultiResolveLoading">
            <el-alert type="info" :closable="false" show-icon style="margin-bottom:12px;"
                title="请先选择店铺和本地父体，再从 Excel 复制整列 SKU 粘贴到输入框；识别后会自动带出关联品名和品名配置。" />
            <el-form label-width="72px" size="small" style="margin-bottom:10px;">
                <el-form-item label="店铺" required style="margin-bottom:10px;">
                    <el-select v-model="genMultiSkuImportShop" :disabled="genMultiResolveLoading" filterable clearable placeholder="请选择店铺"
                        style="width:100%;">
                        <el-option v-for="item in filters" :key="'gen-sku-shop-' + item.value" :label="item.text"
                            :value="item.value" />
                    </el-select>
                </el-form-item>
                <el-form-item label="本地父体" required style="margin-bottom:0;">
                    <el-select v-model="genMultiSkuImportParent" :disabled="genMultiResolveLoading" filterable clearable placeholder="父体（可选，仅用于消除歧义）"
                        style="width:100%;">
                        <el-option v-for="p in parentOptions" :key="'gen-sku-parent-' + p.value" :label="p.text"
                            :value="p.value" />
                    </el-select>
                </el-form-item>
            </el-form>
            <el-input v-model="genMultiSkuText" :disabled="genMultiResolveLoading" type="textarea" :rows="8" placeholder="请从 Excel 复制 SKU 列并粘贴到这里，每行一个 SKU"
                @input="updateGenMultiSkuPasteCount" @paste.native="onGenMultiSkuPaste" />
            <div class="gen-sku-import-actions">
                <span class="msku-hint">已识别 {{ genMultiSkuPasteCount }} 个非空 SKU。首行“SKU/本地SKU/关联SKU”表头会自动忽略；识别范围：{{
                    genMultiSkuImportParent || '按 SKU 自动识别父体' }} / {{ genMultiSkuImportShop || '未选择店铺' }}。</span>
            </div>
            <span slot="footer">
                <el-button :disabled="genMultiResolveLoading" @click="genMultiSkuImportVisible = false">取消</el-button>
                <el-button type="primary" :loading="genMultiResolveLoading"
                    :disabled="!genMultiSkuPasteCount"
                    @click="resolveGenMultiSkus">识别并带出配置</el-button>
            </span>
        </el-dialog>

        <!-- 批量生成弹窗：两个入口共用同一容器，模式只切换工作流内容。 -->
        <el-dialog :title="genMultiMode === 'asin' ? '批量新建ASIN' : '批量生成MSKU'" :visible.sync="genMultiVisible" width="1500px"
            custom-class="au-dialog main-feature-dialog gen-multi-dialog" :close-on-click-modal="false" append-to-body
            :close-on-press-escape="!genMultiBusy" :show-close="!genMultiBusy"
            :before-close="closeGenMulti" @closed="resetGenMulti" @opened="refreshGenMultiTableLayout">
            <div slot="title" class="gen-multi-title">
                <span>{{ genMultiMode === 'asin' ? '批量新建ASIN' : '批量生成MSKU' }}</span>
                <el-radio-group v-model="genMultiView" size="mini" class="gen-multi-view-tabs"
                    :disabled="genMultiBusy" @change="refreshGenMultiTableLayout">
                    <el-radio-button label="config">{{ genMultiMode === 'asin' ? '配置字段' : '配置生成' }}</el-radio-button>
                    <el-radio-button label="results"
                        :disabled="genMultiMode === 'asin' ? !(batchAsinPlan.items || []).length : !genMultiResults.length">
                        {{ genMultiMode === 'asin' ? '生成 MSKU' : '生成结果' }}
                    </el-radio-button>
                    <el-radio-button v-if="genMultiMode === 'asin'" label="result"
                        :disabled="!batchAsinResult.batch_id && !batchAsinResultRows.length">创建结果</el-radio-button>
                </el-radio-group>
            </div>
            <template v-if="genMultiMode === 'asin'">
                    <div v-show="genMultiView === 'config'" v-loading="genMultiTableBusy"
                        :inert="genMultiTableBusy ? '' : null" element-loading-text="正在带出配置，请稍候…">
                    <el-alert class="gen-multi-guide" type="info" :closable="false" show-icon
                        title="批量新建 ASIN 先生成 MSKU，再带出对应品名的产品类型和创建字段。可直接使用品名列表勾选，也可上传批量模板；父体和品名按 SKU 关系自动识别。店铺不属于 SKU/品名关系，必须在批量操作栏选择本批次唯一目标店铺后才能继续生成。" />
                    <div class="gen-multi-toolbar">
                        <div class="gen-multi-actions">
                            <span class="gen-multi-status"><i class="el-icon-info"></i>{{ batchAsinRows.length ? '已选 ' + batchAsinRows.length + ' 个品名' : '未选择品名' }}</span>
                            <el-select v-model="batchAsinImportShop" filterable clearable size="small" placeholder="店铺"
                                style="width:130px" aria-label="批量新建ASIN目标店铺" :disabled="genMultiBusy"
                                @change="onBatchAsinImportShopChange">
                                <el-option v-for="item in filters" :key="'batch-asin-import-shop-' + item.value"
                                    :label="item.text" :value="item.value" />
                            </el-select>

                            <div class="gen-multi-field">
                                <span class="gen-multi-field-label">后缀</span>
                                <el-radio-group v-model="batchAsinSuffix" size="small" @change="onBatchAsinSuffixChange">
                                    <el-radio-button label="T">T（创建发货产品）</el-radio-button>
                                    <el-radio-button label="C">C（创建跟卖）</el-radio-button>
                                    <el-radio-button label="CUSTOM">自定义</el-radio-button>
                                </el-radio-group>
                                <el-input v-if="batchAsinSuffix === 'CUSTOM'" v-model="batchAsinCustomSuffix" maxlength="6"
                                    size="small" placeholder="1–6 位字母/数字" style="width:135px" @change="onBatchAsinSuffixChange" />
                            </div>
                            <div class="gen-multi-field">
                                <el-tooltip content="修改后统一应用到全部行，也可在表格中单独调整数量" placement="top">
                                    <span class="gen-multi-field-label">数量</span>
                                </el-tooltip>
                                <el-input-number v-model="batchAsinDefaultNum" :min="1" :max="200" :precision="0"
                                    size="small" :controls="false" style="width:62px" aria-label="批量新建数量"
                                    @change="applyBatchAsinDefaultNum" />
                            </div>
                            <el-tooltip placement="top"
                                content="请先选择品名或上传 MSKU 模板带出品名"
                                :disabled="!!batchAsinRows.length">
                                <span style="display:inline-block">
                                    <el-button type="primary" plain size="small" :loading="batchAsinCfgLoading"
                                        :disabled="batchAsinCfgLoading || !batchAsinRows.length" @click="prepareBatchAsin(true)">
                                        重新带出配置
                                    </el-button>
                                </span>
                            </el-tooltip>
                            <span class="gen-multi-field-label">上传 MSKU 模板</span>
                            <el-tooltip placement="top" content="请先选择目标店铺，再上传 MSKU 模板带出品名"
                                :disabled="!!batchAsinImportShop">
                                <span style="display:inline-block">
                                    <el-upload action="#" :auto-upload="false" :show-file-list="false" accept=".xlsx"
                                        :disabled="genMultiBusy || batchAsinMskuImporting || !batchAsinImportShop"
                                        :on-change="importBatchAsinMskuFile">
                                        <el-button type="primary" plain size="small" :loading="batchAsinMskuImporting"
                                            :disabled="genMultiBusy || batchAsinMskuImporting || !batchAsinImportShop">
                                            上传并带出品名
                                        </el-button>
                                    </el-upload>
                                </span>
                            </el-tooltip>
                            <el-button type="text" size="small" :disabled="genMultiBusy"
                                @click="downloadBatchAsinMskuTemplate">下载批量模板</el-button>
                        </div>
                        <div class="batch-asin-msku-source-hint">模板按 SKU 自动识别父体和品名；同一 SKU 对应多个父体时填写本地父体列。必须先选择目标店铺，才能上传模板带出品名。</div>
                    </div>
                    <msku-generation-table ref="genMultiTable" :rows="batchAsinRows" :asin="true"
                        :busy="genMultiBusy" :actions="generationTableActions" :product-types="productTypes"
                        :batch-product-type="batchAsinProductTypes.length === 1 ? batchAsinProductTypes[0] : ''"
                        :batch-mode="batchGenMode"
                        :failed-sku-count="true ? 0 : genMultiSkuDetailFailedRows.length"
                        @batch-product-type="applyBatchAsinProductType"
                        @batch-mode="applyGenRowMode" />
                    <el-alert v-if="!batchAsinCfgLoading && batchAsinProductTypeConflict" type="error"
                        :closable="false" show-icon style="margin-top:10px;"
                        :title="'本批次只能使用一个产品类型，当前包含：' + batchAsinProductTypeConflictText + '。请统一后再生成 MSKU。'" />
                    <el-alert v-if="!batchAsinCfgLoading && batchAsinOverlongList.length" type="warning"
                        :closable="false" show-icon style="margin-top:10px;">
                        <template slot="title">
                            <div>以下品名的 MSKU 基础名超出最大长度，请先修改基础名预览后再生成：</div>
                            <div v-for="row in batchAsinOverlongList" :key="'asin-overlong-' + row.parent + row.pm + row.shop"
                                class="gen-config-missing-row">
                                {{ row.pm }}：基础名 {{ batchAsinRowBase(row).length }} 字符，最大 {{ row.max_base_len }} 字符
                                <el-button type="text" size="mini" @click="startBatchAsinBaseEdit(row)">编辑基础名</el-button>
                            </div>
                        </template>
                    </el-alert>
                    <el-alert v-if="!batchAsinCfgLoading && batchAsinConfigMissingList.length" type="warning"
                        :closable="false" show-icon style="margin-top:10px;">
                        <template slot="title">
                            <div>请补齐以下字段后再生成：</div>
                            <div v-for="row in batchAsinConfigMissingList" :key="'asin-missing-' + row.parent + row.pm + row.shop"
                                class="gen-config-missing-row">
                                {{ row.pm }}：{{ batchAsinRowIssue(row) }}
                                <el-button v-if="batchAsinShowMskuConfigAction(row) && !row.config_error" type="text" size="mini"
                                    @click="openBatchAsinMskuRule(row)">配置 MSKU</el-button>
                                <el-button v-if="row.config_error" type="text" size="mini" @click="openBatchAsinPmConfig(row)">配置品名</el-button>
                                <el-button v-if="batchAsinNeedsProductType(row)" type="text" size="mini"
                                    @click="openBatchAsinParentConfig(row)">配置父体</el-button>
                            </div>
                        </template>
                    </el-alert>
                </div>
                <div v-if="genMultiView === 'results'" class="gen-multi-results">
                    <el-alert type="info" :closable="false" show-icon class="gen-multi-result-summary"
                        title="MSKU 已生成。下方按品名归组：每个 MSKU 对应一条独立的产品创建记录，主标题按 MSKU 生成独立编号，副标题和其他创建字段默认复用品名配置。同一品名的多个 MSKU 用横向卡片切换查看，卡片可进入编辑模式修改该 MSKU 的全部字段；上传 Excel 后按行覆盖。" />
                    <el-alert v-if="batchAsinImportResult" type="success" :closable="false" show-icon
                        style="margin-bottom:10px;" :title="batchAsinImportResult" />
                    <el-alert v-if="batchAsinSubmitMissingList.length" type="error" :closable="false" show-icon
                        style="margin-bottom:10px;">
                        <template slot="title">
                            <div class="batch-asin-missing-summary">
                                <span><i class="el-icon-warning-outline"></i>
                                    提交前校验失败：{{ batchAsinSubmitMissingList.length }} 个 MSKU 共
                                    {{ batchAsinSubmitMissingFieldCount }} 个空字段，请补齐后再确认创建。</span>
                                <el-button type="text" size="mini"
                                    @click="batchAsinMissingExpanded = !batchAsinMissingExpanded">
                                    {{ batchAsinMissingExpanded ? '收起' : '展开全部' }}
                                </el-button>
                            </div>
                            <div v-for="group in visibleBatchAsinMissingGroups"
                                :key="'asin-missing-group-' + group.pm" class="batch-asin-missing-group">
                                <div class="batch-asin-missing-group-head">
                                    {{ group.pm || '未识别品名' }}
                                    <span class="batch-asin-missing-count">{{ group.items.length }} 个 MSKU</span>
                                </div>
                                <div v-for="missing in group.items" :key="'asin-missing-' + missing.msku"
                                    class="batch-asin-missing-row" @click="focusBatchAsinMissing(missing)">
                                    <span class="batch-asin-missing-msku" :title="missing.msku">{{ missing.msku }}</span>
                                    <span class="batch-asin-missing-fields">
                                        <el-tag v-for="field in missing.fields"
                                            :key="'asin-missing-field-' + missing.msku + '-' + field"
                                            size="mini" type="danger" effect="plain">{{ field }}</el-tag>
                                    </span>
                                    <el-button type="text" size="mini">定位</el-button>
                                </div>
                            </div>
                            <div v-if="!batchAsinMissingExpanded
                                && batchAsinSubmitMissingGroups.length > batchAsinMissingVisibleLimit"
                                class="batch-asin-missing-more">
                                还有 {{ batchAsinSubmitMissingGroups.length - batchAsinMissingVisibleLimit }} 个品名未显示
                            </div>
                        </template>
                    </el-alert>
                    <div class="batch-asin-generated-toolbar">
                        <span>生成的 MSKU 已归入对应品名，可单独显示或隐藏</span>
                        <div class="batch-asin-generated-toolbar-actions">
                            <el-button type="text" size="mini" @click="expandBatchAsinItems">全部显示</el-button>
                            <el-button type="text" size="mini" @click="collapseBatchAsinItems">全部隐藏</el-button>
                        </div>
                    </div>
                    <div v-for="(item, index) in (batchAsinPlan.items || [])" :key="'asin-plan-' + index"
                        class="gen-multi-result-card" :data-batch-asin-item="index">
                        <div style="display:flex;align-items:center;flex-wrap:wrap;gap:6px 14px;font-size:13px;">
                            <b>{{ item.pm }}</b>
                            <span style="color:#909399;">{{ item.parent }} / {{ item.shop }}</span>
                            <el-tag v-if="item.sku" size="mini" type="warning" effect="plain">SKU: {{ item.sku }}</el-tag>
                            <el-tag size="mini" type="info" effect="plain">{{ item.mode === 'template' ? '按模板' : '按属性' }}</el-tag>
                            <el-tag size="mini" effect="plain">后缀: {{ item.suffix }}</el-tag>
                            <el-tag v-if="(item.mskus || []).length" size="mini" type="success">{{ item.mskus.length }} 个待创建</el-tag>
                            <el-tag v-if="(item.reused || []).length" size="mini" type="warning">复用 {{ item.reused.length }} 个</el-tag>
                            <el-tag v-if="(item.conflict || []).length" size="mini" type="danger">冲突 {{ item.conflict.length }} 个</el-tag>
                        </div>
                        <el-button type="text" size="mini" @click="toggleBatchAsinOriginalConfig(item, index)">
                            {{ batchAsinOriginalConfigExpanded.includes(batchAsinItemKey(item, index)) ? '隐藏' : '显示' }}品名原有配置
                        </el-button>
                        <el-button type="text" size="mini" :disabled="genMultiBusy"
                            @click="openBatchAsinPmConfig(item)">编辑品名配置</el-button>
                        <span v-if="batchAsinConfigUpdate(item)" class="batch-asin-config-update">
                            <span v-if="batchAsinConfigUpdate(item).saved" class="is-updated">
                                <i class="el-icon-circle-check"></i> 配置已更新
                            </span>
                            <span v-else-if="batchAsinConfigUpdate(item).multi_row_update" class="is-failed">
                                <i class="el-icon-warning-outline"></i> 已跳过配置写回：{{
                                    batchAsinConfigUpdate(item).error }}
                            </span>
                            <span v-else class="is-failed">
                                <i class="el-icon-warning-outline"></i> 配置更新失败：{{ batchAsinConfigUpdate(item).error || '未知原因' }}
                            </span>
                        </span>
                        <div v-show="batchAsinOriginalConfigExpanded.includes(batchAsinItemKey(item, index))"
                            class="batch-asin-config-preview">
                            <div class="batch-asin-config-item">
                                <span class="batch-asin-config-label">产品类型</span>
                                <span class="batch-asin-config-value">{{ productTypeLabel(item.product_type) || '未填写' }}</span>
                            </div>
                            <div class="batch-asin-config-item">
                                <span class="batch-asin-config-label">MSKU颜色 / 供应商</span>
                                <span class="batch-asin-config-value">{{ item.msku_color || '未填写' }} / {{ item.supplier || '未填写' }}</span>
                            </div>
                            <div v-for="field in batchAsinConfigPreviewFields(item)" :key="field.key"
                                class="batch-asin-config-item">
                                <span class="batch-asin-config-label">{{ field.label }}</span>
                                <span class="batch-asin-config-value" :class="{ 'is-empty': batchAsinFieldValueText(item, field) === '未填写' }">
                                    {{ batchAsinFieldValueText(item, field) }}
                                </span>
                            </div>
                        </div>
                        <div v-if="(item.reused || []).length" class="batch-asin-config-empty">
                            已复用：{{ batchAsinReuseList(item.reused) }}
                        </div>
                        <div v-if="(item.conflict || []).length" class="batch-asin-config-empty">
                            已拦截：{{ batchAsinReuseList(item.conflict) }}
                        </div>
                        <div v-if="(item.mskus || []).length" class="batch-asin-msku-toggle-row">
                            <span>生成的 MSKU（{{ item.mskus.length }}）<em class="batch-asin-msku-field-hint">横向切换查看每个 MSKU 的字段，可逐个进入编辑模式</em></span>
                            <el-button type="text" size="mini" @click="toggleBatchAsinItem(item, index)">
                                <i :class="isBatchAsinItemExpanded(item, index) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                                {{ isBatchAsinItemExpanded(item, index) ? '隐藏' : '显示' }}
                            </el-button>
                        </div>
                        <div v-if="(item.mskus || []).length" v-show="isBatchAsinItemExpanded(item, index)"
                            class="batch-asin-item-mskus">
                            <batch-asin-msku-cards :mskus="item.mskus"
                                :active-index="batchAsinItemActiveIndex(item)"
                                :editing="!!item._mskuEditing"
                                :rows="batchAsinMskuFieldRows(item, batchAsinItemActiveMsku(item))"
                                :field-defs="item.fields || []"
                                :raw-values="batchAsinMskuFieldValues(item, batchAsinItemActiveMsku(item))"
                                :quantity="batchAsinMskuQuantity(item, batchAsinItemActiveMsku(item))"
                                :update-config="!!(item.msku_config_updates || {})[batchAsinItemActiveMsku(item)]"
                                :busy="genMultiBusy"
                                @select="targetIndex => onBatchAsinMskuSelect(item, targetIndex)"
                                @toggle-edit="value => onBatchAsinMskuEditToggle(item, value)"
                                @change="payload => onBatchAsinMskuFieldChange(item, batchAsinItemActiveMsku(item), payload)"
                                @update-config-change="value => setBatchAsinConfigUpdate(item, batchAsinItemActiveMsku(item), value)"
                                @quantity-change="value => onBatchAsinMskuQuantityChange(item, batchAsinItemActiveMsku(item), value)" />
                        </div>
                    </div>
                </div>
                <div v-if="genMultiView === 'result'" class="gen-multi-results">
                    <el-alert :title="'ASIN 创建任务已提交：' + (batchAsinResult.batch_id || '-')"
                        type="success" :closable="false" show-icon style="margin-bottom:10px;" />
                    <el-table :data="batchAsinResultRows" size="small" max-height="430" border stripe>
                        <el-table-column type="index" label="#" width="55" align="center" />
                        <el-table-column prop="msku" label="生成的 MSKU" min-width="220" show-overflow-tooltip />
                        <el-table-column label="主任务 / 刊登任务" min-width="230" show-overflow-tooltip>
                            <template slot-scope="scope">
                                <div v-if="scope.row.master_task_id">主任务：{{ scope.row.master_task_id }}</div>
                                <div v-if="scope.row.publish_task_id">刊登任务：{{ scope.row.publish_task_id }}</div>
                                <span v-if="!scope.row.master_task_id && !scope.row.publish_task_id" class="batch-asin-task-muted">—</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="工作台" width="90" align="center">
                            <template slot-scope="scope">
                                <el-button v-if="scope.row.master_task_id" type="text" size="mini"
                                    @click="openBatchAsinTask(scope.row)">查看</el-button>
                                <span v-else class="batch-asin-task-muted">—</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="状态" width="110" align="center">
                            <template slot-scope="scope">
                                <el-tag size="mini" :type="batchAsinStatusTagType(scope.row.status)">
                                    {{ batchAsinStatusText(scope.row.status) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="reason" label="说明" min-width="220" show-overflow-tooltip />
                    </el-table>
                </div>
            </template>
            <template v-else>
            <div v-show="genMultiView === 'config'" v-loading="genMultiTableBusy" :inert="genMultiTableBusy ? '' : null"
                element-loading-text="正在处理，请稍候…">
            <el-alert class="gen-multi-guide" type="info" :closable="false" show-icon
                title="可勾选品名、粘贴 SKU 或上传表格；重新导入会替换当前列表。系统按 SKU 关系识别父体和品名，从 ERP 缓存带出供应商，颜色、供应商及基础名均可修改。T 行默认开启配对，请填写关联 SKU。点击“生成 MSKU”仅生成预览，点击“下载 Excel 并保存任务”后才保存到 Auto 监控队列。" />
            <div class="gen-multi-toolbar">
                <div class="gen-multi-actions">
                    <span class="gen-multi-status"><i class="el-icon-info"></i>{{ genMultiRows.length ? '已选 ' + genMultiRows.length + ' 个品名' : '未选择品名' }}</span>
                        <el-select v-model="genMultiImportShop" filterable clearable size="small" placeholder="店铺"
                            style="width:130px" aria-label="导入店铺" :disabled="genMultiBusy" @change="onGenMultiImportShopChange">
                            <el-option v-for="item in filters" :key="'gen-import-shop-' + item.value" :label="item.text"
                                :value="item.value" />
                        </el-select>

                    <div class="gen-multi-field">
                        <span class="gen-multi-field-label">后缀</span>
                        <el-radio-group v-model="genMultiSuffix" size="small" @change="onGenMultiSuffixChange">
                            <el-radio-button label="T">T（创建发货产品）</el-radio-button>
                            <el-radio-button label="C">C（创建跟卖）</el-radio-button>
                            <el-radio-button label="CUSTOM">自定义</el-radio-button>
                        </el-radio-group>
                        <el-input v-if="genMultiSuffix === 'CUSTOM'" v-model="genMultiCustomSuffix" maxlength="6"
                            size="small" placeholder="1–6 位字母/数字" style="width:135px" @change="onGenMultiCustomSuffixInput" />
                    </div>
                    <div class="gen-multi-field">
                        <el-tooltip content="修改后统一应用到全部行，也可在表格中单独调整数量" placement="top">
                            <span class="gen-multi-field-label">数量</span>
                        </el-tooltip>
                        <el-input-number v-model="genMultiDefaultNum" :min="1" :max="200" :precision="0"
                            size="small" :controls="false" style="width:62px" aria-label="批量生成数量"
                            @change="applyGenMultiQuantity" />
                    </div>
                    <div class="gen-multi-field">
                        <span class="gen-multi-field-label">创建后配对</span>
                        <el-tag size="mini" :type="effectiveGenMultiSuffix() === 'T' ? 'success' : 'info'" effect="light">
                            {{ effectiveGenMultiSuffix() === 'T' ? '默认开启' : '按行设置' }}
                        </el-tag>
                    </div>
                    <el-upload action="#" :auto-upload="false" :show-file-list="false" accept=".xlsx"
                        :disabled="genMultiBusy" :on-change="importGenMultiColorFile">
                        <el-button type="primary" plain size="small" :loading="genMultiColorImporting"
                            :disabled="genMultiBusy">上传表格生成</el-button>
                    </el-upload>
                    <el-button type="primary" plain size="small" @click="openGenMultiSkuImport">粘贴 SKU</el-button>
                    <el-button type="text" size="small" @click="downloadMskuColorTemplate">下载批量模板</el-button>
                </div>
            </div>
            <msku-generation-table ref="genMultiTable" :rows="genMultiRows" :asin="false"
                        :busy="genMultiBusy" :actions="generationTableActions" :product-types="productTypes"
                        :batch-product-type="batchAsinProductTypes.length === 1 ? batchAsinProductTypes[0] : ''"
                        :batch-mode="batchGenMode"
                        :failed-sku-count="false ? 0 : genMultiSkuDetailFailedRows.length"
                        @batch-product-type="applyBatchAsinProductType"
                        @batch-mode="applyGenRowMode" />
            <el-alert v-if="!genMultiCfgLoading && genMultiConfigMissingList.length" type="warning" :closable="false"
                show-icon style="margin-top:10px;">
                <template slot="title">
                    <div>有 {{ genMultiConfigMissingList.length }} 行暂不能生成，请按品名补齐缺失字段：</div>
                    <div v-for="row in genMultiConfigMissingList" :key="'missing-' + row.parent + row.pm + row.shop"
                        class="gen-config-missing-row">
                        {{ row.pm }}：{{ genRowMissingText(row) }}
                        <el-button v-if="genRowShowParentRuleAction(row)" type="text" size="mini"
                            @click="openGenParentRule(row)">配置父体规则</el-button>
                        <el-button v-if="genRowShowPmRuleAction(row)" type="text" size="mini"
                            @click="openGenPmRule(row)">配置品名规则</el-button>
                    </div>
                </template>
            </el-alert>
            </div>
            <div v-if="genMultiResults.length" v-show="genMultiView === 'results'" class="gen-multi-results">
                <el-alert :type="genMultiResultsStale ? 'warning' : (genMultiFailedList.length ? 'error' : 'success')"
                    :title="genMultiResultsStale ? '配置已变更，请返回配置页重新生成预览' : (genMultiFailedList.length ? '部分记录生成失败，请修正后重新生成' : ('已生成 ' + genMultiGeneratedCount + ' 个 MSKU' + (genMultiSavedCount ? '，任务已保存，可再次下载' : '，下载 Excel 时将保存任务')))"
                    :closable="false" show-icon class="gen-multi-result-summary" />
                <!-- 汇总：失败（缺配置）+ 截断，一眼看出哪几个品名缺什么 -->
                <el-alert v-if="genMultiFailedList.length" type="error" :closable="false" show-icon
                    style="margin-bottom:10px;">
                    <template slot="title">
                        {{ genMultiFailedList.length }} 个品名生成失败：
                        <div v-for="(f, i) in genMultiFailedList" :key="'f' + i"
                            style="font-size:12px;font-weight:normal;color:#F56C6C;margin-top:2px;">
                            {{ f.pm }}（{{ f.parent }} / {{ f.shop }}）：{{ f.error }}
                        </div>
                    </template>
                </el-alert>
                <el-alert v-if="genMultiTruncList.length" type="warning" :closable="false" show-icon
                    style="margin-bottom:10px;">
                    <template slot="title">
                        {{ genMultiTruncList.length }} 个品名基础名超长被截断：
                        <div v-for="(t, i) in genMultiTruncList" :key="'t' + i"
                            style="font-size:12px;font-weight:normal;color:#E6A23C;margin-top:2px;">
                            {{ t.pm }}：基础名 {{ t.base_len }} 字符超出最大 {{ t.max_base_len }} 字符
                        </div>
                    </template>
                </el-alert>
                <div v-for="(r, i) in genMultiResults" :key="i" class="gen-multi-result-card">
                    <div style="display:flex;align-items:center;flex-wrap:wrap;gap:6px 14px;font-size:13px;">
                        <b>{{ r.pm }}</b>
                        <span style="color:#909399;">{{ r.parent }} / {{ r.shop }}</span>
                        <el-tag v-if="r.sku" size="mini" type="warning" effect="plain">SKU: {{ r.sku }}</el-tag>
                        <el-tag v-if="r.mode" size="mini" type="info" effect="plain">
                            {{ r.mode === 'attribute' ? '按属性' : '按模板' }}</el-tag>
                        <el-tag v-if="r.suffix" size="mini" effect="plain">后缀: {{ r.suffix }}</el-tag>
                        <el-tag v-if="r.success" size="mini" type="success">{{ (r.mskus || []).length }} 个</el-tag>
                        <el-tag v-else size="mini" type="danger">失败</el-tag>
                    </div>
                    <div v-if="!r.success" style="font-size:12px;color:#F56C6C;margin-top:4px;">{{ r.error }}</div>
                    <template v-else>
                        <div v-if="r.truncated" style="font-size:12px;color:#CC0000;font-weight:bold;margin-top:4px;">
                            当前生成模式基础名超长已被截断：基础名 {{ r.base_len }} 字符超出最大 {{ r.max_base_len }}
                            字符（{{ r.base }} → {{ (r.base || '').slice(0, r.max_base_len) }}），请查看配置字符是否超出
                        </div>
                        <div v-else style="font-size:12px;color:#409eff;margin-top:4px;">
                            基础名: {{ r.base }}（共 {{ (r.mskus || []).length }} 个）
                        </div>
                        <div class="gen-result-mskus">
                            <el-tag v-for="m in (genMultiExpandedResults.includes(i) ? r.mskus : (r.mskus || []).slice(0, 8))" :key="m" size="small" :type="r.truncated ? 'danger' : ''"
                                style="margin:2px 4px 2px 0;">{{ m }}</el-tag>
                        </div>
                        <el-button v-if="(r.mskus || []).length > 8" type="text" size="small" @click="toggleGenMultiResult(i)">
                            {{ genMultiExpandedResults.includes(i) ? '收起' : '展开全部 ' + r.mskus.length + ' 个 MSKU' }}
                        </el-button>
                    </template>
                </div>
            </div>
            </template>
            <div slot="footer" class="gen-multi-footer">
                <template v-if="genMultiMode === 'asin'">
                    <div v-if="batchAsinRows.length" class="gen-multi-footer-status" aria-live="polite">
                        <span>{{ batchAsinRows.length }} 个品名 / {{ genMultiView === 'results' || genMultiView === 'result' ? '已生成 ' + (batchAsinPlan.mskus || []).length : '待生成 MSKU' }}</span>
                        <span>{{ genMultiView === 'config' ? (batchAsinMissingShopList.length ? '请在批量操作栏选择本批次唯一目标店铺' : (batchAsinLengthBudgetLoading ? '正在计算 MSKU 长度预算' : ((batchAsinConfigMissingList.length || batchAsinOverlongList.length) ? '请修正配置后再生成' : '配置已带出，可生成 MSKU'))) : '核对字段后确认创建 ASIN' }}</span>
                    </div>
                    <div class="gen-multi-footer-actions">
                        <el-button :disabled="genMultiBusy" @click="closeGenMulti()">关闭</el-button>
                        <el-button v-if="genMultiView !== 'config'" :disabled="genMultiBusy"
                            @click="genMultiView = 'config'; refreshGenMultiTableLayout()">返回配置</el-button>
                        <el-tooltip v-if="genMultiView === 'config'" placement="top"
                            :content="batchAsinGenerateHint" :disabled="!batchAsinGenerateHint">
                            <span style="display:inline-block">
                                <el-button type="primary" :loading="batchAsinLoading"
                                    :disabled="genMultiBusy || !batchAsinRows.length || batchAsinConfigMissingList.length
                                        || batchAsinOverlongList.length || batchAsinProductTypeConflict || batchAsinMissingShopList.length
                                        || batchAsinLengthBudgetLoading"
                                    @click="generateBatchAsin">生成 MSKU 并带出字段</el-button>
                            </span>
                        </el-tooltip>
                        <template v-if="genMultiView === 'results'">
                            <el-button type="primary" plain :loading="batchAsinLoading"
                                :disabled="genMultiBusy || !(batchAsinPlan.mskus || []).length"
                                @click="exportBatchAsinPlan">导出字段 Excel</el-button>
                            <el-button type="warning" plain :loading="batchAsinImporting"
                                :disabled="genMultiBusy || !(batchAsinPlan.mskus || []).length"
                                @click="pickBatchAsinImport">上传修改后的 Excel</el-button>
                            <el-button type="primary" :loading="batchAsinLoading"
                                :disabled="genMultiBusy || !(batchAsinPlan.items || []).length || batchAsinMissingShopList.length"
                                @click="executeBatchAsin">确认创建 ASIN</el-button>
                        </template>
                        <el-button v-if="genMultiView === 'result' && batchAsinResult.batch_id" type="primary" plain
                            :loading="batchAsinLoading" :disabled="genMultiBusy"
                            @click="refreshBatchAsinTasks">刷新任务状态</el-button>
                    </div>
                </template>
                <template v-else>
                    <div v-if="genMultiRows.length" class="gen-multi-footer-status" aria-live="polite">
                        <span>{{ genMultiRows.length }} 个品名 / {{ genMultiView === 'results' ? '已生成 ' + genMultiGeneratedCount : '预计生成 ' + genMultiTotalMskus }} 个 MSKU</span>
                        <span>{{ genMultiFooterHint }}</span>
                    </div>
                    <div class="gen-multi-footer-actions">
                        <el-button :disabled="genMultiBusy" @click="closeGenMulti()">关闭</el-button>
                        <el-button v-if="genMultiView === 'results'" :disabled="genMultiBusy"
                            @click="genMultiView = 'config'; refreshGenMultiTableLayout()">返回修改</el-button>
                        <el-button type="primary" :loading="genMultiExporting || genMultiLoading"
                            :disabled="genMultiBusy || (!genMultiSaveReady && !genMultiCanGenerate)"
                            @click="onGenMultiPrimaryAction">
                            {{ genMultiView === 'results' && genMultiSaveReady ? (genMultiSavedCount ? '下载 Excel' : '下载 Excel 并保存任务')
                                : (genMultiResultsStale || genMultiFailedList.length ? '重新生成 MSKU' : '生成 MSKU') }}
                        </el-button>
                    </div>
                </template>
            </div>
            <input v-if="genMultiMode === 'asin'" ref="batchAsinFileInput" type="file" accept=".xlsx"
                style="display:none" @change="onBatchAsinFilePicked" />
        </el-dialog>
    </div>
</template>

<script>
import MskuGenerationTable from './MskuGenerationTable.vue';
import BatchAsinMskuCards from './BatchAsinMskuCards.vue';
import PmConfigDialog from './PmConfigDialog.vue';
import ParentConfigDialog from './ParentConfigDialog.vue';
import MskuRuleDialog from './MskuRuleDialog.vue';
import AutoWorkbenchDialog from './AutoWorkbenchDialog.vue';
import ColorTranslation from './ColorTranslation.vue';
import { confirmMskuRules, configSaveSummary } from './configSaveFlow';

export default {
    components: {
        MskuGenerationTable,
        BatchAsinMskuCards,
        PmConfigDialog, ParentConfigDialog, MskuRuleDialog, AutoWorkbenchDialog, ColorTranslation,
    },
    data() {
        return {
            searchForm: { shop: '', parentName: '' },
            isSending: false,
            pmFilter: '',
            tableData: [],
            allParentList: [],
            filters: [
                { text: 'CoBak-US', value: 'CoBak-US' }, { text: 'CoBak-UK', value: 'CoBak-UK' },
                { text: 'CoBak-SG', value: 'CoBak-SG' }, { text: 'CoBak-SE', value: 'CoBak-SE' },
                { text: 'CoBak-PL', value: 'CoBak-PL' }, { text: 'CoBak-NL', value: 'CoBak-NL' },
                { text: 'CoBak-MX', value: 'CoBak-MX' }, { text: 'CoBak-JP', value: 'CoBak-JP' },
                { text: 'CoBak-IT', value: 'CoBak-IT' }, { text: 'CoBak-INDIA-IN', value: 'CoBak-INDIA-IN' },
                { text: 'CoBak-FR', value: 'CoBak-FR' }, { text: 'CoBak-ES', value: 'CoBak-ES' },
                { text: 'CoBak-DE', value: 'CoBak-DE' }, { text: 'CoBak-CA', value: 'CoBak-CA' },
                { text: 'CoBak-BR', value: 'CoBak-BR' }, { text: 'CoBak Direct-BE', value: 'CoBak Direct-BE' },
                { text: 'SMIFACE-CA', value: 'SMIFACE-CA' }, { text: 'SMIFACE-US', value: 'SMIFACE-US' },
            ],
            // 移入
            moveInVisible: false, moveInStep: 'form', moveInRow: {},
            moveInLoading: false, moveInPreviewData: {}, moveInExtraValues: {},
            moveInResult: {}, moveInTaskId: '',
            // 指定 ASIN 移入候选：本地父体/品名 → 关联SKU → 当前店铺 Listing
            moveInCandidates: [], moveInCandidateLoading: false, moveInSelectedAsin: '',
            // 移出
            moveOutVisible: false, moveOutStep: 'confirm', moveOutRow: {},
            moveOutLoading: false, moveOutPreviewInfo: {}, moveOutResult: {},
            moveOutPreviewLoading: false, moveOutSnapshotKey: '',
            // Auto统一工作台
            dialogAutoWorkbench: false,
            autoWorkbenchTab: 'msku',
            autoWorkbenchMsku: '',
            taskList: [],
            activeTaskCount: 0,
            // 父体配置 / 品名配置
            dialogParentConfigVisible: false,
            dialogMskuRuleVisible: false,
            dialogMskuPmRuleVisible: false,
            mskuPmRuleMeta: { parent: '', pm: '', shop: '' },
            dialogPmConfigVisible: false,
            pmConfigMeta: { '本地父体': '', '本地品名': '', '店铺': '', msku: '' },
            // 批量生成弹窗内打开品名配置时，保存后刷新对应行的生成配置
            // 颜色配置
            colorConfigVisible: false,
            productTypes: [],
            // 创建并配对（选 SKU，走后端复合方法，无采购线）
            createPairVisible: false,
            createPairLoading: false,
            createPairRow: {},
            createPairSku: '',
            createPairSkus: [],
            createPairError: '',
            // 批量更新
            batchDialogVisible: false,
            batchStep: 'form',
            batchKind: 'pm',
            batchProductType: '',
            batchDownloading: false,
            batchRows: [],
            batchErrors: [],
            batchExecuting: false,
            batchResults: [],
            batchSummary: null,
            // 批量更新MSKU（模板上传）
            mskuBatchVisible: false,
            mskuBatchStep: 'form',
            mskuBatchDownloading: false,
            mskuBatchUploading: false,
            mskuBatchRows: [],
            mskuBatchErrors: [],
            mskuBatchExecuting: false,
            mskuBatchResults: [],
            mskuBatchSummary: null,
            // SKU + 颜色 Excel 导入（MSKU 域；不写产品操作配置字段）
            mskuColorImportVisible: false,
            mskuColorImportParent: '',
            mskuColorImportShop: '',
            mskuColorImportSuffix: 'T',
            mskuColorImportShouldPair: true,
            mskuColorImportRequestId: '',
            mskuColorImportFiles: [],
            mskuColorImportFile: null,
            mskuColorImportRows: [],
            mskuColorImportErrors: [],
            mskuColorImportPreviewing: false,
            mskuColorImportSaving: false,
            mskuColorImportDownloading: false,
            // 批量生成MSKU（表格勾选多品名）
            tableSelection: [],
            // 批量新建ASIN（只创建任务，不写 product_msku_rel）
            batchAsinLoading: false,
            batchAsinCfgLoading: false,
            batchAsinImporting: false,
            batchAsinMskuImporting: false,
            batchAsinImportResult: '',
            batchAsinRows: [],
            batchAsinPlan: { items: [], mskus: [], reused: [], conflict: [] },
            batchAsinExpandedItems: [],
            batchAsinOriginalConfigExpanded: [],
            batchAsinResult: {},
            batchAsinResultRows: [],
            batchAsinRequestId: '',
            // 导入后品名配置的更新结果：在「品名原有配置」后展示「配置已更新」
            batchAsinConfigResults: [],
            // 提交前校验失败明细默认只展示前几个品名，其余折叠
            batchAsinMissingExpanded: false,
            batchAsinMissingVisibleLimit: 5,
            // 生成结果里「保存 MSKU 生成配置到品名规则」的请求状态
            batchAsinConfigSyncing: false,
            batchAsinConfigSaved: false,
            // Auto 混合批次采购草稿与采购汇总确认
            batchAsinDraft: null,
            batchAsinDraftVisible: false,
            batchAsinDraftConfirming: false,
            batchAsinDraftConfirmId: '',
            batchAsinSuffix: 'T',
            batchAsinCustomSuffix: '',
            batchAsinDefaultNum: 1,
            batchAsinImportShop: '',
            batchAsinImportParent: '',
            batchAsinPrepareToken: 0,
            batchAsinLengthBudgetToken: 0,
            // 展开「品名原有配置」时实时读配置的请求序号（旧响应丢弃）
            batchAsinOriginalConfigToken: 0,
            batchAsinLengthBudgetLoading: false,
            batchAsinParentConfigVisible: false,
            batchAsinParentConfigMeta: { parent: '' },
            // 批量新建ASIN 缺 MSKU 生成配置时使用的 MSKU 专用快捷弹窗（只维护基础名规则）
            batchAsinMskuRuleVisible: false,
            batchAsinMskuRuleMeta: { parent: '', pm: '', shop: '' },
            genMultiVisible: false,
            // 同一个批量弹窗的入口模式：msku 保持原有生成界面，asin 进入创建 ASIN 字段界面。
            genMultiMode: 'msku',
            genMultiView: 'config',
            genMultiPreviewSignature: '',
            genMultiExpandedResults: [],
            genMultiLoading: false,
            genMultiExporting: false,
            genMultiSuffix: 'T',
            genMultiCustomSuffix: '',
            genMultiDefaultNum: 10,
            // 「生成模式」列表头的一键应用值；行内单独切换不受影响。
            batchGenMode: '',
            genMultiCfgLoading: false,
            genMultiRows: [],
            genMultiResults: [],
            genMultiBatchId: '',
            genMultiRequestId: '',
            genMultiSavedCount: 0,
            genMultiImportShop: '',
            genMultiImportParent: '',
            // 批量弹窗的店铺是整批唯一目标，不作为模板列或行级可编辑字段。
            // 父体选项与外层共用 parentOptions；批量目标店铺独立保存。
            genMultiConfigSyncing: false,
            genMultiConfigSaved: false,
            genMultiColorImporting: false,
            // SKU → ERP 本地品名/供应商详情缓存；后端优先读本地缓存，未命中才请求 ERP。
            genMultiSkuDetailCache: {},
            genMultiSkuDetailLoading: {},
            genMultiSkuImportVisible: false,
            genMultiSkuImportShop: '',
            genMultiSkuImportParent: '',
            genMultiSkuText: '',
            genMultiSkuPasteCount: 0,
            genMultiResolveLoading: false,
            // 生成待填模板（按外层品名列表勾选生成）
            fillTplVisible: false,
            fillParentTplVisible: false,
            fillParents: [],
            fillParentFilter: '',
            fillTplProductType: '',
            fillDownloading: false,
        }
    },
    computed: {
        // 是否存在逐 MSKU 正数采购数量：有则必须先走采购汇总确认。
        // 只统计当前生成集合内的 MSKU，重新生成后的历史残留数量不参与判断。
        batchAsinHasProcurement() {
            const items = (this.batchAsinPlan && this.batchAsinPlan.items) || [];
            return items.some(item => (item.mskus || []).some(
                msku => this.batchAsinMskuQuantity(item, msku) > 0));
        },
        // 采购汇总行（仅正数量明细）。
        batchAsinDraftRows() {
            const items = (this.batchAsinDraft && (this.batchAsinDraft.items
                || (this.batchAsinDraft.plan || {}).items)) || [];
            const rows = [];
            items.forEach(item => {
                (item.allocation_rows || []).forEach(allocation => {
                    if (Number(allocation.quantity) > 0) {
                        rows.push({
                            pm: item.pm || '', shop: item.display_shop || item.shop || '',
                            msku: allocation.msku || '', quantity: Number(allocation.quantity) || 0,
                            warehouse: allocation.warehouse || item.warehouse || '',
                        });
                    }
                });
            });
            return rows;
        },
        batchAsinDraftTotalQuantity() {
            return this.batchAsinDraftRows.reduce((total, row) => total + row.quantity, 0);
        },
        batchAsinDraftConfigSaveText() {
            const draft = this.batchAsinDraft || {};
            try { return configSaveSummary(draft.items || (draft.plan || {}).items || []); }
            catch (error) { return error.message; }
        },
        // 移出快照是否可直接提交：必须已预览且店铺/MSKU 与快照范围一致。
        moveOutSnapshotReady() {
            const info = this.moveOutPreviewInfo || {};
            if (!info.success) return false;
            const shop = this.moveOutRow['店铺'] || '';
            const msku = (this.moveOutRow.msku || '').trim();
            return !!shop && !!msku && this.moveOutSnapshotKey === shop + '|' + msku;
        },
        generationTableActions() {
            const asin = this.genMultiMode === 'asin';
            return {
                genMultiRowClass: asin ? this.batchAsinRowClass : this.genMultiRowClass,
                genMultiRowIssue: asin ? this.batchAsinRowIssue : this.genMultiRowIssue,
                retryGenMultiSkuDetails: this.retryGenMultiSkuDetails,
                onGenRowSkuChange: asin ? this.onBatchAsinSkuChange : this.onGenRowSkuChange,
                genSkuOptionLabel: asin ? this.batchAsinSkuOptionLabel : this.genSkuOptionLabel,
                isGenSkuDetailLoading: asin ? () => false : this.isGenSkuDetailLoading,
                onGenRowSkuInput: asin ? this.onBatchAsinSkuChange : this.onGenRowSkuInput,
                loadGenRowSkuDetail: asin ? this.onBatchAsinSkuChange : this.loadGenRowSkuDetail,
                onGenRowSuffixChange: asin ? this.onBatchAsinRowSuffixChange : this.onGenRowSuffixChange,
                onGenRowPairChange: asin ? this.onBatchAsinRowPairChange : this.onGenRowPairChange,
                onGenRowColorChange: asin ? this.onBatchAsinColorChange : this.onGenRowColorChange,
                // 只有批量新建ASIN 支持「颜色来源」选择（品名配置色 vs 本次上传色）
                onGenRowColorSourceChange: asin ? this.onBatchAsinColorSourceChange : () => {},
                onGenRowSupplierChange: asin ? this.onBatchAsinSupplierChange : this.onGenRowSupplierChange,
                onGenRowCountryChange: asin ? this.onBatchAsinCountryChange : this.onGenRowCountryChange,
                onGenRowModeChange: asin ? this.onBatchAsinModeChange : this.onGenRowModeChange,
                genRowOverlong: asin ? this.batchAsinRowOverlong : this.genRowOverlong,
                genRowBase: asin ? this.batchAsinRowBase : this.genRowBase,
                startGenRowEdit: asin ? this.startBatchAsinBaseEdit : this.startGenRowEdit,
                saveGenRowBaseEdit: asin ? this.saveBatchAsinBaseEdit : this.saveGenRowBaseEdit,
                resetGenRowBase: asin ? this.resetBatchAsinBase : this.resetGenRowBase,
                onGenMultiRowCommand: asin ? this.onBatchAsinRowCommand : this.onGenMultiRowCommand,
                onBatchAsinProductTypeChange: this.onBatchAsinProductTypeChange,
                genRowShouldPair: this.genRowShouldPair,
                quantityChange: asin ? this.invalidateBatchAsinPlan : () => {},
            };
        },
        // 批量新建ASIN：配置不全的行（MSKU 生成配置缺失或产品类型未配置）
        batchAsinConfigMissingList() {
            return this.batchAsinRows.filter(row => this.batchAsinMissingFields(row).length
                || row.sku_detail_error || row.config_error || row.import_error);
        },
        // 批量新建 ASIN：入口处拦截基础名超长，避免生成后才发现被截断。
        batchAsinOverlongList() {
            return this.batchAsinRows.filter(row => this.batchAsinRowOverlong(row));
        },
        // 批量新建 ASIN：生成后按每个 MSKU 检查实际创建字段，确认提交前不得有空值。
        batchAsinSubmitMissingList() {
            const missing = [];
            (this.batchAsinPlan.items || []).forEach(item => {
                const productType = String(item.product_type || '').trim();
                const definitions = [];
                const seen = new Set();
                const addDefinition = (field, fallbackLabel) => {
                    const fn = field && String(field.field_name || '').trim();
                    if (!fn || seen.has(fn)) return;
                    seen.add(fn);
                    definitions.push({
                        key: fn,
                        label: (field && (field.title_zh || field.title_en)) || fallbackLabel || fn,
                        field: field || {},
                    });
                };
                // 标题不是普通 fields 的输入项，但最终也是刊登字段，必须逐 MSKU 检查。
                addDefinition({ field_name: 'item_name' }, '标题');
                addDefinition({ field_name: 'title_differentiation' }, '副标题');
                (item.fields || []).forEach(field => addDefinition(field));

                (item.mskus || []).forEach(msku => {
                    const values = this.batchAsinMskuFieldValues(item, msku) || {};
                    const fields = [];
                    if (!productType) fields.push('产品类型');
                    if (!definitions.length) fields.push('创建字段');
                    definitions.forEach(descriptor => {
                        const hasValue = Object.prototype.hasOwnProperty.call(values, descriptor.key);
                        let value = hasValue ? values[descriptor.key] : undefined;
                        const defaultValue = descriptor.field && descriptor.field.default_value;
                        // 仅字段未出现在快照中时使用默认值；显式配置的空值必须拦截。
                        if (!hasValue && defaultValue !== undefined) value = defaultValue;
                        if (this.batchAsinValueIsEmpty(value)) fields.push(descriptor.label);
                    });
                    if (fields.length) missing.push({
                        msku: String(msku || '').trim() || '(空MSKU)',
                        pm: item.pm || '',
                        fields: [...new Set(fields)],
                    });
                });
            });
            return missing;
        },
        // 提交前校验失败：按品名分组，便于折叠与定位到对应 MSKU 卡片
        batchAsinSubmitMissingGroups() {
            const groups = [];
            const byPm = new Map();
            (this.batchAsinSubmitMissingList || []).forEach(missing => {
                const pm = missing.pm || '';
                let group = byPm.get(pm);
                if (!group) {
                    group = { pm, items: [] };
                    byPm.set(pm, group);
                    groups.push(group);
                }
                group.items.push(missing);
            });
            return groups;
        },
        batchAsinSubmitMissingFieldCount() {
            return (this.batchAsinSubmitMissingList || [])
                .reduce((total, missing) => total + (missing.fields || []).length, 0);
        },
        visibleBatchAsinMissingGroups() {
            const groups = this.batchAsinSubmitMissingGroups;
            return this.batchAsinMissingExpanded
                ? groups : groups.slice(0, this.batchAsinMissingVisibleLimit);
        },
        // 只有上传模板带出过 MSKU 生成配置时，才提供「保存生成配置到品名规则」
        batchAsinHasUploadRows() {
            return (this.batchAsinPlan.items || []).some(
                item => Object.keys((item && item.upload_overrides) || {}).length > 0);
        },
        batchAsinProductTypes() {
            return [...new Set((this.batchAsinRows || [])
                .map(row => String(row.product_type || row.product_type_choice || '').trim())
                .filter(Boolean))];
        },
        batchAsinProductTypeConflict() {
            return this.batchAsinProductTypes.length > 1;
        },
        batchAsinProductTypeConflictText() {
            return this.batchAsinProductTypes.map(type => this.productTypeLabel(type)).join('、');
        },
        batchAsinMissingShopList() {
            return this.batchAsinRows.filter(row => !String(row.shop || '').trim());
        },
        // 「生成 MSKU 并带出字段」被禁用时的悬浮提示：按最先挡住的环节给出原因。
        batchAsinGenerateHint() {
            if (!this.batchAsinRows.length) return '请先选择品名或上传 MSKU 模板带出品名';
            if (this.batchAsinMissingShopList.length) return '请先选择本批次唯一目标店铺';
            if (this.batchAsinProductTypeConflict) return '本批次只能使用一个产品类型，请统一后再生成';
            if (this.batchAsinOverlongList.length || this.batchAsinConfigMissingList.length) {
                return '请先修正表格中标红的配置后再生成';
            }
            if (this.batchAsinLengthBudgetLoading) return '正在计算 MSKU 长度预算，请稍候';
            return '';
        },
        parentOptions() {
            return (this.allParentList || []).map(p => ({
                text: p['本地父体'] || '', value: p['本地父体'] || ''
            }));
        },
        filteredTableData() {
            const kw = this.pmFilter.toLowerCase();
            return kw ? this.tableData.filter(r => (r['本地品名'] || '').toLowerCase().includes(kw)) : this.tableData;
        },
        fillTplParentCount() {
            return new Set(this.tableSelection.map(r => r['本地父体'])).size;
        },
        filteredFillParents() {
            const kw = (this.fillParentFilter || '').trim().toLowerCase();
            return kw ? this.parentOptions.filter(row => row.text.toLowerCase().includes(kw)) : this.parentOptions;
        },
        // 批量生成：缺少配置（生成失败）的品名列表
        genMultiFailedList() {
            return this.genMultiResults.filter(r => !r.success);
        },
        genMultiBusy() {
            return this.genMultiLoading || this.genMultiExporting || this.genMultiConfigSyncing
                || this.genMultiColorImporting || this.genMultiResolveLoading || this.genMultiCfgLoading
                || this.batchAsinLoading || this.batchAsinCfgLoading || this.batchAsinImporting
                || this.batchAsinMskuImporting
                || Object.values(this.genMultiSkuDetailLoading).some(Boolean);
        },
        // 批量店铺在操作栏统一选择；请求期间锁定，避免整批配置被并发切换。
        genMultiShopSelectionDisabled() {
            return this.genMultiLoading || this.genMultiExporting || this.genMultiConfigSyncing
                || this.genMultiColorImporting || this.genMultiResolveLoading || this.genMultiCfgLoading
                || this.batchAsinLoading || this.batchAsinImporting || this.batchAsinMskuImporting;
        },
        genMultiTableBusy() {
            return this.genMultiShopSelectionDisabled;
        },
        // 仅比较生成相关字段，编辑展开状态、搜索等不会使预览失效。
        genMultiSignature() {
            return JSON.stringify({
                suffix: this.effectiveGenMultiSuffix(),
                rows: this.genMultiRows.map(row => ({
                    ...this.genMultiRowPayload(row),
                    country: row.country, series: row.series, style: row.style,
                    template: row.msku_template, base: this.genRowBase(row),
                    separator: row.msku_separator, max_base_len: row.max_base_len,
                })),
            });
        },
        genMultiResultsStale() {
            return this.genMultiResults.length > 0 && this.genMultiPreviewSignature !== this.genMultiSignature;
        },
        genMultiIssueRows() {
            return this.genMultiRows.filter(row => this.genMultiRowIssue(row));
        },
        genMultiMissingShopList() {
            return this.genMultiRows.filter(row => !String(row.shop || '').trim());
        },
        genMultiValidDefaultNum() {
            return Number.isInteger(this.genMultiDefaultNum) && this.genMultiDefaultNum >= 1 && this.genMultiDefaultNum <= 200;
        },
        genMultiCanGenerate() {
            return this.genMultiRows.length > 0 && !this.genMultiMissingShopList.length
                && !this.genMultiIssueRows.length && !this.genMultiBusy;
        },
        genMultiGeneratedCount() {
            return this.genMultiResults.reduce((count, row) => count + (row.success ? (row.mskus || []).length : 0), 0);
        },
        genMultiFooterHint() {
            if (this.genMultiBusy) return '正在处理，请稍候…';
            if (!this.genMultiRows.length) return '导入 Excel 或粘贴 SKU 开始';
            if (this.genMultiMissingShopList.length) return '请在批量操作栏选择本批次唯一目标店铺';
            if (this.genMultiResultsStale) return '配置已变更，需要重新生成';
            if (this.genMultiIssueRows.length) return this.genMultiIssueRows.length + ' 条记录待修正，请查看表格提示';
            if (this.genMultiFailedList.length) return '请查看失败原因，修正后重新生成';
            if (this.genMultiSaveReady) return this.genMultiSavedCount ? '任务已保存，再次下载不会重复保存' : '预览尚未入库，下载时保存任务';
            return '配置已就绪，点击生成预览';
        },
        // 只有当前配置对应的整批预览成功后，才允许保存及下载。
        genMultiSaveReady() {
            return this.genMultiResults.length > 0 && this.genMultiFailedList.length === 0
                && !this.genMultiResultsStale && !this.genMultiIssueRows.length;
        },
        // 批量生成：基础名超长被截断的品名列表
        genMultiTruncList() {
            return this.genMultiResults.filter(r => r.success && r.truncated);
        },
        // 批量生成：配置未准备好时禁止提交，避免生成后只有半批记录
        genMultiConfigMissingList() {
            return this.genMultiRows.filter(r => {
                if (r.resolveError) return false;
                if (r.cfgError) return true;
                if (!String(r.parent || '').trim() || !String(r.pm || '').trim()
                    || !String(r.shop || '').trim()) return true;
                const suffix = String(r.suffix || this.effectiveGenMultiSuffix() || '').trim();
                if (!/^[A-Za-z0-9]{1,6}$/.test(suffix)) return true;
                const modeMissing = r.mode === 'template'
                    ? !r.msku_template
                    : (!r.series || !r.style || !String(r.msku_color || '').trim());
                return modeMissing || !r.country || !r.supplier;
            });
        },
        // SKU 详情反查失败的行禁止提交，但错误只在“关联 SKU”列内展示。
        genMultiResolveErrorList() {
            return this.genMultiRows.filter(r => r.resolveError);
        },
        // 仅 ERP 接口失败可重试；本地缓存命中不会触发 ERP 请求。
        genMultiSkuDetailFailedRows() {
            return this.genMultiRows.filter(row => this.isGenSkuDetailError(row.resolveError));
        },
        genMultiHasUploadRows() {
            return this.genMultiRows.some(row => this.genRowHasUploadSource(row));
        },
        // 批量生成：总计要生成的 MSKU 数量（数量角标）
        genMultiTotalMskus() {
            return this.genMultiRows.reduce((s, r) => s + (Number(r.num) || 0), 0);
        }
    },
    mounted() {
        this.loadInitData();
        // 主表直接展示产品类型摘要；提前加载名称映射，避免必须先打开配置弹窗。
        this.fetchProductTypes();
    },
    methods: {
        genMultiRowIssue(row) {
            if (row.resolveError) return row.resolveError;
            if (this.genMultiConfigMissingList.includes(row)) return this.genRowMissingText(row) || '请补齐生成配置';
            if (this.genRowShouldPair(row) && !String(row.sku || '').trim()) return '开启配对时需填写关联 SKU';
            if (!Number.isInteger(row.num) || row.num < 1 || row.num > 200) return '数量需为 1–200 的整数';
            return '';
        },
        genMultiRowClass({ row }) {
            return this.genMultiRowIssue(row) ? 'gen-multi-issue-row' : '';
        },
        applyGenMultiQuantity() {
            if (!this.genMultiValidDefaultNum || this.genMultiBusy) return;
            this.genMultiRows.forEach(row => { row.num = this.genMultiDefaultNum; });
        },
        onGenMultiPrimaryAction() {
            if (this.genMultiBusy) return;
            if (this.genMultiView === 'results' && this.genMultiSaveReady) return this.exportGenMulti();
            if (this.genMultiSaveReady) {
                this.genMultiView = 'results';
                return;
            }
            if (this.genMultiMissingShopList.length) {
                return this.$message.warning('请先在批量操作栏选择本批次唯一目标店铺');
            }
            this.executeGenMulti();
        },
        removeGenMultiRow(row) {
            const index = this.genMultiRows.indexOf(row);
            if (index !== -1) this.genMultiRows.splice(index, 1);
            this.refreshGenMultiTableLayout();
        },
        onGenMultiRowCommand(command, row) {
            if (this.genMultiBusy) return;
            if (command === 'config') this.openGenPmRule(row);
            if (command === 'remove') this.removeGenMultiRow(row);
        },
        toggleGenMultiResult(index) {
            const position = this.genMultiExpandedResults.indexOf(index);
            if (position === -1) this.genMultiExpandedResults.push(index);
            else this.genMultiExpandedResults.splice(position, 1);
        },
        closeGenMulti(done) {
            if (this.genMultiBusy) return;
            if (typeof done === 'function') done();
            else this.genMultiVisible = false;
        },
        statusType(s) {
            const m = {
                '待操作': 'info', '正在移入': '', '已移入': 'success',
                '正在移出': 'warning', '已移出': 'info',
            };
            return m[s] || 'info';
        },
        productTypeLabel(productType) {
            const value = String(productType || '').trim();
            if (!value) return '';
            const found = (this.productTypes || []).find(item => item.value === value);
            return found ? (found.label + '（' + value + '）') : value;
        },
        tableRowClass() {
            return '';
        },
        // 页面初始化：加载全部父体列表
        loadInitData() {
            this.$axios.get('/vue/auto_update/seller_asin_select', { params: {} }).then(res => {
                if (res.msg === 'success') {
                    this.allParentList = res.data || [];
                }
            }).catch(() => { });
        },
        onShopChange() { /* 切换店铺 */ },
        // 查询：选中父体 + 店铺 → 拉品名 + 合并 MSKU
        handleSearch() {
            const shop = this.searchForm.shop;
            const pname = this.searchForm.parentName;
            if (!shop) return alert('请先选择店铺');
            if (!pname) return alert('请先选择本地父体');
            this.loadTableData(shop, pname);
        },
        resetSearch() {
            this.searchForm = { shop: '', parentName: '' };
            this.tableData = [];
        },
        loadTableData(shop, pname) {
            this.isSending = true;
            this.tableData = [];
            this.$axios.post('/vue/auto_update/select_child',
                { name: pname, shop: shop }
            ).then(res => {
                if (res.msg !== 'success' || !res.data) { this.isSending = false; return; }
                const allRows = res.data;
                // 并行拉取：在售 MSKU、产品操作配置摘要、MSKU 域颜色状态。
                // 三者独立：写 msku_color 不得影响产品操作配置状态。
                Promise.all([
                    this.$axios.post('/vue/auto_update/get_msku_rel',
                        { name: pname, shop: shop }),
                    this.$axios.get('/vue/vue_product/operation-config/template-list',
                        { params: { parent: pname, shop: shop } }),
                    this.$axios.get('/vue/msku-generation/color-status',
                        { params: { parent: pname, shop: shop } })
                        // MSKU 域状态只影响颜色展示；接口短暂不可用时绝不能把
                        // 产品创建/移入移出配置状态一起降级为“未配置”。
                        .catch(() => ({ code: 200, data: [] })),
                ]).then(([res2, resC, resMsku]) => {
                    const mskuMap = {};
                    if (res2.msg === 'success' && res2.data) {
                        res2.data.forEach(r => { mskuMap[r['本地父体'] + '|' + r['本地品名']] = r; });
                    }
                    const pmConfigMap = {};
                    const configRequestOk = resC.code === 200;
                    if (configRequestOk && resC.data) {
                        resC.data.forEach(r => {
                            const configKey = [r['本地父体'] || '', r['本地品名'] || '', r['店铺'] || ''].join('|');
                            pmConfigMap[configKey] = r;
                        });
                    }
                    const colorMap = {};
                    if (resMsku.code === 200 && resMsku.data) {
                        resMsku.data.forEach(r => {
                            colorMap[(r.parent || '') + '|' + (r.pm || '') + '|' + (r.shop || '')] = r.msku_color || '';
                        });
                    }
                    allRows.forEach(row => {
                        const rowShop = row['店铺'] || shop;
                        const rel = mskuMap[row['本地父体'] + '|' + row['本地品名']];
                        const config = pmConfigMap[
                            [row['本地父体'] || '', row['本地品名'] || '', rowShop || ''].join('|')
                        ] || {};
                        row._hasRel = !!rel;                    // 是否有关联记录：false=未同步(/)
                        row.msku = rel ? rel.msku || '' : '';
                        row['状态'] = rel ? rel['状态'] || '待操作' : '待操作';
                        row.msku_product_type = rel ? rel.product_type || '' : '';
                        // 产品配置优先展示“品名覆盖 > 父体继承”的有效产品类型；
                        // 当前售卖 MSKU 的类型仅作为移入旧流程的回退值保存。
                        row.config_product_type = config.product_type || '';
                        row.product_type = row.config_product_type || row.msku_product_type || '';
                        row.config_status = config.config_status || '未配置';
                        row['品名配置'] = row.config_status;
                        row.config_source = config.config_source || '';
                        row.product_type_source = config.product_type_source || '';
                        row.pm_product_type = config.pm_product_type || '';
                        row.parent_product_type = config.parent_product_type || '';
                        row.variation_theme = config.variation_theme || '';
                        row.parent_sku = config.parent_sku || '';
                        row.create_main_title = config.create_main_title || '';
                        row.create_sub_title = config.create_sub_title || '';
                        row.series = config.series || '';
                        row.style = config.style || '';
                        row.msku_template = config.msku_template || '';
                        row.msku_supplier = config.supplier || '';
                        row.msku_country = config.country || '';
                        row.configured_field_count = Number(config.configured_field_count || 0);
                        row.pm_field_count = Number(config.pm_field_count || 0);
                        row.parent_field_count = Number(config.parent_field_count || 0);
                        row.config_updated_at = config.updated_at || '';
                        row.config_fetch_error = !configRequestOk;
                        row.msku_color = colorMap[
                            [row['本地父体'] || '', row['本地品名'] || '', rowShop || ''].join('|')
                        ] || '';
                    });
                    this.tableData = allRows;
                    this.isSending = false;
                }).catch(() => {
                    // 配置状态拉取失败不阻断列表展示
                    allRows.forEach(row => {
                        row['品名配置'] = '未配置';
                    });
                    this.tableData = allRows;
                    this.isSending = false;
                });
            }).catch(() => { this.isSending = false; });
        },
        // === 移入 ===
        openMoveIn(row) {
            // 移入前置校验：当前记录已有售卖MSKU → 需先移出
            if (row.msku) {
                this.$message.warning('该品名已有售卖MSKU（' + row.msku + '），需先执行移出操作');
                return;
            }
            this.moveInRow = { ...row, new_msku: '' }; this.moveInStep = 'form';
            this.moveInPreviewData = {}; this.moveInExtraValues = {};
            this.moveInResult = {}; this.pollResult = null; this.moveInThemes = [];
            this.moveInCandidates = []; this.moveInSelectedAsin = '';
            if (!this.moveInRow['店铺']) this.moveInRow['店铺'] = this.searchForm.shop || '';
            if (!this.productTypes.length) this.fetchProductTypes();
            // 新MSKU按配置自动生成（-C 后缀），可修改
            this.loadMoveInPrepare(row);
            // 源 ASIN 候选来自 listing；变体主题/变体值来自统一解析（父体主题 + 品名变体值）。
            this.loadMoveInCandidates();
            this.loadMoveInVariationFields();
            // Parent SKU 从品名配置自动带入（可修改）
            this.$axios.get('/vue/vue_product/operation-config', {
                params: { parent: row['本地父体'], pm: row['本地品名'], shop: row['店铺'] || this.searchForm.shop }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    if (res.data.parent_sku) this.moveInRow.parent_sku = res.data.parent_sku;
                    this.moveInRow.product_type = res.data.product_type || row.product_type || '';
                } else {
                    this.moveInRow.product_type = row.product_type || '';
                }
            }).catch(() => {
                this.moveInRow.product_type = row.product_type || '';
            });
            this.moveInVisible = true;
        },
        // 指定 ASIN 移入候选：本地父体/品名 → 关联SKU → 当前店铺 Listing
        loadMoveInCandidates() {
            const row = this.moveInRow || {};
            const parent = row['本地父体'] || '';
            const pm = row['本地品名'] || '';
            const shop = row['店铺'] || this.searchForm.shop || '';
            this.moveInCandidates = [];
            this.moveInSelectedAsin = '';
            if (!pm || !shop) return;
            this.moveInCandidateLoading = true;
            this.$axios.get('/vue/vue_product/asin-candidates', {
                params: { parent, pm, shop },
            }).then(res => {
                if (res.code !== 200) return;
                const data = res.data || {};
                this.moveInCandidates = data.candidates || [];
                if (!this.moveInCandidates.length) {
                    this.$message.warning(data.message || '当前父体/品名在该店铺下没有可移入的 Listing 记录');
                }
            }).catch(() => {
                this.$message.error('源 ASIN 候选加载失败');
            }).finally(() => { this.moveInCandidateLoading = false; });
        },
        formatAsinCandidateLabel(item) {
            const row = item || {};
            // 下拉与选中框只展示 ASIN 与源 MSKU，不带品名/SKU
            return `${row.asin || ''} | ${row.source_msku || ''}`;
        },
        onMoveInAsinChange(key) {
            const parts = String(key || '').split('|');
            const asin = parts[0] || '';
            const sourceMsku = parts.slice(1).join('|') || '';
            const found = (this.moveInCandidates || []).find(
                c => c.asin === asin && c.source_msku === sourceMsku) || {};
            this.$set(this.moveInRow, 'msku', sourceMsku);
            this.$set(this.moveInRow, 'asin', asin);
            this.$set(this.moveInRow, 'sku', found.sku || '');
            this.moveInPreviewData = {};
            this.moveInExtraValues = {};
        },
        // 变体字段统一解析（父体主题 + 品名变体值），与后端 resolve_variation_fields 同源。
        loadMoveInVariationFields() {
            const row = this.moveInRow || {};
            const parent = row['本地父体'] || '';
            const pm = row['本地品名'] || '';
            const shop = row['店铺'] || this.searchForm.shop || '';
            if (!parent || !pm || !shop) return;
            this.$axios.get('/vue/vue_product/variation-fields', {
                params: { parent, pm, shop, scene: 'move_in' },
            }).then(res => {
                if (res.code !== 200 || !res.data) return;
                const data = res.data;
                this.$set(this.moveInRow, 'variation_theme', data.theme || '');
                if (data.product_type) this.$set(this.moveInRow, 'product_type', data.product_type);
                // 已配置的变体值直接作为补充值默认项，缺失项留空待用户填写。
                const values = data.values || {};
                Object.keys(values).forEach(key => {
                    if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
                        this.$set(this.moveInExtraValues, key, values[key]);
                    }
                });
            }).catch(() => { });
        },
        // 生成移入新MSKU（后缀 -C）：按父体/品名配置的 MSKU 生成模式
        loadMoveInPrepare(row) {
            this.$axios.post('/vue/vue_product/create/prepare', {
                parent: row['本地父体'], pm: row['本地品名'], shop: row['店铺'] || this.searchForm.shop, suffix: 'C',
            }).then(res => {
                if (res.code === 200 && res.data && res.data.success) {
                    this.moveInRow.new_msku = res.data.msku || '';
                    if (res.data.msku_error) {
                        this.$message.warning('生成移入MSKU失败：' + res.data.msku_error);
                    }
                } else {
                    this.$message.error((res.data && res.data.error) || res.message || '生成移入MSKU失败');
                }
            }).catch(() => { });
        },
        // 预览后返回表单步骤（保留已填内容，仅清空旧预览结果）
        backToMoveInForm() {
            this.moveInPreviewData = {};
            this.moveInStep = 'form';
        },
        moveInPreview() {
            if (!this.moveInRow.msku) return alert('请选择源 ASIN');
            if (!this.moveInRow.parent_sku) return alert('请输入要移入的父体 MSKU');
            if (!this.moveInRow.variation_theme) return alert('父体未配置变体主题，请先在父体配置中选择');
            this.moveInLoading = true;
            this.$axios.post('/vue/auto_update/move_in_preview', {
                shop: this.moveInRow['店铺'], msku: this.moveInRow.msku,
                parent_sku: this.moveInRow.parent_sku, variation_theme: this.moveInRow.variation_theme,
                parent: this.moveInRow['本地父体'], pm: this.moveInRow['本地品名'],
                sku: this.moveInRow.sku || '', asin: this.moveInRow.asin || '',
            }).then(res => {
                this.moveInLoading = false;
                if (res.msg === 'success') {
                    this.moveInPreviewData = res.data;
                    if (res.data.missing_fields) {
                        res.data.missing_fields.forEach(mf => {
                            if (!(mf.field_name in this.moveInExtraValues))
                                this.$set(this.moveInExtraValues, mf.field_name, '');
                        });
                    }
                    this.moveInStep = 'preview';
                } else alert('预览失败: ' + (res.message || ''));
            }).catch(e => { this.moveInLoading = false; alert('请求失败: ' + e); });
        },
        moveInExecute() {
            if (!this.moveInRow.new_msku) return alert('请输入新MSKU');
            this.moveInLoading = true;
            this.$axios.post('/vue/auto_update/move_in_execute', {
                shop: this.moveInRow['店铺'], msku: this.moveInRow.msku,
                new_msku: this.moveInRow.new_msku.trim(),
                parent_sku: this.moveInRow.parent_sku, variation_theme: this.moveInRow.variation_theme,
                variation_field_values: this.moveInExtraValues,
                sku: this.moveInRow.sku || '', asin: this.moveInRow.asin || '',
                '本地父体': this.moveInRow['本地父体'], '本地品名': this.moveInRow['本地品名'],
            }).then(res => {
                this.moveInLoading = false;
                if (res.msg === 'success') {
                    this.moveInResult = res.data; this.moveInTaskId = res.data.task_id;
                    this.moveInStep = 'executing';
                    // 主表刷新；任务状态由后端处理，任务面板打开时再读取。
                    this.refreshTableData();
                    // 保留本地未完成数量提示，不在前端轮询任务状态。
                    if (res.data.task_id) {
                        this.taskList.unshift({
                            task_id: res.data.task_id,
                            new_msku: this.moveInRow.new_msku.trim(),
                            action: 'move_in',
                            status: 'publishing',
                            status_msg: '提交成功',
                            step: '已提交移入刊登，等待Amazon处理',
                        });
                        this._updateActiveCount();
                    }
                } else alert('移入失败: ' + (res.message || ''));
            }).catch(e => { this.moveInLoading = false; alert('请求失败: ' + e); });
        },
        // === 任务面板 ===
        openTaskPanel() {
            this.openAutoWorkbench('tasks');
        },
        openAutoMskuTasks() {
            this.openAutoWorkbench('msku');
        },
        openLogDialog() {
            this.openAutoWorkbench('logs');
        },
        openAutoWorkbench(tab, msku) {
            this.autoWorkbenchTab = ['msku', 'tasks', 'logs'].indexOf(tab) >= 0 ? tab : 'msku';
            this.autoWorkbenchMsku = String(msku || '').trim();
            this.dialogAutoWorkbench = true;
        },
        openBatchAsinTask(row) {
            if (!row || !row.master_task_id) return;
            this.openAutoWorkbench('msku', row.msku);
        },
        onWorkbenchTasksLoaded(rows) {
            const all = [];
            (rows || []).forEach(row => {
                all.push(row);
                if (row.children && row.children.length) all.push(...row.children);
            });
            this.taskList = all.filter(t => t.action === 'move_in' || t.action === 'move_out');
            this._updateActiveCount();
        },
        _updateActiveCount() {
            const active = ['pending', 'publishing', 'phase1_done', 'phase2_done', 'procuring'];
            this.activeTaskCount = this.taskList.filter(t => active.includes(t.status)).length;
        },
        // === 移出 ===
        openMoveOut(row) {
            this.moveOutRow = { ...row };
            this.moveOutStep = 'confirm';
            this.moveOutPreviewInfo = {}; this.moveOutResult = {};
            this.moveOutSnapshotKey = '';
            if (!this.moveOutRow['店铺']) this.moveOutRow['店铺'] = this.searchForm.shop || '';
            this.moveOutVisible = true;
            if (this.moveOutRow.msku) this.moveOutPreview();
        },
        // 预览移出：由后端按在线原标题生成新标题与后缀，确认时原样回传该快照。
        moveOutPreview() {
            const msku = (this.moveOutRow.msku || '').trim();
            const shop = this.moveOutRow['店铺'] || '';
            if (!msku) return this.$message.warning('请输入要移出的 MSKU');
            if (!shop) return this.$message.warning('请选择店铺');
            this.moveOutPreviewLoading = true;
            this.$axios.post('/vue/auto_update/move_out_preview', {
                shop: shop, msku: msku,
            }).then(res => {
                if (res.msg === 'success' && res.data && res.data.success) {
                    this.moveOutPreviewInfo = res.data;
                    // 记录快照对应的范围；店铺或 MSKU 变化后必须重新预览。
                    this.moveOutSnapshotKey = shop + '|' + msku;
                } else {
                    this.moveOutPreviewInfo = {};
                    this.moveOutSnapshotKey = '';
                    this.$message.error((res.data && res.data.error) || res.message || '移出预览失败');
                }
            }).catch(() => {
                this.moveOutPreviewInfo = {};
                this.moveOutSnapshotKey = '';
                this.$message.error('移出预览请求失败');
            }).finally(() => { this.moveOutPreviewLoading = false; });
        },
        // 手动输入/修改移出 MSKU、切换店铺后旧快照失效，需重新预览。
        onMoveOutMskuChange() {
            const msku = (this.moveOutRow.msku || '').trim();
            const shop = this.moveOutRow['店铺'] || '';
            if (this.moveOutSnapshotKey && this.moveOutSnapshotKey !== shop + '|' + msku) {
                this.moveOutPreviewInfo = {};
                this.moveOutSnapshotKey = '';
            }
            if (!msku || !shop) return;
            this.moveOutPreview();
        },
        moveOutExecute() {
            if (!this.moveOutRow.msku) return alert('请输入要移出的 MSKU');
            if (!this.moveOutSnapshotReady) return alert('请先预览移出结果');
            this.moveOutLoading = true;
            this.$axios.post('/vue/auto_update/move_out_execute', {
                shop: this.moveOutRow['店铺'], msku: this.moveOutRow.msku,
                snapshot: this.moveOutPreviewInfo,
                '本地父体': this.moveOutRow['本地父体'], '本地品名': this.moveOutRow['本地品名'],
            }).then(res => {
                this.moveOutLoading = false;
                if (res.msg === 'success') {
                    this.moveOutResult = res.data; this.moveOutStep = 'executing';
                    // 主表刷新；任务状态由后端处理，任务面板打开时再读取。
                    this.refreshTableData();
                    // 保留本地未完成数量提示，不在前端轮询任务状态。
                    if (res.data.task_id) {
                        this.taskList.unshift({
                            task_id: res.data.task_id,
                            new_msku: this.moveOutRow.msku,
                            action: 'move_out',
                            status: 'publishing',
                            status_msg: '提交成功',
                            step: '已提交移出刊登，等待Amazon处理',
                        });
                        this._updateActiveCount();
                    }
                }
                else alert('移出失败: ' + (res.message || ''));
            }).catch(e => { this.moveOutLoading = false; alert('请求失败: ' + e); });
        },
        refreshTableData() {
            const shop = this.searchForm.shop;
            const pname = this.searchForm.parentName;
            if (shop && pname) {
                this.loadTableData(shop, pname);
            }
        },
        // === 操作配置（父体配置 / 品名配置）===
        fetchProductTypes() {
            this.$axios.get('/vue/vue_product/product-types').then(res => {
                if (res.code === 200 && res.data) {
                    this.productTypes = res.data;
                }
            }).catch(() => { });
        },
        openParentConfig() {
            this.dialogParentConfigVisible = true;
        },
        openGenPmRule(row) {
            this.mskuPmRuleMeta = { parent: row.parent || '', pm: row.pm || '', shop: row.shop || '' };
            this.dialogMskuPmRuleVisible = true;
        },
        openGenParentRule(row) {
            this.searchForm.parentName = row.parent || this.searchForm.parentName;
            this.dialogMskuRuleVisible = true;
        },
        onMskuParentRuleSaved() {
            this.loadGenMultiConfigs();
            this.refreshTableData();
        },
        onMskuPmRuleSaved() {
            this.loadGenMultiConfigs();
            this.refreshTableData();
        },
        openConfig(row) {
            this.pmConfigMeta = {
                '本地父体': row['本地父体'] || '',
                '本地品名': row['本地品名'] || '',
                '店铺': row['店铺'] || '',
                msku: row.msku || '',
            };
            this.dialogPmConfigVisible = true;
        },
        handlePmConfigSaved() {
            this.refreshTableData();
            if (this.genMultiVisible) {
                if (this.genMultiMode === 'asin') this.prepareBatchAsin(false);
                else this.loadGenMultiConfigs();
            }
        },
        // 表格「编辑」下拉命令分发
        onRowCommand(cmd, row) {
            if (cmd === 'config') return this.openConfig(row);
            // 后台刊登尚未完成时禁止重复提交任何产品操作。
            if (['正在移入', '正在移出'].includes(row['状态'])) {
                this.$message.warning('该品名当前正在处理，请等待任务完成后再操作');
                return;
            }
            // 创建配对只能作用于尚未绑定当前售卖 MSKU 的品名；已有 MSKU 时避免重复创建。
            if (cmd === 'create_pair') {
                if (row.msku) {
                    this.$message.warning('该品名已有售卖MSKU（' + row.msku + '），不能重复创建配对');
                    return;
                }
                return this.openCreatePair(row);
            }
            // 新品可直接进入移入表单，由用户填写源 MSKU，无需已有售卖关联。
            if (cmd === 'move_in') return this.openMoveIn(row);
            // 移出和重置需要已有的关联记录。
            if (!row._hasRel) {
                this.$message.warning('该品名未同步，需先关联后才能执行该操作');
                return;
            }
            if (cmd === 'move_out') this.openMoveOut(row);
            else if (cmd === 'reset') this.openReset(row);
        },
        // === 重置 ===
        openReset(row) {
            this.$confirm('确定要重置该品名吗？将删除当前售卖MSKU（' + (row.msku || '-') + '），使其变为未绑定状态。',
                '重置确认', { type: 'warning', confirmButtonText: '确认重置', cancelButtonText: '取消' })
                .then(() => {
                    this.$axios.post('/vue/auto_update/reset_msku_rel', {
                        '本地父体': row['本地父体'],
                        '本地品名': row['本地品名'],
                        '店铺': row['店铺'],
                    }).then(res => {
                        if (res.msg === 'success') {
                            this.$message.success('已重置为未绑定');
                            this.refreshTableData();
                        } else {
                            this.$message.error(res.message || '重置失败');
                        }
                    }).catch(() => this.$message.error('请求失败'));
                }).catch(() => { });
        },
        // === 批量更新 ===
        openBatchDialog() {
            this.batchDialogVisible = true;
            this.batchStep = 'form';
            if (!this.productTypes.length) this.fetchProductTypes();
        },
        resetBatchDialog() {
            this.batchStep = 'form';
            this.batchKind = 'pm';
            this.batchProductType = '';
            this.batchRows = [];
            this.batchErrors = [];
            this.batchResults = [];
            this.batchSummary = null;
        },
        batchProductTypeChange() {
            this.batchRows = [];
            this.batchErrors = [];
        },
        // === 生成待填模板（品名走外层勾选，父体走弹窗多选） ===
        openFillTplDialog(kind) {
            if (!this.productTypes.length) this.fetchProductTypes();
            this.fillTplProductType = '';
            if (kind === 'parent') {
                this.resetFillParentDialog();
                this.fillParentTplVisible = true;
                this.$nextTick(() => this.clearFillParentSelection());
            } else {
                if (!this.tableSelection.length) return this.$message.warning('请先在品名列表勾选数据');
                this.fillTplVisible = true;
            }
        },
        onFillParentSelectionChange(rows) {
            this.fillParents = rows.map(row => row.value);
        },
        clearFillParentSelection() {
            this.fillParents = [];
            const table = this.$refs.fillParentTable;
            if (table && typeof table.clearSelection === 'function') table.clearSelection();
        },
        resetFillParentDialog() {
            this.clearFillParentSelection();
            this.fillParentFilter = '';
            this.fillDownloading = false;
        },
        downloadFilledTemplate(kind) {
            if (!this.fillTplProductType) return this.$message.warning('请先选择产品类型');
            let body;
            if (kind === 'parent') {
                if (!this.fillParents.length) return this.$message.warning('请勾选至少一个父体');
                body = { product_type: this.fillTplProductType, kind: 'parent', parents: this.fillParents };
            } else {
                if (!this.tableSelection.length) return this.$message.warning('请先在品名列表勾选数据');
                body = {
                    product_type: this.fillTplProductType, kind: 'pm',
                    items: this.tableSelection.map(r => ({
                        parent: r['本地父体'], pm: r['本地品名'], shop: r['店铺']
                    })),
                };
            }
            this.fillDownloading = true;
            this.$axios.post('/vue/vue_product/batch/template-fill', body, { responseType: 'blob' }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.fillTplProductType + (kind === 'parent' ? '_父体待填' : '_待填') + '.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                if (kind === 'parent') {
                    this.clearFillParentSelection();
                    this.fillParentTplVisible = false;
                    this.$message.success('父体待填模板已生成');
                }
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.fillDownloading = false;
                }, 300);
            }).catch(error => {
                this.fillDownloading = false;
                this.$message.error(error.message || '生成待填模板失败');
            });
        },
        downloadBatchTemplate() {
            if (!this.batchProductType) return this.$message.warning('请先选择产品类型');
            this.batchDownloading = true;
            const url = this.batchKind === 'parent'
                ? '/vue/vue_product/batch/parent-template'
                : '/vue/vue_product/batch/template';
            this.$axios.get(url, {
                params: { product_type: this.batchProductType },
                responseType: 'blob',
            }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.batchProductType + '.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.batchDownloading = false;
                }, 300);
            }).catch(error => {
                this.batchDownloading = false;
                this.$message.error(error.message || '模板下载失败');
            });
        },
        batchUploadRequest(option) {
            const fd = new FormData();
            fd.append('product_type', this.batchProductType);
            fd.append('kind', this.batchKind);
            fd.append('file', option.file);
            this.$axios.post('/vue/vue_product/batch/parse', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    this.batchRows = res.data.rows || [];
                    this.batchErrors = res.data.errors || [];
                    this.batchStep = 'preview';
                    if (this.batchRows.length) this.$message.success(`解析到 ${this.batchRows.length} 行`);
                } else {
                    this.$message.error((res && res.message) || '解析失败');
                }
            }).catch(e => this.$message.error('解析失败: ' + (e && e.message ? e.message : e)));
        },
        executeBatch() {
            this.batchExecuting = true;
            this.$axios.post('/vue/vue_product/batch/execute', {
                product_type: this.batchProductType,
                kind: this.batchKind,
                rows: this.batchRows,
            }).then(res => {
                this.batchExecuting = false;
                if (res.code === 200 && res.data) {
                    this.batchResults = res.data.results || [];
                    this.batchSummary = res.data.summary || null;
                    this.batchStep = 'result';
                    this.refreshTableData();
                } else {
                    this.$message.error((res && res.message) || '执行失败');
                }
            }).catch(e => {
                this.batchExecuting = false;
                this.$message.error('执行失败: ' + e);
            });
        },
        formatBatchValues(values) {
            if (!values) return '';
            return Object.keys(values).map(k => {
                let v = values[k];
                if (v && typeof v === 'object') v = JSON.stringify(v);
                return k + '=' + v;
            }).join('；');
        },
        // === 当前售卖MSKU 单元格点击编辑 ===
        startMskuEdit(row) {
            this.$set(row, 'msku_edit', row.msku || '');
            this.$set(row, '_mskuEditing', true);
        },
        cancelMskuEdit(row) {
            this.$set(row, '_mskuEditing', false);
        },
        saveMskuEdit(row) {
            const val = (row.msku_edit || '').trim();
            if (!val) return this.$message.warning('MSKU 不能为空');
            if (!row['本地父体'] || !row['本地品名'] || !row['店铺']) {
                return this.$message.warning('该行缺少 本地父体/本地品名/店铺，无法保存');
            }
            this.$set(row, '_mskuSaving', true);
            this.$axios.post('/vue/auto_update/update_msku', {
                '本地父体': row['本地父体'],
                '本地品名': row['本地品名'],
                '店铺': row['店铺'],
                msku: val,
            }).then(res => {
                this.$set(row, '_mskuSaving', false);
                if (res.msg === 'success') {
                    row.msku = val;
                    row._hasRel = true;          // 未同步 → 关联
                    row['状态'] = row['状态'] || '待操作';
                    this.$set(row, '_mskuEditing', false);   // 保存成功后退出编辑态
                    this.$message.success('MSKU 已更新');
                } else {
                    this.$message.error(res.message || '更新失败');
                }
            }).catch(() => {
                this.$set(row, '_mskuSaving', false);
                this.$message.error('请求失败');
            });
        },
        // === 批量更新MSKU（模板上传）===
        openMskuBatch() {
            this.mskuBatchVisible = true;
            this.mskuBatchStep = 'form';
        },
        // === 批量生成MSKU（表格勾选多品名）===
        tableRowKey(row) {
            return (row['本地父体'] || '') + '|' + (row['本地品名'] || '') + '|' + (row['店铺'] || '');
        },
        onTableSelectionChange(rows) {
            this.tableSelection = rows || [];
        },
        clearTableSelection() {
            this.tableSelection = [];
            const table = this.$refs.mainTable;
            if (table && typeof table.clearSelection === 'function') table.clearSelection();
        },
        newClientRequestId(prefix) {
            return String(prefix || 'request') + '_' + Date.now() + '_' + Math.random().toString(16).slice(2);
        },
        openBatchAsin() {
            const suffix = this.effectiveBatchAsinSuffix() || 'T';
            const firstSelected = this.tableSelection[0] || {};
            const targetShop = String(this.searchForm.shop || '').trim();
            if (!this.productTypes.length) this.fetchProductTypes();
            this.genMultiMode = 'asin';
            this.genMultiView = 'config';
            this.genMultiRows = [];
            this.genMultiResults = [];
            this.genMultiPreviewSignature = '';
            this.genMultiLoading = false;
            this.genMultiExporting = false;
            this.genMultiConfigSyncing = false;
            this.genMultiConfigSaved = false;
            this.genMultiColorImporting = false;
            this.genMultiResolveLoading = false;
            this.batchAsinLoading = false;
            this.batchAsinCfgLoading = false;
            this.batchAsinImporting = false;
            this.batchAsinMskuImporting = false;
            this.batchAsinImportShop = targetShop;
            this.batchAsinImportParent = this.searchForm.parentName || firstSelected['本地父体'] || '';
            this.batchAsinExpandedItems = [];
            this.batchAsinRows = this.tableSelection.map(row => ({
                parent: row['本地父体'] || '', pm: row['本地品名'] || '', shop: targetShop,
                current_msku: row.msku || '',
                sku: '', skus: [], num: this.batchAsinDefaultNum || 1,
                mode: 'auto', msku_template: '', series: '', style: '', country: '', msku_separator: '-', suffix,
                should_pair: suffix === 'T',
                msku_color: '', supplier: '', base_override: '', base: '', configured_base: '', country_abbr: '',
                color: '', max_base_len: 0, truncated: false,
                product_type: row.config_product_type || '',
                configured_product_type: row.config_product_type || '',
                product_type_choice: row.config_product_type || '',
                product_type_override: '', product_type_source: row.product_type_source || '',
                fields: [], field_values: {}, titles: {}, config_error: '',
                 local_name: '', sku_details: {}, sku_detail_error: '', _supplierManual: false,
                 _colorManual: false,
            })).filter(row => row.pm);
            this.batchAsinPlan = { items: [], mskus: [], reused: [], conflict: [] };
            this.batchAsinResult = {};
            this.batchAsinResultRows = [];
            this.batchAsinRequestId = this.newClientRequestId('batch_asin');
            this.batchAsinImportResult = '';
            this.genMultiVisible = true;
            this.clearTableSelection();
            if (this.batchAsinRows.length) this.prepareBatchAsin(false);
        },
        onBatchAsinImportShopChange(shop) {
            const targetShop = String(shop || '').trim();
            this.batchAsinImportShop = targetShop;
            this.batchAsinRows.forEach(row => this.$set(row, 'shop', targetShop));
            this.invalidateBatchAsinPlan();
            if (targetShop && this.batchAsinRows.length) this.prepareBatchAsin(false);
        },
        resetBatchAsin() {
            this.batchAsinPrepareToken += 1;
            this.batchAsinLengthBudgetToken += 1;
            this.batchAsinOriginalConfigToken += 1;
            this.batchAsinLengthBudgetLoading = false;
            this.genMultiView = 'config';
            this.batchAsinLoading = false;
            this.batchAsinCfgLoading = false;
            this.batchAsinImporting = false;
            this.batchAsinMskuImporting = false;
            this.batchAsinImportResult = '';
            this.batchAsinImportShop = '';
            this.batchAsinRows = [];
            this.batchAsinPlan = { items: [], mskus: [], reused: [], conflict: [] };
            this.batchAsinExpandedItems = [];
            this.batchAsinResult = {};
            this.batchAsinResultRows = [];
            this.batchAsinRequestId = '';
            this.batchAsinDraft = null;
            this.batchAsinConfigResults = [];
            this.batchAsinDraftVisible = false;
            this.batchAsinDraftConfirming = false;
            this.batchAsinDraftConfirmId = '';
            this.batchAsinImportParent = '';
        },
        invalidateBatchAsinPlan() {
            if (!(this.batchAsinPlan.items || []).length && !this.batchAsinResultRows.length
                && !this.batchAsinImportResult) {
                this.batchAsinExpandedItems = [];
                return;
            }
            this.batchAsinPlan = { items: [], mskus: [], reused: [], conflict: [] };
            this.batchAsinExpandedItems = [];
            this.batchAsinResult = {};
            this.batchAsinResultRows = [];
            this.batchAsinImportResult = '';
            this.batchAsinConfigSaved = false;
            if (this.genMultiView !== 'config') this.genMultiView = 'config';
        },
        effectiveBatchAsinSuffix() {
            return this.batchAsinSuffix === 'CUSTOM'
                ? (this.batchAsinCustomSuffix || '').trim().toUpperCase() : this.batchAsinSuffix;
        },
        onBatchAsinSuffixChange() {
            this.invalidateBatchAsinPlan();
            const suffix = this.effectiveBatchAsinSuffix() || 'T';
            this.batchAsinRows.forEach(row => {
                row.suffix = suffix;
                row.should_pair = suffix === 'T';
            });
            this.refreshBatchAsinLengthBudgets();
        },
        onBatchAsinRowSuffixChange(row) {
            this.invalidateBatchAsinPlan();
            row.suffix = String(row.suffix || 'T').trim().toUpperCase();
            row.should_pair = row.suffix === 'T';
            this.refreshBatchAsinLengthBudgets();
        },
        onBatchAsinRowPairChange(row) {
            this.invalidateBatchAsinPlan();
            if (row.should_pair && !row.sku && row.skus && row.skus.length) row.sku = row.skus[0];
            if (row.should_pair) this.onBatchAsinSkuChange(row);
        },
        batchAsinSkuOptionLabel(row, sku) {
            const detail = row && row.sku_details
                ? row.sku_details[String(sku || '').trim()] : null;
            const erpLocalName = detail && (detail.erp_local_name || detail.local_name);
            const value = String(sku || '').trim();
            return erpLocalName
                ? value + ' | ' + String(erpLocalName).trim()
                : value;
        },
        batchAsinColorText(value) {
            if (value && typeof value === 'object') {
                return String(value['配置颜色'] || value['品名颜色'] || value['英文翻译'] || '').trim();
            }
            return String(value || '').trim();
        },
        onBatchAsinColorChange(row) {
            if (!row) return;
            this.invalidateBatchAsinPlan();
            this.$set(row, '_colorManual', true);
            this.$set(row, 'msku_color', this.batchAsinColorText(row.msku_color));
            this.$set(row, 'color', row.msku_color);
            this.batchAsinRefreshColorConflict(row, true);
            // 属性基础名依赖颜色；颜色变化后清理旧的手工基础名，保持预览与实际生成一致。
            if (row.mode !== 'template') this.resetGenRowBase(row);
            this.refreshGenRowAttributeBase(row);
        },
        // 品名配置色 vs 本次（模板/手工）色不一致 → 行内出现「颜色来源」选择。
        // syncRowColor=true 表示用户刚改了颜色输入，用它刷新“本次上传”候选值。
        batchAsinRefreshColorConflict(row, syncRowColor) {
            if (!row) return;
            if (syncRowColor) this.$set(row, 'row_msku_color', String(row.msku_color || '').trim());
            const configColor = String(row.config_msku_color || '').trim();
            const rowColor = String(row.row_msku_color || '').trim();
            const conflict = !!configColor && !!rowColor && configColor !== rowColor;
            this.$set(row, 'color_conflict', conflict);
            if (conflict && !row.color_source) this.$set(row, 'color_source', 'row');
            if (!conflict) this.$set(row, 'color_source', '');
        },
        // 选定颜色来源后，把生效颜色写回行；基础名与标题/副标题统一使用它。
        onBatchAsinColorSourceChange(row) {
            if (!row) return;
            this.$set(row, '_colorManual', true);
            const value = row.color_source === 'config'
                ? String(row.config_msku_color || '').trim()
                : String(row.row_msku_color || '').trim();
            this.$set(row, 'msku_color', value);
            this.$set(row, 'color', value);
            this.invalidateBatchAsinPlan();
            if (row.mode !== 'template') this.resetGenRowBase(row);
            this.refreshGenRowAttributeBase(row);
        },
        onBatchAsinSkuChange(row) {
            if (!row) return;
            this.invalidateBatchAsinPlan();
            const sku = String(row.sku || '').trim();
            const detail = row.sku_details && row.sku_details[sku];
            if (!detail) {
                row.sku_detail_error = '';
                return;
            }
            row.local_name = detail.erp_local_name || detail.local_name || '';
            row.sku_detail_error = detail.error || '';
            row.supplier_abbr_missing = !!detail.supplier_abbr_missing;
            if (detail.supplier && !row._supplierManual) {
                row.supplier = detail.supplier;
                this.refreshBatchAsinLengthBudgets();
            }
        },
        onBatchAsinSupplierChange(row) {
            if (row) {
                this.invalidateBatchAsinPlan();
                row._supplierManual = true;
                this.refreshBatchAsinLengthBudgets();
            }
        },
        onBatchAsinModeChange(row) {
            if (!row) return;
            this.invalidateBatchAsinPlan();
            this.$set(row, '_modeManual', true);
            // 切换模式后不沿用另一模式的手工基础名。
            this.resetGenRowBase(row);
        },
        // 批量新建 ASIN 的基础名预览复用批量生成 MSKU 的口径，支持行级手工覆盖。
        batchAsinRowBase(row) {
            return this.genRowBase(row);
        },
        batchAsinRowOverlong(row) {
            return this.genRowOverlong(row);
        },
        startBatchAsinBaseEdit(row) {
            this.startGenRowEdit(row);
        },
        saveBatchAsinBaseEdit(row) {
            this.saveGenRowBaseEdit(row);
            this.invalidateBatchAsinPlan();
        },
        resetBatchAsinBase(row) {
            this.resetGenRowBase(row);
            this.invalidateBatchAsinPlan();
        },
        refreshBatchAsinLengthBudgets() {
            const rows = this.batchAsinRows || [];
            if (!rows.length) {
                this.batchAsinLengthBudgetLoading = false;
                return;
            }
            const requestToken = ++this.batchAsinLengthBudgetToken;
            this.batchAsinLengthBudgetLoading = true;
            this.$axios.post('/vue/msku-generation/length-budget/batch', {
                suffix: this.effectiveBatchAsinSuffix() || 'T',
                items: rows.map(row => ({
                    supplier: row.supplier || '', country: row.country || '',
                    suffix: row.suffix || this.effectiveBatchAsinSuffix() || 'T',
                })),
            }).then(res => {
                if (requestToken !== this.batchAsinLengthBudgetToken) return;
                const budgets = res && res.data && res.data.results;
                if (!Array.isArray(budgets)) return;
                budgets.forEach(item => {
                    const row = rows[item.index];
                    if (!row) return;
                    if (item.error) this.$set(row, 'lengthBudgetError', item.error);
                    else {
                        this.$set(row, 'lengthBudgetError', '');
                        this.$set(row, 'max_base_len', item.max_base_len || 0);
                        this.$set(row, 'length_budget', item);
                    }
                });
            }).catch(() => { /* 配置接口返回值仍可作为预览的保守依据 */ }).finally(() => {
                // 只结束最后一次请求的等待状态，避免旧请求覆盖新输入的预算。
                if (requestToken === this.batchAsinLengthBudgetToken) this.batchAsinLengthBudgetLoading = false;
            });
        },
        batchAsinMissingFields(row) {
            if (!row) return [];
            const missing = [];
            if (!String(row.parent || '').trim()) missing.push('本地父体');
            if (!String(row.pm || '').trim()) missing.push('本地品名');
            if (!String(row.shop || '').trim()) missing.push('店铺');
            const suffix = String(row.suffix || this.effectiveBatchAsinSuffix() || '').trim();
            if (!/^[A-Za-z0-9]{1,6}$/.test(suffix)) missing.push('后缀');
            if (row.mode === 'template') {
                if (!String(row.msku_template || '').trim()) missing.push('MSKU模板');
            } else {
                if (!String(row.series || '').trim()) missing.push('系列');
                if (!String(row.style || '').trim()) missing.push('款式');
                if (!String(row.msku_color || '').trim()) missing.push('MSKU颜色');
            }
            if (!String(row.country || '').trim()) missing.push('国家代码');
            if (!String(row.supplier || '').trim()) missing.push('供应商');
            if (!String(row.product_type || '').trim()
                && suffix.toUpperCase() !== 'C') missing.push('产品类型');
            if (suffix.toUpperCase() === 'T' && row.should_pair
                && !String(row.sku || '').trim()) missing.push('关联 SKU');
            return [...new Set(missing)];
        },
        batchAsinRowClass({ row }) {
            return this.batchAsinRowIssue(row) ? 'gen-multi-issue-row' : '';
        },
        batchAsinShowMskuConfigAction(row) {
            const missing = this.batchAsinMissingFields(row);
            return missing.some(field => ['系列', '款式', 'MSKU颜色', 'MSKU模板', '国家代码', '供应商'].includes(field));
        },
        batchAsinNeedsProductType(row) {
            const suffix = String((row && row.suffix) || this.effectiveBatchAsinSuffix() || '').trim().toUpperCase();
            return !!(row && !String(row.product_type || '').trim() && suffix !== 'C');
        },
        onBatchAsinProductTypeChange(row) {
            if (!row) return;
            this.invalidateBatchAsinPlan();
            this.setBatchAsinProductType(row, row.product_type_choice);
            this.prepareBatchAsin(false);
        },
        applyBatchAsinProductType(value) {
            if (this.genMultiBusy) return;
            this.invalidateBatchAsinPlan();
            this.batchAsinRows.forEach(row => this.setBatchAsinProductType(row, value));
            this.prepareBatchAsin(false);
        },
        // 「生成模式」列表头一键应用：与产品类型一致地应用到全部行，行内仍可单独切换。
        applyGenRowMode(value) {
            if (this.genMultiBusy) return;
            this.batchGenMode = value || '';
            if (!value) return;
            const asin = this.genMultiMode === 'asin';
            const rows = asin ? this.batchAsinRows : this.genMultiRows;
            const handler = asin ? this.onBatchAsinModeChange : this.onGenRowModeChange;
            rows.forEach(row => {
                this.$set(row, 'mode', value);
                handler(row);
            });
            if (asin) this.prepareBatchAsin(false);
            else this.loadGenMultiConfigs();
        },
        setBatchAsinProductType(row, value) {
            this.$set(row, 'product_type_choice', value || '');
            const selected = String(row.product_type_choice || '').trim();
            const configured = String(row.configured_product_type || '').trim();
            // 与长期配置相同的值不物化为批次覆盖；其它值只影响本次批量任务，
            // 用户若要长期生效可直接点击“配置品名/配置父体”保存。
            row.product_type_override = selected && selected !== configured ? selected : '';
            row.product_type = selected;
            row.fields = [];
            row.field_values = {};
            row.titles = {};
            row.config_error = '';
        },
        onBatchAsinCountryChange(row) {
            this.invalidateBatchAsinPlan();
            this.$set(row, 'country', String(row.country || '').trim().toUpperCase());
            this.$set(row, '_countryManual', true);
            this.refreshBatchAsinLengthBudgets();
        },
        applyBatchAsinDefaultNum() {
            if (!Number.isInteger(this.batchAsinDefaultNum) || this.batchAsinDefaultNum < 1) return;
            this.invalidateBatchAsinPlan();
            this.batchAsinRows.forEach(row => { row.num = this.batchAsinDefaultNum; });
        },
        removeBatchAsinRow(index) {
            this.invalidateBatchAsinPlan();
            this.batchAsinRows.splice(index, 1);
        },
        onBatchAsinRowCommand(command, row) {
            // 与批量生成MSKU一致：行内「配置品名规则」只打开 MSKU 基础名规则弹窗，
            // 不打开完整品名配置（完整品名配置走「配置品名」入口）。
            if (command === 'config') return this.openBatchAsinMskuRule(row);
            if (command === 'parentConfig') return this.openBatchAsinParentConfig(row);
            if (command === 'remove') {
                const index = this.batchAsinRows.indexOf(row);
                if (index !== -1) this.removeBatchAsinRow(index);
            }
        },
        batchAsinRowFromMskuTemplate(source) {
            const item = source || {};
            const uploadOverrides = Object.assign({}, item.upload_overrides || {});
            const rowErrors = Array.isArray(item.errors) ? item.errors : [];
            const suffix = String(item.suffix || this.effectiveBatchAsinSuffix() || 'T').trim().toUpperCase();
            // 模板里填了 MSKU模板 的行默认「按模板」生成；行内仍可单独切回。
            const uploadTemplate = String(uploadOverrides.msku_template || '').trim();
            const configuredMode = item.mode || 'auto';
            const mode = uploadTemplate && configuredMode !== 'template' ? 'template' : configuredMode;
            return {
                // 父体仍可按行从旧模板读取；店铺不从模板读取，统一使用批量操作栏的目标店铺。
                parent: item.parent || '',
                pm: item.pm || '',
                shop: this.batchAsinImportShop || '',
                current_msku: '',
                sku: item.sku || '',
                skus: item.sku ? [item.sku] : [],
                num: Number(item.num) || this.batchAsinDefaultNum || 1,
                mode,
                msku_template: item.msku_template || uploadOverrides.msku_template || '',
                series: item.series || uploadOverrides.series || '',
                style: item.style || uploadOverrides.style || '',
                country: item.country || uploadOverrides.country || '',
                msku_separator: item.msku_separator || uploadOverrides.msku_separator || '-',
                suffix,
                should_pair: suffix === 'T',
                msku_color: item.msku_color || uploadOverrides.msku_color || '',
                supplier: item.supplier || '',
                base_override: '',
                base: item.base_preview || '',
                configured_base: item.base_preview || '',
                country_abbr: item.country || '',
                color: '',
                max_base_len: item.max_base_len || 0,
                truncated: !!item.truncated,
                product_type: '',
                configured_product_type: '',
                product_type_choice: '',
                product_type_override: '',
                product_type_source: '',
                fields: [],
                field_values: {},
                titles: {},
                config_error: item.config_error || '',
                import_error: !item.pm && rowErrors.length ? rowErrors.join('；') : '',
                local_name: item.local_name || '',
                sku_details: {},
                sku_detail_error: '',
                 upload_overrides: uploadOverrides,
                 config_overrides: Object.assign({}, uploadOverrides),
                 _supplierManual: item.supplier_source === '上传来源',
                 _colorManual: Object.prototype.hasOwnProperty.call(uploadOverrides, 'msku_color'),
                _modeManual: Object.prototype.hasOwnProperty.call(uploadOverrides, 'use_msku_template')
                    || (!!uploadTemplate && configuredMode !== 'template'),
                _skuManual: true,
            };
        },
        importBatchAsinMskuFile(file) {
            if (this.genMultiBusy) return;
            // 店铺决定本批次所有行的归属，没选店铺时无法带出品名，直接拦住。
            if (!this.batchAsinImportShop) {
                this.$message.warning('请先选择目标店铺，再上传 MSKU 模板带出品名');
                return;
            }
            const raw = file && file.raw;
            if (!raw) return;
            const form = new FormData();
            form.append('parent', '');
            // 模板不携带店铺；预览使用批量操作栏选定的唯一目标店铺。
            form.append('shop', this.batchAsinImportShop || '');
            form.append('suffix', this.effectiveBatchAsinSuffix() || 'T');
            form.append('file', raw);
            this.batchAsinMskuImporting = true;
            this.$axios.post('/vue/msku-generation/import-preview', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }).then(res => {
                if (res.code !== 200 || !res.data) {
                    throw new Error((res && res.message) || 'MSKU 模板解析失败');
                }
                const sourceRows = res.data.rows || [];
                this.batchAsinLengthBudgetToken += 1;
                this.batchAsinRows = sourceRows.map(row => this.batchAsinRowFromMskuTemplate(row));
                this.batchAsinPlan = { items: [], mskus: [], reused: [], conflict: [] };
                this.batchAsinExpandedItems = [];
                this.batchAsinResult = {};
                this.batchAsinResultRows = [];
                this.batchAsinImportResult = '';
                this.genMultiView = 'config';
                if (!this.batchAsinRows.length) {
                    throw new Error('文件中没有可处理的 MSKU 行');
                }
                this.prepareBatchAsin(false);
                const errors = res.data.errors || [];
                if (errors.length) {
                    this.$message.warning('已导入 ' + this.batchAsinRows.length
                        + ' 行，其中 ' + errors.length + ' 行需要修正，请查看表格提示');
                } else {
                    this.$message.success('已按 MSKU 模板识别品名，并带出 ASIN 配置');
                }
            }).catch(error => {
                const message = error && error.response && error.response.data && error.response.data.message;
                this.$message.error(message || error.message || 'MSKU 模板解析失败');
            }).finally(() => { this.batchAsinMskuImporting = false; });
        },
        openBatchAsinPmConfig(row) {
            this.pmConfigMeta = {
                '本地父体': row.parent || '',
                '本地品名': row.pm || '',
                '店铺': row.shop || '',
                // 配置行来自计划时没有 current_msku，退到该品名的第一个待创建 MSKU。
                msku: row.current_msku || (row.mskus || [])[0] || '',
            };
            this.dialogPmConfigVisible = true;
        },
        openBatchAsinParentConfig(row) {
            this.batchAsinParentConfigMeta = { parent: row.parent || '' };
            this.batchAsinParentConfigVisible = true;
        },
        onBatchAsinParentConfigSaved() {
            if (this.genMultiVisible && this.genMultiMode === 'asin') this.prepareBatchAsin(false);
        },
        // 批量新建ASIN 缺 MSKU 生成配置（系列/款式/颜色/模板/国家/供应商）时，只打开
        // MSKU 基础名规则弹窗；完整品名配置（产品类型、创建字段、标题）不在这一步维护。
        openBatchAsinMskuRule(row) {
            this.batchAsinMskuRuleMeta = {
                parent: row.parent || '', pm: row.pm || '', shop: row.shop || '',
            };
            this.batchAsinMskuRuleVisible = true;
        },
        onBatchAsinMskuRuleSaved() {
            if (this.genMultiVisible && this.genMultiMode === 'asin') this.prepareBatchAsin(false);
        },
        batchAsinRowIssue(row) {
            if (row.import_error) return row.import_error;
            // 供应商缩写缺失（ERP 有供应商名但字典没映射）不能静默用品名配置兜底，
            // 必须拦住让用户补字典或手填；手填后该行不再阻塞（手填值优先）。
            if (row.sku_detail_error && !this.supplierIssueSettled(row)) return row.sku_detail_error;
            if (row.config_error) return row.config_error;
            if (this.batchAsinRowOverlong(row)) {
                return '基础名 ' + this.batchAsinRowBase(row).length + ' 字符超出最大 '
                    + row.max_base_len + ' 字符，请编辑基础名预览';
            }
            const missing = this.batchAsinMissingFields(row);
            if (missing.length) return '缺少：' + missing.join('、');
            if (String(row.suffix || 'T').toUpperCase() === 'T' && row.should_pair
                && !String(row.sku || '').trim())
                return '发货产品创建后配对，需选择关联 SKU';
            return '';
        },
        // 品名配置更新结果：按 本地父体/本地品名/店铺 匹配导入返回的 config_results。
        batchAsinConfigUpdate(row) {
            if (!row || !(this.batchAsinConfigResults || []).length) return null;
            const key = [row.parent || '', row.pm || '', row.shop || ''].join('|');
            return this.batchAsinConfigResults.find(entry => [
                entry.parent || '', entry.pm || '', entry.shop || '',
            ].join('|') === key) || null;
        },
        batchAsinConfigPreviewFields(row) {
            if (!row || !row.product_type) return [];
            const titles = row.titles || {};
            const hasMainTitle = Object.prototype.hasOwnProperty.call(row, 'title_main_base');
            const hasSubTitle = Object.prototype.hasOwnProperty.call(row, 'title_sub_base');
            const configuredMainTitle = hasMainTitle ? row.title_main_base : titles.item_name;
            const configuredSubTitle = hasSubTitle ? row.title_sub_base : titles.title_differentiation;
            const fields = [
                { key: 'item_name', label: '标题', value: configuredMainTitle,
                    configValue: configuredMainTitle, source: '标题配置' },
                { key: 'title_differentiation', label: '副标题', value: configuredSubTitle,
                    configValue: configuredSubTitle, source: '标题配置' },
            ];
            const seen = new Set(fields.map(item => item.key));
            (row.fields || []).forEach(field => {
                if (!field || !field.field_name || seen.has(field.field_name)) return;
                seen.add(field.field_name);
                fields.push({
                    key: field.field_name,
                    label: field.title_zh || field.field_name,
                    field,
                    source: field.value_source || (field.config_level === 'parent' ? '父体字段' : '品名字段'),
                });
            });
            return fields;
        },
        batchAsinValueIsEmpty(value) {
            if (value === undefined || value === null) return true;
            if (typeof value === 'string') return value.trim() === '';
            if (Array.isArray(value)) {
                return !value.length || value.some(item => this.batchAsinValueIsEmpty(item));
            }
            if (typeof value === 'object') {
                const keys = Object.keys(value);
                return !keys.length || keys.some(key => this.batchAsinValueIsEmpty(value[key]));
            }
            // false / 0 都是有效的已填写值。
            return false;
        },
        batchAsinFieldValueText(row, descriptor) {
            const values = (row && row.field_values) || {};
            const field = (descriptor && descriptor.field) || {};
            let value = Object.prototype.hasOwnProperty.call(descriptor || {}, 'configValue')
                ? descriptor.configValue : values[descriptor.key];
            if (value === undefined || value === null || value === '') {
                value = descriptor.value;
            }
            if ((value === undefined || value === null || value === '')
                && field.default_value !== undefined && field.default_value !== null
                && field.default_value !== '') {
                value = field.default_value;
            }
            if (value === undefined || value === null || value === '') {
                return ['item_name', 'title_differentiation'].includes(descriptor.key)
                    ? '' : '未填写';
            }
            if (value === true) return '是';
            if (value === false) return '否';
            if (field.field_name === 'item_package_dimensions' && typeof value === 'object') {
                const parts = ['length', 'width', 'height'].map(key => value[key] === undefined ? '-' : value[key]);
                return parts.join(' × ') + (value.unit ? ' ' + value.unit : '');
            }
            if (field.field_name === 'item_package_weight' && typeof value === 'object') {
                return (value.value === undefined ? '-' : value.value) + (value.unit ? ' ' + value.unit : '');
            }
            if (Array.isArray(value)) {
                return value.map(item => {
                    if (item && typeof item === 'object') {
                        return item.label || item.value || item.name || JSON.stringify(item);
                    }
                    return String(item);
                }).join('、') || '未填写';
            }
            if (typeof value === 'object') {
                return Object.keys(value).map(key => key + ': ' + this.batchAsinScalarText(value[key])).join('；');
            }
            return String(value);
        },
        batchAsinScalarText(value) {
            if (value === true) return '是';
            if (value === false) return '否';
            if (value === null || value === undefined || value === '') return '-';
            return typeof value === 'object' ? JSON.stringify(value) : String(value);
        },
        batchAsinGenerationOverrides(row) {
            const overrides = Object.assign({},
                (row && row.config_overrides) || {}, (row && row.upload_overrides) || {});
            if (row && (row._colorManual || Object.prototype.hasOwnProperty.call(overrides, 'msku_color'))) {
                overrides.msku_color = String(row.msku_color || '').trim();
            }
            if (row && row._supplierManual) overrides.supplier = String(row.supplier || '').trim();
            if (row && row._countryManual) overrides.country = String(row.country || '').trim();
            return overrides;
        },
        prepareBatchAsin(force) {
            const prepareRows = this.batchAsinRows.filter(row => row.pm && row.shop && !row.import_error);
            if (!prepareRows.length) return;
            this.invalidateBatchAsinPlan();
            const requestToken = ++this.batchAsinPrepareToken;
            this.batchAsinCfgLoading = true;
            const items = prepareRows.map(row => ({
                parent: row.parent, pm: row.pm, shop: row.shop, sku: row.sku,
                num: Number(row.num), mode: row.mode, suffix: row.suffix,
                msku_color: row.msku_color, supplier: row.supplier,
                product_type_override: row.product_type_override || '',
                config_overrides: this.batchAsinGenerationOverrides(row),
                upload_overrides: row.upload_overrides || {},
            }));
            this.$axios.post('/vue/vue_product/batch-asin/prepare',
                { items, suffix: this.effectiveBatchAsinSuffix() || 'T' }).then(res => {
                if (requestToken !== this.batchAsinPrepareToken) return;
                if (res.code !== 200 || !res.data) throw new Error((res && res.message) || '配置带出失败');
                (res.data.results || []).forEach((cfg, index) => {
                    const row = prepareRows[index] || this.batchAsinRows.find(r => r.parent === cfg.parent
                        && r.pm === cfg.pm && r.shop === cfg.shop
                        && (!cfg.sku || r.sku === cfg.sku));
                    if (!row) return;
                    ['base', 'configured_base', 'supplier', 'country', 'country_abbr',
                     'msku_template', 'series', 'style', 'msku_separator',
                     'color', 'max_base_len', 'truncated',
                     // 标题类字段必须一起回填，否则「显示品名原有配置」面板读的还是
                     // 首次带出时的旧标题（面板渲染的是行级 title_main_base/title_sub_base）。
                     'title_main_base', 'title_sub_base', 'title_color'].forEach(key => {
                         if (cfg[key] !== undefined) row[key] = cfg[key];
                     });
                    if (!row._modeManual && cfg.mode !== undefined) row.mode = cfg.mode;
                    // 品名配置里已显式配置的 MSKU 颜色：与本次上传色不同时，行内出现
                    // 「颜色来源」选择，避免显示上传色却按品名配置色生成。
                    this.$set(row, 'config_msku_color', String(cfg.config_msku_color || '').trim());
                    this.$set(row, 'row_msku_color', String(cfg.row_msku_color || '').trim());
                    // 与批量生成 MSKU 一样：配置颜色优先，没有配置时使用品名推荐色。
                    // 手工修改过的行保留本次输入，避免“重新带出配置”覆盖用户值。
                    if (!row._colorManual) {
                        const recommendedColor = this.batchAsinColorText(
                            cfg.msku_color || cfg.recommended_msku_color
                            || cfg.recommended_color || cfg.color);
                        this.$set(row, 'msku_color', recommendedColor);
                    }
                    this.batchAsinRefreshColorConflict(row, false);
                    row.skus = cfg.skus || [];
                    if (!row.sku && row.skus.length
                        && String(row.suffix || 'T').toUpperCase() === 'T') row.sku = row.skus[0];
                    row.sku_details = cfg.sku_details || {};
                    row.local_name = cfg.local_name || '';
                    row.sku_detail_error = cfg.sku_detail_error || '';
                    row.supplier_abbr_missing = !!cfg.supplier_abbr_missing;
                    const selectedDetail = row.sku_details[String(row.sku || '').trim()];
                    if (selectedDetail) {
                        row.local_name = selectedDetail.erp_local_name
                            || selectedDetail.local_name || row.local_name;
                        row.sku_detail_error = selectedDetail.error || row.sku_detail_error;
                        row.supplier_abbr_missing = !!selectedDetail.supplier_abbr_missing;
                        if (selectedDetail.supplier && !row._supplierManual) {
                            row.supplier = selectedDetail.supplier;
                        }
                    }
                    row.configured_product_type = cfg.configured_product_type || row.configured_product_type || '';
                    row.product_type = cfg.product_type || '';
                    row.product_type_source = cfg.product_type_source || '';
                    row.product_type_choice = row.product_type_override || row.product_type || '';
                     row.fields = cfg.fields || [];
                     row.field_values = cfg.field_values || {};
                     row.titles = cfg.titles || {};
                     row.config_error = cfg.config_error || '';
                 });
                // SKU 详情可能在本次带出后补齐供应商，长度预算需按最终行值刷新。
                this.refreshBatchAsinLengthBudgets();
                if (force) this.$message.success('已按品名配置重新带出');
            }).catch(error => {
                if (requestToken !== this.batchAsinPrepareToken) return;
                const message = error && error.response && error.response.data
                    && error.response.data.message;
                this.$message.error(message || error.message || '配置带出失败');
            }).finally(() => {
                if (requestToken === this.batchAsinPrepareToken) this.batchAsinCfgLoading = false;
            });
        },
        flattenBatchAsinResult(plan, result) {
            const rows = [];
            (plan.items || []).forEach(item => {
                (item.mskus || []).forEach(msku => rows.push({ msku, status: '待执行', reason: '' }));
                (item.reused || []).forEach(itemResult => rows.push({ msku: itemResult.msku, status: 'reused', reason: itemResult.reason || '' }));
                (item.conflict || []).forEach(itemResult => rows.push({ msku: itemResult.msku, status: 'conflict', reason: itemResult.reason || '' }));
            });
            (result && result.reused || []).forEach(itemResult => rows.push({ msku: itemResult.msku, status: 'reused', reason: itemResult.reason || '' }));
            (result && result.conflict || []).forEach(itemResult => rows.push({ msku: itemResult.msku, status: 'conflict', reason: itemResult.reason || '' }));
            const byMsku = {};
            (result && result.master_tasks || []).forEach(task => {
                if (!task || !task.msku) return;
                byMsku[task.msku] = {
                    status: task.status || 'waiting_publish',
                    publish_status: task.publish_status || 'pending',
                    reason: [task.status_msg, task.publish_error].filter(Boolean).join('；'),
                    master_task_id: task.id,
                    publish_task_id: task.publish_task_id || '',
                };
            });
            return rows.map(row => byMsku[row.msku]
                ? { ...row, ...byMsku[row.msku] } : row);
        },
        async generateBatchAsin() {
            if (this.genMultiBusy) return;
            if (!this.batchAsinRows.length) return;
            if (this.batchAsinMissingShopList.length) {
                return this.$message.warning('请先在批量操作栏选择本批次唯一目标店铺');
            }
            if (this.batchAsinProductTypeConflict) {
                return this.$message.warning('一次批量新建 ASIN 只能使用同一个产品类型');
            }
            if (this.batchAsinLengthBudgetLoading) {
                return this.$message.warning('正在计算 MSKU 长度预算，请稍候再生成');
            }
            if (this.batchAsinOverlongList.length) {
                return this.$message.warning('请先修改基础名预览：存在基础名超出 MSKU 最大长度的品名');
            }
            const firstIssue = this.batchAsinRows.map(r => this.batchAsinRowIssue(r)).find(Boolean);
            if (firstIssue) return this.$message.warning('请先修正配置：' + firstIssue);
            // 重新生成会产生新的 MSKU/字段载荷，不能继续使用已经提交过的
            // 确认 request_id；同一份预览在执行阶段仍保持该 ID 以支持重试幂等。
            this.batchAsinRequestId = this.newClientRequestId('batch_asin');
            this.batchAsinLoading = true;
            try {
                if (!await confirmMskuRules(this, this.batchAsinRows)) {
                    this.batchAsinLoading = false;
                    return;
                }
            } catch (error) {
                this.batchAsinLoading = false;
                this.$message.error(error.response && error.response.data && error.response.data.message || error.message || 'MSKU规则处理失败');
                return;
            }
            const items = this.batchAsinRows.map(row => ({
                parent: row.parent, pm: row.pm, shop: row.shop,
                sku: String(row.sku || '').trim(), num: Number(row.num), mode: row.mode,
                suffix: String(row.suffix || 'T').toUpperCase(), should_pair: !!row.should_pair,
                msku_color: row.msku_color, supplier: row.supplier,
                base_override: row.base_override || '',
                product_type_override: row.product_type_override || '',
                config_overrides: this.batchAsinGenerationOverrides(row),
                upload_overrides: row.upload_overrides || {},
            }));
            this.$axios.post('/vue/vue_product/batch-asin/generate',
                { items, suffix: this.effectiveBatchAsinSuffix() || 'T' }).then(res => {
                if (res.code !== 200 || !res.data) throw new Error((res && res.message) || '生成失败');
                this.batchAsinPlan = res.data;
                this.expandBatchAsinItems();
                this.batchAsinImportResult = '';
                this.batchAsinConfigSaved = false;
                this.genMultiView = 'results';
                if (!(res.data.mskus || []).length)
                    this.$message.warning('没有可用 MSKU（全部被复用检测拦截），请检查 reused/conflict');
            }).catch(error => {
                const message = error && error.response && error.response.data
                    && error.response.data.message;
                this.$message.error(message || error.message || '生成失败');
            }).finally(() => { this.batchAsinLoading = false; });
        },
        exportBatchAsinPlan() {
            if (this.batchAsinLoading || !(this.batchAsinPlan.mskus || []).length) return;
            this.batchAsinLoading = true;
            this.$axios.post('/vue/vue_product/batch-asin/export', { plan: this.batchAsinPlan },
                { responseType: 'blob' }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = '批量新建ASIN字段.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }, 300);
                this.$message.success('已导出，核对/修改后可上传覆盖');
            }).catch(error => {
                this.$message.error(error.message || '导出失败');
            }).finally(() => { this.batchAsinLoading = false; });
        },
        pickBatchAsinImport() {
            if (this.$refs.batchAsinFileInput) this.$refs.batchAsinFileInput.click();
        },
        onBatchAsinFilePicked(event) {
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!file) return;
            this.batchAsinImporting = true;
            const form = new FormData();
            form.append('file', file);
            form.append('plan', JSON.stringify(this.batchAsinPlan));
            this.$axios.post('/vue/vue_product/batch-asin/import', form).then(res => {
                if (res.code !== 200 || !res.data) throw new Error((res && res.message) || '导入失败');
                if (res.data.plan) {
                    this.batchAsinPlan = res.data.plan;
                    this.batchAsinConfigSaved = false;
                    this.expandBatchAsinItems();
                }
                const changed = (res.data.results || [])
                    .reduce((n, r) => n + (r.fields_changed || []).length, 0);
                const ignored = (res.data.results || [])
                    .reduce((n, r) => n + (r.fields_ignored || []).length, 0);
                const rows = (res.data.results || []).length;
                this.batchAsinImportResult = '已导入 ' + rows + ' 行、覆盖 ' + changed + ' 个字段值'
                    + ((res.data.unmatched || []).length
                        ? '；未匹配 MSKU ' + res.data.unmatched.length + ' 个' : '')
                    + (ignored ? '；忽略不属于对应产品类型的字段值 ' + ignored + ' 个' : '');
                // Excel 只记录保存选择；最终确认创建时统一保存。
                const configResults = res.data.config_results || [];
                if (res.data.config_save_pending) this.batchAsinImportResult += '；保存选择已带出，确认创建时生效';
                this.batchAsinConfigResults = configResults;
                // 写回的标题/副标题回填到行级快照：展开「品名原有配置」时能看到最新值，
                // 不必等重新生成/重新带出配置（该面板渲染的就是 plan 的行级快照）。
                configResults.forEach(entry => {
                    if (!entry || !entry.saved || !entry.title_values) return;
                    const target = this.batchAsinRows.find(row => row.parent === entry.parent
                        && row.pm === entry.pm && row.shop === entry.shop);
                    if (!target) return;
                    Object.keys(entry.title_values).forEach(key => {
                        this.$set(target, key, entry.title_values[key]);
                    });
                });
                if (configResults.length) {
                    const multiRow = configResults.filter(item => item.multi_row_update);
                    const failed = configResults.filter(
                        item => !item.saved && !item.multi_row_update);
                    const savedCount = configResults.filter(item => item.saved).length;
                    this.batchAsinImportResult += '；品名配置已更新 '
                        + savedCount + '/' + configResults.length;
                    if (multiRow.length) {
                        this.confirmMultiRowConfigUpdate(multiRow);
                    }
                    if (failed.length) {
                        this.$message.warning('部分品名配置保存失败（不影响本次刊登）：'
                            + failed.map(item => (item.pm || '') + '：' + (item.error || '保存失败')).join('；'));
                    }
                }
                const purchaseRows = (res.data.results || []).filter(
                    item => Number(item.procurement_quantity) > 0);
                if (purchaseRows.length) {
                    const total = purchaseRows.reduce(
                        (sum, item) => sum + Number(item.procurement_quantity || 0), 0);
                    this.batchAsinImportResult += '；新建并采购 ' + purchaseRows.length
                        + ' 个 MSKU、共 ' + total + ' 件（需在采购汇总中确认）';
                }
                if (!rows) {
                    this.$message.warning('上传文件没有匹配到当前计划中的 MSKU，未更新任何字段');
                } else if (!changed) {
                    this.$message.warning('已匹配 MSKU，但没有可覆盖的非空字段');
                } else {
                    this.$message.success('字段已按上传文件覆盖');
                }
            }).catch(error => {
                const message = error && error.response && error.response.data
                    && error.response.data.message;
                this.$message.error(message || error.message || '导入失败');
            }).finally(() => { this.batchAsinImporting = false; });
        },
        // 同一品名有多行都要求更新配置：品名配置只能保存一份值，无法判断以谁为准，
        // 后端已跳过写回。这里用二次确认弹窗提醒"哪些品名存在多条配置保存要求"，
        // 不展开具体字段取值（不做顶部浮动提示）。
        confirmMultiRowConfigUpdate(multiRow) {
            const lines = multiRow.map(item => {
                const matched = item.error && String(item.error).match(/(\d+)\s*行/);
                const rowText = matched ? matched[1] + ' 行' : '多行';
                return '• 品名「' + (item.pm || '-') + '」' + rowText + '请求更新配置';
            });
            const message = '以下品名在本次上传中有多个 MSKU 请求更新配置，'
                + '品名配置只能保存一份值，已跳过写回：\n\n' + lines.join('\n')
                + '\n\n请核对「是否更新配置」列后重新导入。';
            this.$confirm(message, '品名配置更新确认', {
                type: 'warning',
                confirmButtonText: '确认，稍后重新导入',
                cancelButtonText: '知道了',
                distinguishCancelAndClose: true,
                customClass: 'batch-asin-config-confirm',
            }).catch(() => { });
        },
        batchAsinReuseText(reason) {
            const map = {
                batch_duplicate: '批内重复', asin_create_task: '已有ASIN任务',
                auto_msku_task: '批量MSKU占用', current_selling_msku: '当前售卖MSKU',
                online_product: '线上已存在',
            };
            const key = String(reason || '').replace(/^race_/, '');
            return map[key] || reason;
        },
        batchAsinReuseList(items) {
            return (items || []).map(item => item.msku + '（' + this.batchAsinReuseText(item.reason) + '）').join('、');
        },
        batchAsinItemKey(item, index) {
            return [item && item.parent, item && item.pm, item && item.shop, index]
                .map(value => String(value || '')).join('|');
        },
        isBatchAsinItemExpanded(item, index) {
            return this.batchAsinExpandedItems.includes(this.batchAsinItemKey(item, index));
        },
        toggleBatchAsinItem(item, index) {
            const key = this.batchAsinItemKey(item, index);
            const current = this.batchAsinExpandedItems.indexOf(key);
            if (current === -1) this.batchAsinExpandedItems.push(key);
            else this.batchAsinExpandedItems.splice(current, 1);
        },
        expandBatchAsinItems() {
            this.batchAsinOriginalConfigExpanded = [];
            this.batchAsinExpandedItems = (this.batchAsinPlan.items || [])
                .map((item, index) => this.batchAsinItemKey(item, index));
        },
        toggleBatchAsinOriginalConfig(item, index) {
            const key = this.batchAsinItemKey(item, index);
            const position = this.batchAsinOriginalConfigExpanded.indexOf(key);
            if (position === -1) {
                this.batchAsinOriginalConfigExpanded.push(key);
                // 展开即读最新品名配置：该面板渲染的是 plan 行级快照，不重读会一直显示
                // 本次「带出配置」时的旧值（在品名配置弹窗里改过也不会变）。
                this.refreshBatchAsinOriginalConfig(item);
            } else this.batchAsinOriginalConfigExpanded.splice(position, 1);
        },
        // 按 父体+品名+店铺 实时读品名配置，把标题/副标题与创建字段值刷新到行级快照
        refreshBatchAsinOriginalConfig(item) {
            if (!item || !item.pm || !item.shop) return;
            const requestToken = ++this.batchAsinOriginalConfigToken;
            this.$axios.get('/vue/vue_product/operation-config', {
                params: { parent: item.parent || '', pm: item.pm || '', shop: item.shop || '' },
            }).then(res => {
                if (requestToken !== this.batchAsinOriginalConfigToken) return;
                if (res.code !== 200 || !res.data) throw new Error((res && res.message) || '读取品名配置失败');
                const cfg = res.data || {};
                this.$set(item, 'title_main_base', cfg.create_main_title || '');
                this.$set(item, 'title_sub_base', cfg.create_sub_title || '');
                this.$set(item, 'field_values', cfg.create_field_values || {});
            }).catch(error => {
                const message = error && error.response && error.response.data
                    && error.response.data.message;
                this.$message.warning(message || '读取品名配置失败，当前显示本次带出的快照');
            });
        },
        collapseBatchAsinItems() {
            this.batchAsinExpandedItems = [];
        },
        batchAsinMskuFieldValues(item, msku) {
            const valuesMap = item && item.msku_field_values;
            const values = valuesMap && valuesMap[String(msku || '').trim()];
            return values && typeof values === 'object'
                ? values : ((item && item.field_values) || {});
        },
        setBatchAsinConfigUpdate(item, msku, value) {
            if (!item.msku_config_updates) this.$set(item, 'msku_config_updates', {});
            this.$set(item.msku_config_updates, msku, !!value);
        },
        // 横向卡片当前展示的 MSKU：索引存在 plan 行上，重新渲染后仍保持
        batchAsinItemActiveIndex(item) {
            const count = ((item && item.mskus) || []).length;
            if (!count) return 0;
            const index = Number(item && item._mskuIndex);
            if (!Number.isFinite(index) || index < 0) return 0;
            return Math.min(Math.floor(index), count - 1);
        },
        batchAsinItemActiveMsku(item) {
            return ((item && item.mskus) || [])[this.batchAsinItemActiveIndex(item)] || '';
        },
        onBatchAsinMskuSelect(item, index) {
            this.$set(item, '_mskuIndex', index);
        },
        onBatchAsinMskuEditToggle(item, editing) {
            this.$set(item, '_mskuEditing', !!editing);
        },
        // 卡片编辑写回该 MSKU 自己的字段快照；标题只改基础文案，编号与颜色统一重建。
        onBatchAsinMskuFieldChange(item, msku, payload) {
            const key = String((payload && payload.key) || '').trim();
            if (!item || !key) return;
            const mskuKey = String(msku || '').trim();
            const valuesMap = item.msku_field_values;
            let values = valuesMap && valuesMap[mskuKey];
            if (!values || typeof values !== 'object') {
                // 缺少逐 MSKU 快照时先复制一份，避免写到品名级默认值影响其它 MSKU。
                this.$set(item, 'msku_field_values', Object.assign({}, valuesMap || {}, {
                    [mskuKey]: Object.assign({}, this.batchAsinMskuFieldValues(item, msku)),
                }));
                values = item.msku_field_values[mskuKey];
            }
            this.$set(values, key, payload.value);
            // 颜色列改动同样要重拼副标题（副标题 = 基础文案 + 该行颜色）。
            if (key === 'title_main_base' || key === 'title_sub_base' || key === 'color') {
                this.rebuildBatchAsinMskuTitle(item, msku);
            }
        },
        // 与后端 build_create_title 同口径：主标题 = 基础文案 + (A编号)，副标题 = 基础文案 + ,颜色
        rebuildBatchAsinMskuTitle(item, msku) {
            const mskuKey = String(msku || '').trim();
            const values = (item && item.msku_field_values || {})[mskuKey];
            if (!values || typeof values !== 'object') return;
            const mainBase = String(values.title_main_base || '').trim();
            const subBase = String(values.title_sub_base || '');
            const token = String((item.title_tokens || {})[mskuKey] || '').trim();
            values.item_name = mainBase ? mainBase + ' (A' + token + ')' : '';
            // 副标题颜色：该 MSKU 的颜色列（上传/回填）优先，其次品名配置色；
            // 基础文案里已带同一颜色时先剥掉，避免拼成双份颜色。
            const color = this.batchAsinColorText(values.color)
                || String(item.title_color || '').trim();
            const base = this.stripTrailingTitleColor(subBase, color);
            values.title_differentiation = color
                ? (base ? base + ',' + color : color) : base;
        },
        stripTrailingTitleColor(text, color) {
            let value = String(text || '');
            const target = String(color || '').trim();
            if (!value || !target) return value;
            const suffixes = [',' + target, '，' + target];
            for (let guard = 0; guard < 10; guard += 1) {
                const tail = value.replace(/\s+$/, '');
                const hit = suffixes.find(suffix => tail.toLowerCase()
                    .endsWith(suffix.toLowerCase()));
                if (!hit) break;
                value = tail.slice(0, tail.length - hit.length).replace(/\s+$/, '');
            }
            return value;
        },
        // 顶部校验明细点击「定位」：展开对应品名并切到出错的 MSKU 卡片
        focusBatchAsinMissing(missing) {
            const items = this.batchAsinPlan.items || [];
            const itemIndex = items.findIndex(item => (item.pm || '') === (missing.pm || '')
                && (item.mskus || []).some(msku => String(msku).trim() === missing.msku));
            if (itemIndex === -1) return;
            const item = items[itemIndex];
            const mskuIndex = (item.mskus || [])
                .findIndex(msku => String(msku).trim() === missing.msku);
            const key = this.batchAsinItemKey(item, itemIndex);
            if (!this.batchAsinExpandedItems.includes(key)) this.batchAsinExpandedItems.push(key);
            this.$set(item, '_mskuIndex', mskuIndex < 0 ? 0 : mskuIndex);
            this.$nextTick(() => {
                const target = document.querySelector('[data-batch-asin-item="' + itemIndex + '"]');
                if (target && target.scrollIntoView) target.scrollIntoView({ block: 'center' });
            });
        },
        // 生成结果里把模板带出的 MSKU 生成配置写回品名规则（与批量生成MSKU 同一接口）
        saveBatchAsinUploadConfig() {
            if (this.genMultiBusy || this.batchAsinConfigSyncing
                || this.batchAsinConfigSaved || !this.batchAsinHasUploadRows) return;
            const rows = (this.batchAsinPlan.items || []).filter(item => item && item.success !== false
                && Object.keys(item.upload_overrides || {}).length);
            if (!rows.length) return this.$message.warning('本批次没有来自模板的 MSKU 生成配置');
            this.batchAsinConfigSyncing = true;
            this.$axios.post('/vue/msku-generation/generate/multi/sync-upload-config', { rows })
                .then(res => {
                    if (res.code !== 200 || !res.data) {
                        return this.$message.error((res && res.message) || '生成配置保存失败');
                    }
                    this.batchAsinConfigSaved = true;
                    this.$message.success('已保存 ' + Number(res.data.synced_count || 0) + ' 个品名的 MSKU 生成配置');
                }).catch(error => {
                    const message = error && error.response && error.response.data
                        && error.response.data.message;
                    this.$message.error(message || '生成配置保存失败');
                }).finally(() => { this.batchAsinConfigSyncing = false; });
        },
        // 逐 MSKU 采购数量：默认 0（不采购），只接受非负整数；显式 0 不会回退成正数。
        batchAsinMskuQuantity(item, msku) {
            const map = (item && item.msku_quantities) || {};
            const value = map[String(msku || '').trim()];
            const quantity = Number(value);
            return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 0;
        },
        onBatchAsinMskuQuantityChange(item, msku, value) {
            if (!item) return;
            const key = String(msku || '').trim();
            if (!key) return;
            const quantity = Math.max(0, Math.floor(Number(value) || 0));
            const map = Object.assign({}, item.msku_quantities || {});
            map[key] = quantity;
            this.$set(item, 'msku_quantities', map);
            item.should_purchase = Object.keys(map).some(k => Number(map[k]) > 0);
        },
        batchAsinMskuFieldRows(item, msku) {
            if (!item) return [];
            const fieldValues = this.batchAsinMskuFieldValues(item, msku);
            const valueRow = { field_values: fieldValues, titles: item.titles || {} };
            const descriptors = [
                { key: 'item_name', label: '主标题', field: { field_name: 'item_name' },
                    value: fieldValues.item_name },
                { key: 'title_differentiation', label: '副标题',
                    field: { field_name: 'title_differentiation' },
                    value: fieldValues.title_differentiation },
            ];
            const seen = new Set(descriptors.map(field => field.key));
            (item.fields || []).forEach(field => {
                if (!field || !field.field_name || seen.has(field.field_name)) return;
                seen.add(field.field_name);
                descriptors.push({
                    key: field.field_name,
                    label: field.title_zh || field.field_name,
                    field,
                    value: fieldValues[field.field_name],
                    source: field.value_source
                        || (field.config_level === 'parent' ? '父体字段' : '品名字段'),
                });
            });
            return descriptors.map(descriptor => ({
                key: descriptor.key,
                label: descriptor.label,
                source: descriptor.source || '标题配置',
                value: this.batchAsinFieldValueText(valueRow, descriptor),
            }));
        },
        async executeBatchAsin() {
            if (this.batchAsinLoading || !(this.batchAsinPlan.items || []).length) return;
            if (this.batchAsinMissingShopList.length) {
                return this.$message.warning('请先在批量操作栏选择本批次唯一目标店铺');
            }
            // SKU 详情类阻断（ERP 查询失败 / 供应商缩写缺失）在提交前再查一次：
            // 生成到提交之间可能换过关联 SKU，不能靠生成阶段的拦截兜住。
            const detailIssue = this.batchAsinRows.map(row => (
                row.sku_detail_error && !this.supplierIssueSettled(row)
                    ? (row.pm || '') + '：' + row.sku_detail_error : '')).find(Boolean);
            if (detailIssue) {
                return this.$message.warning('提交前校验失败，请先处理 SKU 详情：' + detailIssue);
            }
            const missing = this.batchAsinSubmitMissingList || [];
            if (missing.length) {
                const detail = missing.map(item => item.msku + '：' + item.fields.join('、')).join('；');
                this.$message.warning('提交前校验失败，请补齐每个 MSKU 的字段：' + detail);
                return;
            }
            // 存在正数采购数量时，必须先展示采购汇总并确认，再提交混合批次。
            if (this.batchAsinHasProcurement) return this.submitBatchAsinProcurementDraft();
            this.batchAsinLoading = true;
            try {
                await this.$confirm(configSaveSummary(this.batchAsinPlan.items), '确认新建 ASIN 与配置保存', {
                    confirmButtonText: '确认创建', cancelButtonText: '返回检查',
                    closeOnClickModal: false, type: 'warning',
                });
            } catch (error) {
                this.batchAsinLoading = false;
                if (error instanceof Error) this.$message.warning(error.message);
                return;
            }
            this.$axios.post('/vue/vue_product/batch-asin/execute', {
                plan: this.batchAsinPlan, request_id: this.batchAsinRequestId,
            }).then(res => {
                if (res.code !== 200 || !res.data) throw new Error((res && res.message) || 'execute失败');
                this.batchAsinResult = res.data;
                this.batchAsinResultRows = this.flattenBatchAsinResult(this.batchAsinPlan, res.data);
                // 已提交的任务行先置为“待提交”，刷新按钮可拉取后端真实状态
                this.batchAsinResultRows.forEach(row => { if (row.status === '待执行') row.status = 'pending'; });
                this.genMultiView = 'result';
                this.$message.success('已进入刊登队列；后续提交、产品同步和配对由统一监控处理');
            }).catch(error => {
                const responseData = error && error.response && error.response.data;
                const message = responseData && (responseData.message || responseData.msg);
                this.$message.error(message || error.message || '批量新建ASIN执行失败');
            }).finally(() => { this.batchAsinLoading = false; });
        },
        // 接收 Auto 已生成计划 → 建采购草稿 → 展示采购汇总（不重新生成 MSKU）。
        submitBatchAsinProcurementDraft() {
            try { configSaveSummary(this.batchAsinPlan.items); }
            catch (error) { return this.$message.warning(error.message); }
            this.batchAsinLoading = true;
            this.batchAsinDraft = null;
            this.$axios.post('/vue/vue_product/batch-asin/procurement-draft', {
                plan: this.batchAsinPlan, request_id: this.batchAsinRequestId,
            }).then(res => {
                if (res.code !== 200 || !res.data) {
                    throw new Error((res && res.message) || '采购草稿创建失败');
                }
                this.batchAsinDraft = res.data;
                this.batchAsinDraftConfirmId = this.newClientRequestId('batch_asin_confirm');
                this.batchAsinDraftVisible = true;
            }).catch(error => {
                const responseData = error && error.response && error.response.data;
                const message = responseData && (responseData.message || responseData.msg);
                this.$message.error(message || error.message || '采购草稿创建失败');
            }).finally(() => { this.batchAsinLoading = false; });
        },
        // 确认采购汇总：提交混合批次（零数量创建 ASIN，正数量同时进入采购）。
        confirmBatchAsinProcurementDraft() {
            if (this.batchAsinDraftConfirming) return;
            const draft = this.batchAsinDraft || {};
            if (!draft.draft_id) return this.$message.warning('采购草稿不存在');
            if (!this.batchAsinDraftConfirmId) {
                this.batchAsinDraftConfirmId = this.newClientRequestId('batch_asin_confirm');
            }
            this.batchAsinDraftConfirming = true;
            this.$axios.post('/vue/vue_product/batch-asin/procurement-draft/confirm', {
                draft_id: draft.draft_id,
                version: draft.version || 1,
                confirm_request_id: this.batchAsinDraftConfirmId,
            }).then(res => {
                if (res.code !== 200 || !res.data) {
                    throw new Error((res && res.message) || '采购汇总确认失败');
                }
                const taskResponse = res.data.task_response || {};
                this.batchAsinResult = taskResponse;
                this.batchAsinResultRows = this.flattenBatchAsinResult(this.batchAsinPlan, taskResponse);
                this.batchAsinResultRows.forEach(row => { if (row.status === '待执行') row.status = 'pending'; });
                this.batchAsinDraftVisible = false;
                this.genMultiView = 'result';
                this.$message.success('采购汇总已确认，已进入统一刊登与采购监控');
            }).catch(error => {
                const responseData = error && error.response && error.response.data;
                const message = responseData && (responseData.message || responseData.msg);
                this.$message.error(message || error.message || '采购汇总确认失败');
            }).finally(() => { this.batchAsinDraftConfirming = false; });
        },
        refreshBatchAsinTasks() {
            const batchId = this.batchAsinResult.batch_id;
            if (!batchId || this.batchAsinLoading) return;
            this.batchAsinLoading = true;
            this.$axios.get('/vue/vue_product/batch-asin/tasks', { params: { batch_id: batchId, limit: 500 } }).then(res => {
                if (res.code !== 200 || !res.data) throw new Error((res && res.message) || '查询失败');
                const byMsku = {};
                (res.data.tasks || []).forEach(task => {
                    const key = task.new_msku || task.msku;
                    if (!key) return;
                    const old = byMsku[key];
                    if (!old || Number(task.attempt_no || 0) > Number(old.attempt_no || 0)) {
                        byMsku[key] = task;
                    }
                });
                this.batchAsinResultRows = this.batchAsinResultRows.map(row => {
                    const task = byMsku[row.msku];
                    if (!task) return row;
                    const reason = [task.step, task.error, task.status_msg, task.publish_error].filter(v => v).join('；')
                        || ('任务 ' + (task.task_id || ''));
                    return { ...row, status: task.status || row.status,
                        publish_status: task.publish_status || row.publish_status,
                        master_task_id: task.auto_msku_task_id || row.master_task_id,
                        publish_task_id: task.task_id || row.publish_task_id, reason };
                });
                this.$message.success('任务状态已刷新');
            }).catch(error => {
                this.$message.error(error.message || '任务状态查询失败');
            }).finally(() => { this.batchAsinLoading = false; });
        },
        batchAsinStatusText(status) {
            const map = {
                pending: '待提交', submitting: '提交中', publishing: '刊登中',
                success: '刊登成功', explicit_failed: '明确失败', result_unknown: '结果待确认',
                waiting_publish: '等待刊登结果', waiting_product: '等待产品',
                manual_review: '人工核查', waiting_fnsku: '等待FNSKU',
                waiting_pair: '等待配对', created_only: '仅创建完成', failed: '失败',
                reused: '复用跳过', conflict: '线上冲突', race_online_product: '线上冲突',
                待执行: '待执行',
            };
            return map[status] || status || '-';
        },
        batchAsinStatusTagType(status) {
            const map = {
                created_only: 'success', waiting_pair: 'success', waiting_fnsku: 'warning',
                waiting_publish: 'warning', waiting_product: 'warning',
                publishing: '', submitting: '', pending: 'info', success: 'success',
                explicit_failed: 'danger', result_unknown: 'danger', manual_review: 'danger',
                failed: 'danger',
                reused: 'warning', conflict: 'danger', race_online_product: 'danger',
            };
            return map[status] !== undefined ? map[status] : 'info';
        },
        buildGenMultiRowsFromSelected(rows) {
            // 去重（同一父体+品名+店铺只保留一条）
            const seen = new Set();
            return (rows || [])
                .map(r => ({
                    parent: r['本地父体'] || '', pm: r['本地品名'] || '', shop: r['店铺'] || '',
                    sku: '', skus: [], resolveError: '',
                    mode: 'attribute', msku_color: '', supplier: '', local_name: '',
                    suffix: this.effectiveGenMultiSuffix() || 'T', _suffixManual: false,
                    should_pair: (this.effectiveGenMultiSuffix() || 'T') === 'T', _pairManual: false,
                    num: this.genMultiDefaultNum,
                }))
                .filter(r => {
                    const k = r.parent + '|' + r.pm + '|' + r.shop;
                    if (seen.has(k)) return false;
                    seen.add(k);
                    return r.pm && r.shop;
                });
        },
        // === MSKU 域：SKU + 颜色导入、预览、保存并生成 ===
        openMskuColorImport() {
            this.resetMskuColorImport();
            this.mskuColorImportParent = this.searchForm.parentName || '';
            this.mskuColorImportShop = this.searchForm.shop || '';
            this.mskuColorImportRequestId = this.newClientRequestId('manual_msku_import');
            this.mskuColorImportVisible = true;
        },
        resetMskuColorImport() {
            this.mskuColorImportParent = '';
            this.mskuColorImportShop = '';
            this.mskuColorImportSuffix = 'T';
            this.mskuColorImportShouldPair = true;
            this.mskuColorImportRequestId = '';
            this.mskuColorImportFiles = [];
            this.mskuColorImportFile = null;
            this.mskuColorImportRows = [];
            this.mskuColorImportErrors = [];
            this.mskuColorImportPreviewing = false;
            this.mskuColorImportSaving = false;
            this.mskuColorImportDownloading = false;
        },
        onMskuColorImportSuffixChange(value) {
            this.mskuColorImportShouldPair = String(value || '').toUpperCase() === 'T';
        },
        selectMskuColorFile(file, fileList) {
            this.mskuColorImportFile = file && file.raw ? file.raw : null;
            this.mskuColorImportFiles = fileList || [];
            this.mskuColorImportRows = [];
            this.mskuColorImportErrors = [];
        },
        removeMskuColorFile() {
            this.mskuColorImportFile = null;
            this.mskuColorImportFiles = [];
            this.mskuColorImportRows = [];
            this.mskuColorImportErrors = [];
        },
        downloadMskuColorTemplate() {
            const asinMode = this.genMultiVisible && this.genMultiMode === 'asin';
            const asinRow = (this.batchAsinRows || [])[0] || {};
            const parent = asinMode ? (this.batchAsinImportParent || asinRow.parent)
                : (this.genMultiVisible ? this.genMultiImportParent : this.mskuColorImportParent);
            // 两种模式使用同一模板，店铺在生成前选择。
            const shop = asinMode ? ''
                : (this.genMultiVisible ? this.genMultiImportShop : this.mskuColorImportShop);
            const suffix = asinMode ? (this.effectiveBatchAsinSuffix() || 'T')
                : (this.genMultiVisible ? (this.effectiveGenMultiSuffix() || 'T') : this.mskuColorImportSuffix);
            this.mskuColorImportDownloading = true;
            const params = { suffix };
            if (parent) params.parent = parent;
            if (shop) params.shop = shop;
            this.$axios.get('/vue/msku-generation/import-template', {
                params,
                responseType: 'blob'
            }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'MSKU批量生成模板.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.mskuColorImportDownloading = false;
                }, 300);
            }).catch(error => {
                this.mskuColorImportDownloading = false;
                this.$message.error(error.message || 'MSKU 批量生成模板下载失败');
            });
        },
        downloadBatchAsinMskuTemplate() {
            this.downloadMskuColorTemplate();
        },
        previewMskuColorImport() {
            if (!this.mskuColorImportFile) return this.$message.warning('请选择 Excel 文件');
            if (!String(this.mskuColorImportShop || '').trim()) {
                return this.$message.warning('请先选择目标店铺');
            }
            const form = new FormData();
            form.append('parent', this.mskuColorImportParent);
            form.append('shop', this.mskuColorImportShop);
            form.append('suffix', this.mskuColorImportSuffix);
            form.append('file', this.mskuColorImportFile);
            this.mskuColorImportPreviewing = true;
            this.$axios.post('/vue/msku-generation/import-preview', form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(res => {
                this.mskuColorImportPreviewing = false;
                if (res.code !== 200 || !res.data) {
                    this.$message.error((res && res.message) || '导入解析失败');
                    return;
                }
                this.mskuColorImportRows = res.data.rows || [];
                this.mskuColorImportErrors = res.data.errors || [];
                const summary = res.data.summary || {};
                if (summary.invalid) {
                    this.$message.warning('已解析 ' + summary.total + ' 行，其中 ' + summary.invalid + ' 行需修正');
                } else {
                    this.$message.success('已解析 ' + summary.valid + ' 行，可保存并生成');
                }
            }).catch(error => {
                this.mskuColorImportPreviewing = false;
                const message = error && error.response && error.response.data && error.response.data.message;
                this.$message.error(message || '导入解析失败');
            });
        },
        commitMskuColorImport() {
            const rows = this.mskuColorImportRows || [];
            if (!rows.length || this.mskuColorImportErrors.length) return;
            if (!String(this.mskuColorImportShop || '').trim()) {
                return this.$message.warning('请先选择目标店铺');
            }
            if (rows.some(row => !row.valid || !(row.msku_color || '').trim())) {
                return this.$message.warning('请先修正所有导入行的 MSKU颜色');
            }
            if (rows.some(row => !(row.supplier || '').trim())) {
                return this.$message.warning('请先补齐所有导入行的供应商（可在此处手填）');
            }
            this.mskuColorImportSaving = true;
            this.$axios.post('/vue/msku-generation/import-commit', {
                parent: this.mskuColorImportParent,
                shop: this.mskuColorImportShop,
                suffix: this.mskuColorImportSuffix,
                request_id: this.mskuColorImportRequestId,
                items: rows.map(row => ({
                    sku: row.sku,
                    suffix: row.suffix || this.mskuColorImportSuffix,
                    should_pair: !!this.mskuColorImportShouldPair,
                    supplier: (row.supplier || '').trim(),
                    msku_color: (row.msku_color || '').trim(),
                    upload_overrides: {
                        ...(row.upload_overrides || {}),
                        msku_color: (row.msku_color || '').trim(),
                    },
                    num: row.num,
                })),
            }).then(res => {
                this.mskuColorImportSaving = false;
                if (res.code !== 200 || !res.data || !res.data.persisted) {
                    this.$message.error((res && res.message) || '保存并生成失败');
                    return;
                }
                const count = Number(res.data.persisted_count || 0);
                this.$message.success('已保存颜色并生成 ' + count + ' 条 Auto MSKU 记录');
                this.mskuColorImportVisible = false;
                this.refreshTableData();
            }).catch(error => {
                this.mskuColorImportSaving = false;
                const message = error && error.response && error.response.data && error.response.data.message;
                this.$message.error(message || '保存并生成失败');
            });
        },
        importGenMultiColorFile(file) {
            if (this.genMultiBusy) return;
            const raw = file && file.raw;
            if (!raw) return;
            const form = new FormData();
            form.append('parent', '');
            form.append('shop', this.genMultiImportShop);
            form.append('suffix', this.effectiveGenMultiSuffix() || 'T');
            form.append('file', raw);
            this.genMultiColorImporting = true;
            this.$axios.post('/vue/msku-generation/import-preview', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }).then(res => {
                if (res.code !== 200 || !res.data) {
                    this.$message.error((res && res.message) || 'MSKU 模板解析失败');
                    return;
                }
                const errors = res.data.errors || [];
                this.genMultiRows = (res.data.rows || []).map(row => {
                    const uploadOverrides = Object.assign({}, row.upload_overrides || {});
                    const importedColor = row.msku_color || uploadOverrides.msku_color || '';
                    // 模板里填了 MSKU模板 的行默认「按模板」生成；行内仍可单独切回按属性。
                    const uploadTemplate = String(uploadOverrides.msku_template || '').trim();
                    const configuredMode = row.mode || 'attribute';
                    const defaultedMode = uploadTemplate && configuredMode !== 'template'
                        ? 'template' : configuredMode;
                    return ({
                    ...(() => {
                        const rowErrors = row.errors || [];
                        const suffixError = row.suffix_error
                            || rowErrors.find(error => String(error).indexOf('后缀') >= 0) || '';
                        return {
                            suffixError,
                            resolveError: row.valid ? '' : rowErrors
                                .filter(error => error !== suffixError).join('；'),
                        };
                    })(),
                    parent: row.parent || '', pm: row.pm || '', shop: row.shop || this.genMultiImportShop || '', sku: row.sku || '',
                    skus: row.sku ? [row.sku] : [], local_name: row.local_name || row.erp_local_name || '', supplier: row.supplier || '',
                    _supplierManual: !!String(row.supplier || '').trim(),
                    mode: defaultedMode, msku_template: row.msku_template || uploadOverrides.msku_template || '',
                    series: row.series || uploadOverrides.series || '', style: row.style || uploadOverrides.style || '',
                    country: row.country || uploadOverrides.country || '',
                    msku_separator: row.msku_separator || uploadOverrides.msku_separator || '-', msku_color: importedColor,
                    upload_overrides: uploadOverrides, rule_sources: row.rule_sources || {},
                    _uploadOverrideFields: Object.keys(uploadOverrides),
                    _colorManual: Object.prototype.hasOwnProperty.call(uploadOverrides, 'msku_color'),
                    _modeManual: Object.prototype.hasOwnProperty.call(uploadOverrides, 'use_msku_template')
                        || (!!uploadTemplate && configuredMode !== 'template'),
                    _skuManual: true,
                    // 导入预览即使发现配置不完整也会返回行数据；不能在这里中断，
                    // 否则用户无法在批量表格中看到品名并进入父体/品名规则配置。
                    suffix: row.suffix || this.effectiveGenMultiSuffix() || 'T',
                    _suffixManual: row.suffix_source === '行级', cfgError: row.config_error || '',
                    should_pair: (row.suffix || this.effectiveGenMultiSuffix() || 'T') === 'T', _pairManual: false,
                    num: row.num || this.genMultiDefaultNum,
                    });
                });
                this.genMultiResults = [];
                this.genMultiSavedCount = 0;
                this.loadGenMultiConfigs();
                this.loadGenMultiSkuDetails();
                this.refreshGenMultiTableLayout();
                if (errors.length) {
                    this.$message.warning('已导入 ' + this.genMultiRows.length + ' 条记录，其中 ' + errors.length
                        + ' 行暂不能生成，请在表格下方补齐配置后再生成');
                } else {
                    this.$message.success('已导入 ' + this.genMultiRows.length + ' 条 MSKU 生成记录');
                }
            }).catch(error => {
                const message = error && error.response && error.response.data && error.response.data.message;
                this.$message.error(message || 'MSKU 模板解析失败');
            }).finally(() => { this.genMultiColorImporting = false; });
        },
        openGenMulti() {
            this.genMultiMode = 'msku';
            this.genMultiView = 'config';
            this.genMultiPreviewSignature = '';
            this.genMultiExpandedResults = [];
            this.genMultiImportShop = this.searchForm.shop || '';
            this.genMultiRows = this.buildGenMultiRowsFromSelected(this.tableSelection);
            this.genMultiResults = [];
            this.genMultiSavedCount = 0;
            this.genMultiRequestId = this.newClientRequestId('manual_msku');
            this.genMultiImportParent = this.searchForm.parentName || '';
            this.genMultiConfigSyncing = false;
            this.genMultiConfigSaved = false;
            this.batchAsinLoading = false;
            this.batchAsinCfgLoading = false;
            this.batchAsinImporting = false;
            this.batchAsinMskuImporting = false;
            this.batchAsinRows = [];
            this.batchAsinPlan = { items: [], mskus: [], reused: [], conflict: [] };
            this.batchAsinExpandedItems = [];
            this.batchAsinResult = {};
            this.batchAsinResultRows = [];
            this.batchAsinRequestId = '';
            this.batchAsinImportResult = '';
            this.batchAsinImportParent = '';
            this.genMultiVisible = true;
            // 已把主表勾选内容复制到批量生成弹窗，主表选中状态立即清空，避免后续误操作。
            this.clearTableSelection();
            this.refreshGenMultiTableLayout();
            if (this.genMultiRows.length) this.loadGenMultiConfigs();
        },
        onGenMultiImportShopChange(shop) {
            const targetShop = String(shop || '').trim();
            this.genMultiImportShop = targetShop;
            this.genMultiRows.forEach(row => this.$set(row, 'shop', targetShop));
            this.genMultiResults = [];
            this.genMultiPreviewSignature = '';
            this.genMultiSavedCount = 0;
            this.genMultiConfigSaved = false;
            if (targetShop && this.genMultiRows.length) this.loadGenMultiConfigs();
        },
        openGenMultiSkuImport() {
            // 子弹窗只使用批量生成范围，不再读取外层筛选条件。
            this.genMultiSkuImportShop = this.genMultiImportShop || '';
            this.genMultiSkuImportParent = '';
            this.genMultiSkuText = '';
            this.genMultiSkuPasteCount = 0;
            this.genMultiResolveLoading = false;
            this.genMultiSkuImportVisible = true;
        },
        resetGenMultiSkuImport() {
            this.genMultiSkuImportShop = '';
            this.genMultiSkuImportParent = '';
            this.genMultiSkuText = '';
            this.genMultiSkuPasteCount = 0;
            this.genMultiResolveLoading = false;
        },
        parseGenMultiSkuText(text) {
            const lines = String(text || '').replace(/\uFEFF/g, '').split(/\r\n|\n|\r/);
            const skus = lines.map(line => {
                // Excel 复制单列时每行可能带一个制表符；只取第一列，避免把其它列拼进 SKU。
                return String(line || '').split('\t')[0].trim();
            }).filter(Boolean);
            return skus.filter((sku, index) => !(index === 0 && /^(sku|本地sku|关联sku)$/i.test(sku)));
        },
        updateGenMultiSkuPasteCount() {
            this.genMultiSkuPasteCount = this.parseGenMultiSkuText(this.genMultiSkuText).length;
        },
        onGenMultiSkuPaste(event) {
            const pasted = event && event.clipboardData
                ? event.clipboardData.getData('text/plain') : '';
            if (!pasted) return;
            // 使用剪贴板原文，保证 Excel 的逐行内容按原样解析。
            event.preventDefault();
            this.genMultiSkuText = pasted;
            this.updateGenMultiSkuPasteCount();
        },
        resolveGenMultiSkus() {
            if (this.genMultiResolveLoading) return;
            const skus = this.parseGenMultiSkuText(this.genMultiSkuText);
            if (!skus.length) {
                this.$message.warning('请先粘贴 SKU 列');
                return;
            }
            this.genMultiResolveLoading = true;
            // The chooser belongs to the batch popup; make its confirmed values the
            // batch range without touching the outer page filters.
            this.genMultiImportShop = this.genMultiSkuImportShop;
            this.genMultiImportParent = this.genMultiSkuImportParent;
            this.$axios.post('/vue/msku-generation/config/by-sku/batch', {
                skus,
                parent: this.genMultiSkuImportParent,
                shop: this.genMultiSkuImportShop,
                suffix: this.effectiveGenMultiSuffix() || 'T',
            }).then(res => {
                this.genMultiResolveLoading = false;
                if (res.code !== 200 || !res.data) {
                    this.$message.error((res && res.message) || 'SKU 识别失败');
                    return;
                }
                const results = res.data.results || [];
                this.genMultiResults = [];
                this.genMultiSavedCount = 0;
                this.genMultiView = 'config';
                this.genMultiRows = results.map(r => ({
                    parent: r.parent || '', pm: r.pm || '', shop: this.genMultiImportShop || r.shop || '',
                    sku: r.sku || '', skus: Array.isArray(r.skus) ? r.skus : (r.sku ? [r.sku] : []),
                    suffix: this.effectiveGenMultiSuffix() || 'T', suffixError: '', _suffixManual: false,
                    resolveError: r.sku_error || '',
                    // SKU 反查结果就是用户本次导入的目标 SKU，后续切换后缀重新读配置时保留它。
                    _skuManual: true, should_pair: (this.effectiveGenMultiSuffix() || 'T') === 'T', _pairManual: false,
                    cfgError: r.sku_error ? '' : (r.success ? '' : (r.error || '')), num: this.genMultiDefaultNum,
                }));
                this.applyGenMultiConfigResults(results);
                this.genMultiSkuImportVisible = false;
                this.genMultiVisible = true;
                this.refreshGenMultiTableLayout();
                const unmatched = Number(res.data.unmatched_count || 0);
                const configFailed = Number(res.data.config_failed_count || 0);
                if (unmatched || configFailed) {
                    const matched = results.length - unmatched - configFailed;
                    let message = '识别完成：' + matched + ' 个成功';
                    if (unmatched) message += '，' + unmatched + ' 个未关联';
                    if (configFailed) message += '，' + configFailed + ' 个配置读取失败';
                    this.$message.warning(message + '，请查看表格提示');
                } else {
                    this.$message.success('已识别 ' + results.length + ' 个 SKU，并带出品名配置');
                }
            }).catch(() => {
                this.genMultiResolveLoading = false;
                this.$message.error('SKU 识别请求失败');
            });
        },
        // 拉取每个品名的 MSKU 生成配置（同时返回 male_sku 关联 SKU）
        loadGenMultiConfigs() {
            const readyRows = this.genMultiRows.filter(row => row.parent && row.pm && row.shop);
            if (!readyRows.length) {
                this.genMultiCfgLoading = false;
                return;
            }
            this.genMultiCfgLoading = true;
            this.$axios.post('/vue/msku-generation/config/batch', {
                items: readyRows.map(r => {
                    const payload = this.genMultiRowPayload(r);
                    const overrides = Object.assign({}, payload.config_overrides);
                    if (r._colorManual) overrides.msku_color = r.msku_color;
                    if (r._supplierManual) overrides.supplier = r.supplier;
                    if (r._modeManual) overrides.use_msku_template = r.mode === 'template' ? 1 : 0;
                    return {
                        parent: r.parent, pm: r.pm, shop: r.shop,
                        suffix: payload.suffix,
                        upload_overrides: payload.upload_overrides,
                        config_overrides: overrides,
                    };
                }),
                suffix: this.effectiveGenMultiSuffix() || 'T',
            }).then(res => {
                this.genMultiCfgLoading = false;
                if (res.code === 200 && res.data && res.data.results) {
                    this.applyGenMultiConfigResults(res.data.results, readyRows);
                } else {
                    this.$message.error((res && res.message) || '品名配置读取失败');
                }
            }).catch(() => {
                this.genMultiCfgLoading = false;
                this.$message.error('品名配置读取失败');
            });
        },
        applyGenMultiConfigResults(configResults, targetRows = this.genMultiRows) {
            // 同品名可能上传多行不同配置；按请求行匹配，不能用品名键覆盖前一行。
            targetRows.forEach((r, index) => {
                const c = (configResults || [])[index];
                if (c && (c.parent !== r.parent || c.pm !== r.pm || c.shop !== r.shop)) return;
                if (!c) return;

                const associationError = c.sku_error
                    || (c.sku_status === '未关联 SKU' ? '未关联 SKU' : '');
                // SKU 导入失败的行即使后续切换后缀重新读配置，也要保留原始错误提示。
                const nextSkuError = associationError || (r._skuManual ? r.resolveError : '');
                const associatedSkus = Array.isArray(c.skus)
                    ? c.skus.filter(sku => String(sku || '').trim())
                    : (c.sku ? [c.sku] : []);
                this.$set(r, 'skus', associatedSkus);
                if (!r._skuManual) {
                    this.$set(r, 'sku', c.sku || '');
                }

                if (c && c.success) {
                    this.$set(r, 'cfgError', '');
                    this.$set(r, 'resolveError', nextSkuError);
                    if (!r._modeManual) this.$set(r, 'mode', c.mode || 'attribute');
                    if (!this.genRowHasUploadField(r, 'msku_template')) this.$set(r, 'msku_template', c.msku_template || '');
                    if (!this.genRowHasUploadField(r, 'series')) this.$set(r, 'series', c.series || '');
                    if (!this.genRowHasUploadField(r, 'style')) this.$set(r, 'style', c.style || '');
                    if (!r._colorManual) this.$set(r, 'msku_color', c.msku_color || c.color || '');
                    this.$set(r, 'color', r.msku_color || c.color || '');
                    this.$set(r, 'shell', c.shell || '');
                    if (!this.genRowHasUploadField(r, 'msku_separator')) this.$set(r, 'msku_separator', c.msku_separator || '-');
                    if (!this.genRowHasUploadField(r, 'country')) this.$set(r, 'country', c.country || '');
                    if (!r._supplierManual) this.$set(r, 'supplier', c.supplier || '');
                    this.$set(r, 'country_abbr', c.country_abbr || '');
                    this.$set(r, 'rule_sources', c.sources || {});
                    this.$set(r, 'template_base', c.template_base || '');
                    this.$set(r, 'attribute_base', c.attribute_base || '');
                    this.refreshGenRowAttributeBase(r);
                    this.$set(r, 'max_base_len', c.max_base_len || 0);
                } else {
                    if (!r._modeManual) this.$set(r, 'mode', c.mode || 'attribute');
                    // 缺字段由合并后的当前行实时判断；查询失败、中文等其它错误继续拦截。
                    this.$set(r, 'cfgError', c.missing_fields && c.missing_fields.length
                        ? '' : (c.error || '配置读取失败'));
                    this.$set(r, 'resolveError', nextSkuError);
                    if (!this.genRowHasUploadField(r, 'msku_template')) this.$set(r, 'msku_template', c.msku_template || '');
                    if (!this.genRowHasUploadField(r, 'series')) this.$set(r, 'series', c.series || '');
                    if (!this.genRowHasUploadField(r, 'style')) this.$set(r, 'style', c.style || '');
                    if (!r._colorManual) this.$set(r, 'msku_color', c.msku_color || c.color || '');
                    if (!this.genRowHasUploadField(r, 'msku_separator')) this.$set(r, 'msku_separator', c.msku_separator || '-');
                    if (!this.genRowHasUploadField(r, 'country')) this.$set(r, 'country', c.country || '');
                    if (!r._supplierManual) this.$set(r, 'supplier', c.supplier || '');
                    this.$set(r, 'country_abbr', c.country_abbr || '');
                    this.$set(r, 'rule_sources', c.sources || {});
                    this.refreshGenRowAttributeBase(r);
                }
            });
            this.refreshGenMultiLengthBudgets();
            this.loadGenMultiSkuDetails();
        },
        onGenRowSkuInput(row) {
            if (!row) return;
            this.$set(row, '_skuManual', true);
            this.$set(row, '_skuError', false);
            this.$set(row, 'resolveError', '');
        },
        onGenRowSkuChange(row) {
            this.onGenRowSkuInput(row);
            this.loadGenRowSkuDetail(row);
        },
        genSkuDetailCacheKey(sku) {
            return String(sku || '').trim();
        },
        isGenSkuDetailError(error) {
            const text = String(error || '').trim();
            return text.indexOf('ERP SKU 信息查询失败') >= 0;
        },
        // 供应商缩写缺失（ERP 有供应商名但字典没映射）已被用户手填覆盖时，
        // 该行不再阻塞：手填值优先级高于 SKU 解析值。
        supplierIssueSettled(row) {
            return !!(row && row.supplier_abbr_missing && row._supplierManual
                && String(row.supplier || '').trim());
        },
        retryGenMultiSkuDetails() {
            const rows = this.genMultiRows.filter(row => this.isGenSkuDetailError(row.resolveError)
                && String(row.sku || '').trim());
            if (!rows.length) {
                this.$message.info('当前没有可重试的 ERP SKU 查询失败项');
                return;
            }
            rows.forEach(row => {
                const key = this.genSkuDetailCacheKey(row.sku);
                this.$delete(this.genMultiSkuDetailCache, key);
                this.$delete(this.genMultiSkuDetailLoading, key);
                this.$set(row, 'resolveError', '');
            });
            rows.forEach(row => this.loadGenRowSkuDetail(row, true));
        },
        isGenSkuDetailLoading(row) {
            if (!row) return false;
            const skus = (row.skus && row.skus.length ? row.skus : [row.sku])
                .map(sku => String(sku || '').trim()).filter(Boolean);
            return skus.some(sku => this.genMultiSkuDetailLoading[this.genSkuDetailCacheKey(sku)]);
        },
        genSkuOptionLabel(row, sku) {
            const detail = this.genMultiSkuDetailCache[this.genSkuDetailCacheKey(sku)];
            const value = String(sku || '').trim();
            const erpLocalName = detail && (detail.erp_local_name || detail.local_name);
            return erpLocalName ? value + ' | ' + String(erpLocalName).trim() : value;
        },
        genRowMissingConfigFields(row) {
            const missing = [];
            if (!String(row.parent || '').trim()) missing.push('本地父体');
            if (!String(row.pm || '').trim()) missing.push('本地品名');
            if (!String(row.shop || '').trim()) missing.push('店铺');
            const suffix = String(row.suffix || this.effectiveGenMultiSuffix() || '').trim();
            if (!/^[A-Za-z0-9]{1,6}$/.test(suffix)) missing.push('后缀（1-6位字母或数字）');
            if (row.mode === 'template') {
                if (!row.msku_template) missing.push('MSKU模板');
            } else {
                if (!row.series) missing.push('系列');
                if (!row.style) missing.push('款式');
                if (!String(row.msku_color || '').trim()) missing.push('MSKU颜色');
            }
            if (!row.country) missing.push('国家');
            return missing;
        },
        // msku_color 是品名级字段，父体没有同名字段，因此仅缺颜色时只给品名规则入口。
        genRowShowParentRuleAction(row) {
            const missing = this.genRowMissingConfigFields(row);
            const ruleMissing = missing.filter(field => !['本地父体', '本地品名', '店铺'].includes(field));
            return ruleMissing.some(field => field !== 'MSKU颜色') || (!missing.length && !!row.cfgError);
        },
        genRowShowPmRuleAction(row) {
            const missing = this.genRowMissingConfigFields(row);
            const ruleMissing = missing.filter(field => !['本地父体', '本地品名', '店铺'].includes(field));
            return ruleMissing.length > 0 || !!row.cfgError;
        },
        genRowMissingText(row) {
            const missing = this.genRowMissingConfigFields(row);
            if (!row.supplier) missing.push('供应商');
            return row.cfgError
                || [...new Set(missing)].filter(Boolean).join('、')
                || '规则待确认';
        },
        onGenRowColorChange(row) {
            if (!row) return;
            this.$set(row, '_colorManual', true);
            this.$set(row, 'color', String(row.msku_color || '').trim());
            // 属性基础名依赖颜色；颜色变化后丢弃旧的手工基础名，避免生成仍带旧颜色。
            if (row.mode !== 'template') this.resetGenRowBase(row);
            this.refreshGenRowAttributeBase(row);
        },
        genRowHasUploadField(row, field) {
            return !!(row && Array.isArray(row._uploadOverrideFields)
                && row._uploadOverrideFields.includes(field));
        },
        genRowHasUploadSource(row) {
            return !!(row && ((row._uploadOverrideFields && row._uploadOverrideFields.length)
                || (row.upload_overrides && Object.keys(row.upload_overrides).length)));
        },
        genRowAttributeBase(row) {
            return [row.series, row.style, row.msku_color]
                .map(value => String(value || '').trim()).filter(Boolean).join(row.msku_separator || '-');
        },
        refreshGenRowAttributeBase(row) {
            if (!row) return;
            this.$set(row, 'attribute_base', this.genRowAttributeBase(row));
        },
        refreshGenMultiLengthBudgets() {
            const rows = this.genMultiRows || [];
            if (!rows.length) return;
            this.$axios.post('/vue/msku-generation/length-budget/batch', {
                suffix: this.effectiveGenMultiSuffix() || 'T',
                items: rows.map(row => ({
                    supplier: row.supplier || '', country: row.country || '',
                    suffix: row.suffix || this.effectiveGenMultiSuffix() || 'T',
                })),
            }).then(res => {
                const budgets = res && res.data && res.data.results;
                if (!Array.isArray(budgets)) return;
                budgets.forEach(item => {
                    const row = rows[item.index];
                    if (!row) return;
                    if (item.error) this.$set(row, 'lengthBudgetError', item.error);
                    else {
                        this.$set(row, 'lengthBudgetError', '');
                        this.$set(row, 'max_base_len', item.max_base_len || 0);
                        this.$set(row, 'length_budget', item);
                    }
                });
            }).catch(() => { /* config response remains a conservative fallback */ });
        },
        loadGenRowSkuDetail(row, forceRefresh = false) {
            if (!row || !(row.sku || '').trim()) return;
            const cacheKey = this.genSkuDetailCacheKey(row.sku);
            const cached = this.genMultiSkuDetailCache[cacheKey];
            if (cached && !forceRefresh) {
                this.applyGenSkuDetail(row, cached);
                return;
            }
            if (this.genMultiSkuDetailLoading[cacheKey]) return;
            this.$set(this.genMultiSkuDetailLoading, cacheKey, true);
            this.$axios.post('/vue/msku-generation/sku-details/batch', {
                skus: [(row.sku || '').trim()], force_refresh: forceRefresh,
            }).then(res => {
                const detail = res && res.data && (res.data.results || [])[0];
                if (!detail) return;
                if (detail.local_name && !detail.error) {
                    this.$set(this.genMultiSkuDetailCache, cacheKey, detail);
                } else {
                    this.$delete(this.genMultiSkuDetailCache, cacheKey);
                }
                this.applyGenSkuDetail(row, detail);
            }).catch(() => {
                this.$set(row, 'resolveError', 'ERP SKU 信息查询失败');
            }).finally(() => {
                this.$delete(this.genMultiSkuDetailLoading, cacheKey);
            });
        },
        applyGenSkuDetail(row, detail) {
            this.$set(row, 'local_name', detail.local_name || '');
            this.$set(row, 'supplier_abbr_missing', !!detail.supplier_abbr_missing);
            // 供应商来自本地缓存或 ERP 详情中的 supplier 映射结果；手工输入优先保留。
            if (detail.supplier && !row._supplierManual) {
                this.$set(row, 'supplier', detail.supplier);
                this.refreshGenMultiLengthBudgets();
            }
            // 供应商缩写缺失（ERP 有名字但字典没映射）必须拦住；手填供应商后不再阻塞。
            const settled = !!detail.supplier_abbr_missing
                && !!row._supplierManual && !!String(row.supplier || '').trim();
            const detailError = (detail.error && !settled) ? detail.error : '';
            if (detailError) {
                this.$set(row, 'resolveError', detailError);
            } else if (row.resolveError
                && (this.isGenSkuDetailError(row.resolveError) || detail.supplier_abbr_missing)) {
                this.$set(row, 'resolveError', '');
            }
        },
        loadGenMultiSkuDetails(forceRefresh = false) {
            const skusToLoad = [];
            this.genMultiRows.forEach(row => {
                const selectedSku = String(row.sku || '').trim();
                const cachedDetail = selectedSku
                    ? this.genMultiSkuDetailCache[this.genSkuDetailCacheKey(selectedSku)]
                    : null;
                if (cachedDetail) this.applyGenSkuDetail(row, cachedDetail);
                const skus = (row.skus && row.skus.length ? row.skus : [row.sku])
                    .map(sku => String(sku || '').trim()).filter(Boolean);
                skus.forEach(sku => {
                    const cacheKey = this.genSkuDetailCacheKey(sku);
                    if ((forceRefresh || !this.genMultiSkuDetailCache[cacheKey])
                        && !this.genMultiSkuDetailLoading[cacheKey]
                        && !skusToLoad.includes(sku)) {
                        skusToLoad.push(sku);
                    }
                });
            });
            for (let i = 0; i < skusToLoad.length; i += 100) {
                const currentBatch = skusToLoad.slice(i, i + 100);
                currentBatch.forEach(sku => {
                    this.$set(this.genMultiSkuDetailLoading, this.genSkuDetailCacheKey(sku), true);
                });
                this.$axios.post('/vue/msku-generation/sku-details/batch', {
                    skus: currentBatch, force_refresh: forceRefresh,
                }).then(res => {
                    const bySku = {};
                    ((res && res.data && res.data.results) || []).forEach(item => {
                        bySku[item.sku] = item;
                        const cacheKey = this.genSkuDetailCacheKey(item.sku);
                        if (item.local_name && !item.error) {
                            this.$set(this.genMultiSkuDetailCache, cacheKey, item);
                        } else {
                            this.$delete(this.genMultiSkuDetailCache, cacheKey);
                        }
                    });
                    this.genMultiRows.forEach(row => {
                        const detail = bySku[row.sku]
                            || this.genMultiSkuDetailCache[this.genSkuDetailCacheKey(row.sku)];
                        if (!detail) return;
                        this.applyGenSkuDetail(row, detail);
                    });
                }).catch(() => {
                    this.genMultiRows.forEach(row => {
                        if (row.sku && currentBatch.includes(String(row.sku).trim())) {
                            this.$set(row, 'resolveError', 'ERP SKU 信息查询失败');
                        }
                    });
                }).finally(() => {
                    currentBatch.forEach(sku => {
                        this.$delete(this.genMultiSkuDetailLoading, this.genSkuDetailCacheKey(sku));
                    });
                });
            }
        },
        onGenRowModeChange(row) {
            if (!row) return;
            this.$set(row, '_modeManual', true);
            // 切换模式后重新使用该模式的配置基础名，避免沿用上一模式的手工覆盖值。
            this.$set(row, 'base_override', '');
            this.$set(row, '_baseEditing', false);
        },
        onGenRowSupplierChange(row) {
            if (row) {
                this.$set(row, '_supplierManual', true);
                // 手填供应商即接管该行的供应商：缩写缺映射的阻断随之解除
                if (row.supplier_abbr_missing && row.resolveError) {
                    this.$set(row, 'resolveError', '');
                }
                this.refreshGenMultiLengthBudgets();
            }
        },
        onGenRowCountryChange(row) {
            if (!row) return;
            this.$set(row, 'country', String(row.country || '').toUpperCase());
            this.$set(row, '_countryManual', true);
            // A hand edit is a one-off generation override unless the user also
            // chooses to sync uploaded fields; it must not mutate parent state.
            this.refreshGenMultiLengthBudgets();
        },
        // 行级基础名预览（按当前行模式）
        genRowConfiguredBase(r) {
            return r.mode === 'template' ? (r.msku_template || '')
                : this.genRowAttributeBase(r);
        },
        genRowBase(r) {
            return (r.base_override || '').trim() || this.genRowConfiguredBase(r);
        },
        startGenRowEdit(r) {
            // 编辑框使用草稿；仅点开“编辑”不应把自动预览锁定为手工值。
            this.$set(r, '_baseDraft', this.genRowBase(r));
            this.$set(r, '_baseEditing', true);
        },
        saveGenRowBaseEdit(row) {
            const draft = String(row._baseDraft || '').trim();
            this.$set(row, 'base_override', draft === this.genRowConfiguredBase(row) ? '' : draft);
            this.$set(row, '_baseEditing', false);
        },
        resetGenRowBase(row) {
            this.$set(row, 'base_override', '');
            this.$set(row, '_baseDraft', '');
            this.$set(row, '_baseEditing', false);
        },
        // 行级超长预判
        genRowOverlong(r) {
            const base = this.genRowBase(r);
            return !!base && !!r.max_base_len && base.length > r.max_base_len;
        },
        resetGenMulti() {
            this.genMultiView = 'config';
            this.genMultiMode = 'msku';
            this.genMultiPreviewSignature = '';
            this.genMultiExpandedResults = [];
            this.genMultiRows = [];
            this.genMultiResults = [];
            this.genMultiBatchId = '';
            this.genMultiRequestId = '';
            this.genMultiSavedCount = 0;
            this.genMultiLoading = false;
            this.genMultiExporting = false;
            this.genMultiSuffix = 'T';
            this.genMultiCustomSuffix = '';
            this.genMultiImportShop = '';
            this.genMultiImportParent = '';
            this.genMultiConfigSyncing = false;
            this.genMultiConfigSaved = false;
            this.batchGenMode = '';
            this.resetBatchAsin();
        },
        onGenMultiSuffixChange() {
            // 全局后缀只是行级空值的默认值；手工修改过的行保留自己的后缀。
            const defaultSuffix = this.effectiveGenMultiSuffix() || 'T';
            this.genMultiRows.forEach(row => {
                if (!row._suffixManual) this.$set(row, 'suffix', defaultSuffix);
                if (!row._pairManual) this.$set(row, 'should_pair', defaultSuffix === 'T');
                const rowSuffix = String(row.suffix || '').trim();
                this.$set(row, 'suffixError', /^[A-Za-z0-9]{1,6}$/.test(rowSuffix)
                    ? '' : '后缀需为1-6位字母或数字');
                this.$set(row, '_skuError', false);
            });
            // Element UI 在 v-if 动态增减列后不会自动刷新 fixed="right" 的宽度，
            // 需要在 DOM 更新后重新计算，否则数量/操作列会与主表行错位。
            this.refreshGenMultiTableLayout();
            this.loadGenMultiConfigs();
        },
        onGenMultiCustomSuffixInput() {
            this.onGenMultiSuffixChange(this.genMultiSuffix);
        },
        refreshGenMultiTableLayout() {
            this.$nextTick(() => {
                const table = this.$refs.genMultiTable;
                if (table && typeof table.doLayout === 'function') table.doLayout();
            });
        },
        genRowShouldPair(row) {
            return !!(row && row.should_pair);
        },
        genMultiRowPayload(row) {
            const shouldPair = this.genRowShouldPair(row);
            const rowSuffix = String(row.suffix || this.effectiveGenMultiSuffix() || 'T').trim().toUpperCase();
            const uploadOverrides = Object.assign({}, row.upload_overrides || {});
            // 模板配置保存必须与当前生成颜色一致，不能继续携带上传时的旧颜色。
            if (this.genRowHasUploadSource(row)
                && (row._colorManual || Object.prototype.hasOwnProperty.call(uploadOverrides, 'msku_color'))) {
                uploadOverrides.msku_color = String(row.msku_color || '').trim();
            }
            const configOverrides = Object.assign({}, uploadOverrides);
            if (row._countryManual) configOverrides.country = (row.country || '').trim();
            return {
                parent: row.parent, pm: row.pm, shop: row.shop,
                suffix: rowSuffix,
                sku: shouldPair ? (row.sku || '').trim() : '',
                should_pair: shouldPair,
                mode: row.mode || 'template',
                msku_color: (row.msku_color || '').trim(),
                supplier: (row.supplier || '').trim(),
                config_overrides: configOverrides,
                upload_overrides: uploadOverrides,
                local_name: row.local_name || '',
                base_override: (row.base_override || '').trim(),
                num: row.num,
            };
        },
        onGenRowPairChange(row) {
            if (!row) return;
            this.$set(row, '_pairManual', true);
            const enabled = this.genRowShouldPair(row);
            this.$set(row, '_skuError', enabled && !(row.sku || '').trim());
            if (!enabled) this.$set(row, '_skuError', false);
        },
        effectiveGenMultiSuffix() {
            return this.genMultiSuffix === 'CUSTOM' ? (this.genMultiCustomSuffix || '').trim() : this.genMultiSuffix;
        },
        onGenRowSuffixChange(row) {
            if (!row) return;
            const raw = String(row.suffix || '').trim().toUpperCase();
            if (!raw) {
                this.$set(row, 'suffix', this.effectiveGenMultiSuffix() || 'T');
                this.$set(row, '_suffixManual', false);
            } else {
                this.$set(row, 'suffix', raw);
                this.$set(row, '_suffixManual', true);
            }
            const valid = /^[A-Za-z0-9]{1,6}$/.test(String(row.suffix || ''));
            this.$set(row, 'suffixError', valid ? '' : '后缀需为1-6位字母或数字');
            if (!row._pairManual) this.$set(row, 'should_pair', row.suffix === 'T');
            this.refreshGenMultiLengthBudgets();
            this.loadGenMultiConfigs();
        },
        async executeGenMulti() {
            if (this.genMultiBusy) return;
            if (!this.genMultiCanGenerate) return;
            if (this.genMultiMissingShopList.length) {
                return this.$message.warning('请先在批量操作栏选择本批次唯一目标店铺');
            }
            const invalidSuffixRows = [];
            this.genMultiRows.forEach((row, index) => {
                const rowSuffix = String(row.suffix || this.effectiveGenMultiSuffix() || 'T').trim().toUpperCase();
                this.$set(row, 'suffix', rowSuffix);
                const invalid = !/^[A-Za-z0-9]{1,6}$/.test(rowSuffix);
                this.$set(row, 'suffixError', invalid ? '后缀需为1-6位字母或数字' : '');
                if (invalid) invalidSuffixRows.push(index + 1);
            });
            if (invalidSuffixRows.length) {
                this.$message.warning('请修正第 ' + invalidSuffixRows.join('、') + ' 行的 MSKU 后缀');
                return;
            }
            const missingSku = [];
            this.genMultiRows.forEach((row, index) => {
                const missing = this.genRowShouldPair(row) && !(row.sku || '').trim();
                this.$set(row, '_skuError', missing);
                if (missing) missingSku.push(index + 1);
            });
            if (missingSku.length) {
                this.$message.warning('请填写第 ' + missingSku.join('、') + ' 行的关联 SKU');
                return;
            }
            // 每次重新生成都是一份新的确认载荷；确认保存的 request_id 不能
            // 复用上一次已经成功入库的预览，否则服务端会按幂等语义返回旧批次。
            this.genMultiRequestId = this.newClientRequestId('manual_msku');
            this.genMultiLoading = true;
            try {
                if (!await confirmMskuRules(this, this.genMultiRows.map(row => this.genMultiRowPayload(row)))) {
                    this.genMultiLoading = false;
                    return;
                }
            } catch (error) {
                this.genMultiLoading = false;
                this.$message.error(error.response && error.response.data && error.response.data.message || error.message || 'MSKU规则处理失败');
                return;
            }
            const previewSignature = this.genMultiSignature;
            this.$axios.post('/vue/msku-generation/generate/multi', {
                // 每行携带自己的后缀与配对开关；空后缀已在行级回退到弹窗默认值。
                items: this.genMultiRows.map(r => this.genMultiRowPayload(r)),
                mode: 'auto',   // 未指定行模式时按品名自身配置生成
                suffix: this.effectiveGenMultiSuffix(),
            }).then(res => {
                this.genMultiLoading = false;
                if (res.code === 200 && res.data) {
                    this.clearTableSelection();
                    this.genMultiResults = res.data.results || [];
                    this.genMultiPreviewSignature = previewSignature;
                    this.genMultiExpandedResults = [];
                    this.genMultiView = this.genMultiResults.length ? 'results' : 'config';
                    this.genMultiBatchId = '';
                    this.genMultiSavedCount = 0;
                    this.genMultiConfigSaved = false;
                    const truncated = this.genMultiResults.filter(r => r.truncated).length;
                    const failed = this.genMultiResults.filter(r => !r.success).length;
                    if (truncated) {
                        this.$message.warning(truncated + ' 个品名的配置基础名超长，MSKU 已被截断，请注意查看');
                    }
                    if (failed) {
                        this.$message.error(failed + ' 个品名生成失败，请查看原因');
                    } else {
                        this.$message.success('MSKU 已生成预览；确认后点击“下载 Excel 并保存任务”入库');
                    }
                    this.refreshGenMultiTableLayout();
                } else {
                    this.$message.error((res && res.message) || '生成失败');
                }
            }).catch(() => {
                this.genMultiLoading = false;
                this.$message.error('生成请求失败');
            });
        },
        genMultiPersistRows() {
            return this.genMultiResults.map(r => ({
                parent: r.parent, pm: r.pm, shop: r.shop, sku: r.sku || '',
                local_name: r.local_name || '', supplier: r.supplier || '',
                msku_color: r.msku_color || '', base: r.base, mskus: r.mskus || [],
                mode: r.mode || '', suffix: r.suffix || this.effectiveGenMultiSuffix(),
                upload_overrides: r.upload_overrides || {},
                should_pair: !!r.should_pair,
                success: r.success, truncated: r.truncated, base_len: r.base_len,
                max_base_len: r.max_base_len, error: r.error || '', color: r.color || {},
            }));
        },
        saveGenMultiUploadConfig() {
            if (this.genMultiBusy || !this.genMultiSaveReady || !this.genMultiHasUploadRows || this.genMultiConfigSaved) return;
            this.genMultiConfigSyncing = true;
            this.$axios.post('/vue/msku-generation/generate/multi/sync-upload-config', {
                rows: this.genMultiPersistRows(),
            }).then(res => {
                if (res.code !== 200 || !res.data) {
                    return this.$message.error((res && res.message) || '模板配置保存失败');
                }
                this.genMultiConfigSaved = true;
                this.$message.success('已保存 ' + Number(res.data.synced_count || 0) + ' 个品名的模板配置');
            }).catch(error => {
                const message = error && error.response && error.response.data && error.response.data.message;
                this.$message.error(message || '模板配置保存失败');
            }).finally(() => { this.genMultiConfigSyncing = false; });
        },
        exportGenMulti() {
            if (this.genMultiBusy || !this.genMultiSaveReady) return;
            this.genMultiExporting = true;
            const rows = this.genMultiPersistRows();
            const download = () => this.$axios.post('/vue/msku-generation/generate/multi/download', {
                rows: this.genMultiResults.map(r => ({
                    parent: r.parent, pm: r.pm, shop: r.shop,
                    sku: r.sku || '',
                    local_name: r.local_name || '', supplier: r.supplier || '',
                    suffix: r.suffix || this.effectiveGenMultiSuffix(),
                    should_pair: !!r.should_pair,
                    base: r.base, mskus: r.mskus || [],
                    truncated: r.truncated, base_len: r.base_len,
                    max_base_len: r.max_base_len, error: r.error || '',
                    color: r.color || {},
                })),
            }, { responseType: 'blob' });
            const request = this.genMultiSavedCount
                ? Promise.resolve()
                : this.$axios.post('/vue/msku-generation/generate/multi/confirm', {
                    rows, suffix: this.effectiveGenMultiSuffix(), request_id: this.genMultiRequestId,
                }).then(res => {
                    if (res.code !== 200 || !res.data || !res.data.persisted) {
                        return Promise.reject(new Error((res && res.message) || '保存失败'));
                    }
                    this.genMultiBatchId = res.data.batch_id || '';
                    this.genMultiSavedCount = Number(res.data.persisted_count || 0);
                });
            request.then(download).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = '批量生成MSKU.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.genMultiExporting = false;
                }, 300);
            }).catch(error => {
                this.genMultiExporting = false;
                this.$message.error(error.message || '导出失败');
            });
        },
        resetMskuBatch() {
            this.mskuBatchStep = 'form';
            this.mskuBatchRows = [];
            this.mskuBatchErrors = [];
            this.mskuBatchResults = [];
            this.mskuBatchSummary = null;
            this.mskuBatchUploading = false;
        },
        downloadMskuTemplate() {
            if (!this.tableSelection.length) return this.$message.warning('请先在品名列表勾选要更新的品名');
            this.mskuBatchDownloading = true;
            this.$axios.post('/vue/auto_update/msku_template', {
                items: this.tableSelection.map(row => ({
                    parent: row['本地父体'] || '',
                    pm: row['本地品名'] || '',
                    shop: row['店铺'] || '',
                    msku: row.msku || '',
                })),
            }, {
                responseType: 'blob',
            }).then(res => {
                const blob = new Blob([res], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = '自动更新MSKU模板.xlsx';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    this.mskuBatchDownloading = false;
                }, 300);
            }).catch(error => {
                this.mskuBatchDownloading = false;
                this.$message.error(error.message || '模板下载失败');
            });
        },
        mskuUploadRequest(option) {
            this.mskuBatchUploading = true;
            const fd = new FormData();
            fd.append('file', option.file);
            this.$axios.post('/vue/auto_update/msku_parse', fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }).then(res => {
                this.mskuBatchUploading = false;
                if (res.msg === 'success' && res.data) {
                    this.mskuBatchRows = res.data.rows || [];
                    this.mskuBatchErrors = res.data.errors || [];
                    this.mskuBatchStep = 'preview';
                    if (this.mskuBatchRows.length) this.$message.success(`解析到 ${this.mskuBatchRows.length} 行`);
                } else {
                    this.$message.error((res && res.message) || '解析失败');
                }
            }).catch(e => this.$message.error('解析失败: ' + (e && e.message ? e.message : e)));
        },
        executeMskuBatch() {
            this.mskuBatchExecuting = true;
            this.$axios.post('/vue/auto_update/msku_apply', {
                rows: this.mskuBatchRows,
            }).then(res => {
                this.mskuBatchExecuting = false;
                if (res.msg === 'success' && res.data) {
                    this.mskuBatchResults = res.data.results || [];
                    this.mskuBatchSummary = res.data.summary || null;
                    this.mskuBatchStep = 'result';
                    this.refreshTableData();
                } else {
                    this.$message.error((res && res.message) || '执行失败');
                }
            }).catch(e => {
                this.mskuBatchExecuting = false;
                this.$message.error('执行失败: ' + e);
            });
        },
        // === 颜色配置（弹窗展示）===
        openColorConfig() {
            this.colorConfigVisible = true;
        },
        // === 创建并配对（选 SKU → 后端复合方法：刊登→FNSKU同步→配对，无采购）===
        openCreatePair(row) {
            if (row.msku) {
                this.$message.warning('该品名已有售卖MSKU（' + row.msku + '），不能重复创建配对');
                return;
            }
            this.createPairRow = { ...row };
            this.createPairSku = '';
            this.createPairSkus = [];
            this.createPairError = '';
            this.createPairVisible = true;
            this.fetchPairSkus(row['本地品名'], row['本地父体']);
        },
        fetchPairSkus(pm, parent) {
            this.$axios.get('/vue/vue_product/pm-skus', {
                // SKU 必须同时属于当前父体和品名，避免同名品名跨父体串用。
                params: { pm, parent }
            }).then(res => {
                if (res.code === 200 && res.data) {
                    this.createPairSkus = res.data || [];
                    if (!this.createPairSkus.length) {
                        this.createPairError = '该品名暂无关联 SKU，请先在备货管理中绑定';
                    }
                }
            }).catch(() => { });
        },
        submitCreatePair() {
            if (!this.createPairSku) return this.$message.warning('请选择要关联的 SKU');
            this.createPairLoading = true;
            this.$axios.post('/vue/vue_product/create-pair', {
                parent: this.createPairRow['本地父体'],
                pm: this.createPairRow['本地品名'],
                shop: this.createPairRow['店铺'],
                sku: this.createPairSku,
                suffix: 'T',
            }).then(res => {
                this.createPairLoading = false;
                if (res.code === 200 && res.data && res.data.success) {
                    this.$notify({ title: '创建并配对已提交', message: '新MSKU: ' + (res.data.new_msku || ''), duration: 5000 });
                    this.createPairVisible = false;
                    this.refreshTableData();
                } else {
                    this.createPairError = (res.data && res.data.error) || res.message || '创建配对失败';
                }
            }).catch(e => {
                this.createPairLoading = false;
                const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || e;
                this.createPairError = '创建配对失败: ' + msg;
            });
        },
    }
}
</script>

<style scoped>
.msku-manage-page {
    background: #f8f9fa;
    min-height: 100vh;
    padding: 20px;
}

.msku-cell {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
}

.msku-cell:hover .msku-edit-icon {
    color: #409EFF;
}

.msku-edit-icon {
    color: #C0C4CC;
    font-size: 12px;
}

.batch-asin-product-type-missing {
    color: #f56c6c;
    font-size: 12px;
}

.batch-asin-product-type-hint {
    color: #909399;
    font-size: 11px;
    line-height: 1.35;
}

.batch-asin-msku-source-hint {
    display: block;
    margin: 6px 10px 0;
    color: #909399;
    font-size: 11px;
    line-height: 1.5;
}

.batch-asin-task-muted {
    color: #c0c4cc;
}

.batch-asin-config-preview {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 6px 14px;
    max-height: 220px;
    overflow-y: auto;
    margin-top: 8px;
    padding: 8px 10px;
    background: #f7f9fc;
    border: 1px solid #e8edf5;
    border-radius: 4px;
}

.batch-asin-config-item {
    display: flex;
    align-items: flex-start;
    min-width: 0;
    gap: 6px;
    color: #606266;
    font-size: 12px;
    line-height: 1.5;
}

.batch-asin-config-label {
    flex: 0 0 118px;
    color: #909399;
    text-align: right;
    overflow-wrap: anywhere;
}

.batch-asin-config-value {
    min-width: 0;
    color: #303133;
    word-break: break-all;
}

.batch-asin-config-value.is-empty {
    color: #f56c6c;
}

.batch-asin-config-empty {
    margin-top: 8px;
    padding: 8px 10px;
    color: #e6a23c;
    font-size: 12px;
    background: #fdf6ec;
    border: 1px solid #f5dab1;
    border-radius: 4px;
}

/* 「品名原有配置」后的配置更新结果标记 */
.batch-asin-config-update {
    margin-left: 8px;
    font-size: 12px;
    white-space: nowrap;
}

.batch-asin-config-update .is-updated {
    color: #67c23a;
}

.batch-asin-config-update .is-failed {
    color: #f56c6c;
}

.batch-asin-generated-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: 4px 0 8px;
    color: #606266;
    font-size: 12px;
}

.batch-asin-generated-toolbar-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
}

.batch-asin-msku-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #ebeef5;
    color: #606266;
    font-size: 12px;
}

.batch-asin-item-mskus {
    margin-top: 6px;
}

.batch-asin-msku-field-hint {
    margin-left: 8px;
    color: #909399;
    font-style: normal;
    font-size: 11px;
}

/* 提交前校验失败明细：按品名分组 + 字段标签，默认折叠 */
.batch-asin-missing-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.batch-asin-missing-summary .el-button {
    color: inherit;
}

.batch-asin-missing-group {
    margin-top: 6px;
}

.batch-asin-missing-group-head {
    font-weight: 600;
    line-height: 1.8;
}

.batch-asin-missing-count {
    margin-left: 6px;
    font-weight: 400;
    font-size: 12px;
    opacity: 0.75;
}

.batch-asin-missing-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 6px;
    border-radius: 4px;
    cursor: pointer;
}

.batch-asin-missing-row:hover {
    background: rgba(0, 0, 0, 0.05);
}

.batch-asin-missing-msku {
    flex: 0 0 auto;
    max-width: 320px;
    overflow: hidden;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.batch-asin-missing-fields {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.batch-asin-missing-more {
    margin-top: 4px;
    font-size: 12px;
    opacity: 0.75;
}

.operation-bar {
    background: #fff;
    padding: 12px 20px;
    border-radius: 4px;
    margin-bottom: 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    overflow-x: auto;
}

.search-form {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: nowrap;
    width: max-content;
    min-width: 100%;
}

.search-form > * {
    flex-shrink: 0;
}

.search-form > .el-button {
    margin-left: 0;
    padding-left: 12px;
    padding-right: 12px;
}

.search-form .form-label {
    font-size: 13px;
    color: #606266;
    white-space: nowrap;
}

.search-form .el-badge {
    line-height: 1;
}

.table-area {
    margin-top: 10px;
    background: #fff;
    padding: 20px;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.msku-hint {
    font-size: 12px;
    color: #909399;
    line-height: 1.6;
}

/* 移出预览：受影响 MSKU 列表过长时内部滚动，不撑开弹窗 */
.move-out-mskus {
    max-height: 160px;
    overflow-y: auto;
    padding: 6px 8px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
}

.msku-batch-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 16px;
}

.msku-color-ready {
    color: #409eff;
    font-weight: 600;
}

.msku-color-empty {
    color: #909399;
}

/* 批量导入配置：模板下载 / 导入配置 两个板块 */
.batch-section {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 12px 16px 4px;
    margin-bottom: 16px;
}

.batch-section-title {
    font-size: 14px;
    font-weight: bold;
    color: #303133;
    margin-bottom: 12px;
}

/* 父体待填模板弹窗：父体多选表格 */
.fill-parent-table {
    border: 1px solid #ebeef5;
    border-radius: 4px;
    padding: 8px;
}

.gen-multi-title {
    display: flex;
    align-items: center;
    gap: 22px;
    padding-right: 30px;
    color: #303133;
    font-size: 18px;
    line-height: 28px;
}

.gen-multi-guide {
    margin-bottom: 12px;
}

.gen-multi-toolbar {
    margin-bottom: 12px;
}

.gen-multi-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 10px;
    background: #f7f9fc;
    border: 1px solid #e8edf5;
    border-radius: 4px;
}

.gen-multi-actions > * {
    flex-shrink: 0;
}

.gen-multi-status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding-right: 10px;
    border-right: 1px solid #e4e7ed;
    color: #909399;
    font-size: 12px;
    white-space: nowrap;
}

.gen-multi-field {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 32px;
}

.gen-multi-field-label {
    color: #606266;
    font-size: 12px;
    white-space: nowrap;
}

.gen-multi-result-summary {
    margin-bottom: 12px;
}

.gen-multi-result-card {
    padding: 12px;
    margin-bottom: 10px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    overflow-wrap: anywhere;
}

.gen-result-mskus {
    max-height: 220px;
    margin-top: 6px;
    overflow-y: auto;
}

.gen-multi-product-name {
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.gen-multi-pair-control {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #909399;
    white-space: nowrap;
}

.gen-multi-pair-control .is-enabled {
    color: #409eff;
}

.gen-multi-row-action {
    padding: 7px 10px;
}

.gen-multi-footer,
.gen-multi-footer-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
}

.gen-multi-footer {
    text-align: left;
}

.gen-multi-footer-status {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    color: #909399;
    font-size: 12px;
}

.gen-multi-footer-actions {
    margin-left: auto;
}

.gen-multi-footer-actions .el-button,
.gen-multi-actions .el-button {
    margin-left: 0;
}

.gen-config-missing-row {
    margin-top: 4px;
    font-size: 12px;
    font-weight: normal;
}

.gen-sku-import-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 6px;
}

/* 批量生成MSKU：行级基础名预览 */
.gen-base-ok {
    color: #409eff;
}

.gen-base-warn {
    color: #CC0000;
    background: #fef0f0;
    border-radius: 3px;
    padding: 1px 4px;
}

.gen-sku-error ::v-deep .el-input__inner {
    border-color: #F56C6C;
}

.gen-sku-select-wrap {
    position: relative;
    display: inline-flex;
    width: 100%;
}

.gen-sku-loading-mask {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: #409EFF;
    font-size: 11px;
    background: rgba(255, 255, 255, 0.78);
    border-radius: 4px;
    cursor: wait;
}

.gen-sku-loading-mask .el-icon-loading {
    font-size: 14px;
}

.gen-sku-error-message {
    color: #F56C6C;
    font-size: 11px;
    line-height: 16px;
    word-break: break-all;
}

.config-layout {
    display: flex;
    gap: 20px;
    align-items: flex-start;
}

.config-left {
    width: 340px;
    flex-shrink: 0;
    border-right: 1px solid #ebeef5;
    padding-right: 20px;
}

.config-right {
    flex: 1;
    min-width: 0;
    max-height: 560px;
    overflow-y: auto;
    padding-left: 6px;
}

.config-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 28px;
    font-size: 13px;
    color: #606266;
    background: #f5f7fa;
    border-radius: 4px;
    padding: 10px 14px;
    margin-bottom: 12px;
}

.meta-item {
    display: inline-flex;
    align-items: center;
}

.meta-label {
    color: #909399;
    margin-right: 6px;
}

.config-empty {
    color: #909399;
    font-size: 13px;
    padding: 12px 0;
}

.dims-row {
    display: flex;
    align-items: center;
    gap: 6px;
}

.dims-x {
    color: #909399;
}

.dims-unit {
    margin-left: 2px;
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
}

::v-deep .warn-row {
    background-color: #fef0f0 !important;
}
</style>

<!-- 全局样式：弹窗 append-to-body 渲染在 body 下，scoped 深度选择器无法命中，
     且 Element 用行内 margin-top（top 属性默认 15vh），必须 !important 覆盖。
     调整下方数值即可整体上移/下移弹窗。 -->
<style>
.au-dialog {
    margin-top: max(0px, calc(15vh - 150px)) !important;
}

/* 主流程四个弹窗在原位置基础上下移 50px，其它旧弹窗保持原位置。 */
.main-feature-dialog {
    margin-top: max(0px, calc(15vh - 100px)) !important;
    width: min(1500px, calc(100vw - 32px)) !important;
    max-width: calc(100vw - 32px);
    overflow-x: hidden;
}

/* 固定弹窗高度：内容超出时在 body 区内部滚动，避免把弹窗撑高/溢出视口 */
.au-dialog {
    height: min(680px, 85vh) !important;
    display: flex;
    flex-direction: column;
}

.au-dialog .el-dialog__body {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
}

.gen-multi-dialog {
    margin-top: 3vh !important;
    margin-bottom: 3vh;
    height: 94vh !important;
    border-radius: 4px;
}

.gen-multi-dialog .el-dialog__header {
    padding: 18px 22px 12px;
}

.gen-multi-dialog .el-dialog__body {
    padding: 14px 22px;
}

.gen-multi-dialog .el-dialog__footer {
    padding: 12px 22px 16px;
}

.gen-multi-dialog .gen-multi-guide .el-alert__title {
    font-size: 12px;
    line-height: 1.7;
}

.gen-multi-dialog .gen-multi-actions .el-radio-button__inner {
    padding-right: 12px;
    padding-left: 12px;
}

.gen-multi-dialog .el-table th {
    background: #f5f7fa;
    color: #606266;
}

.gen-multi-dialog .el-table .gen-multi-issue-row > td {
    background: #fffbf3;
}

.gen-multi-row-menu .gen-multi-remove-action {
    color: #f56c6c;
}

.gen-multi-dialog .el-tag {
    max-width: 100%;
    height: auto;
    white-space: normal;
    overflow-wrap: anywhere;
}

@media (max-width: 900px) {
    .gen-multi-dialog .el-dialog__body,
    .gen-multi-dialog .el-dialog__footer {
        padding: 12px 16px;
    }
}
</style>
